// Deal detail — live. Three columns:
//  Left:   Properties (from live engagement + company data)
//  Center: Notes (single composer — notes, call/email/meeting logs, tasks) + stage history
//  Right:  Agent workspaces (live /api/agent-workspaces filtered to this deal)

const STAGE_DOT_HUE = {
  prospect: 'slate', qualified: 'slate', interested: 'teal',
  ds_call: 'teal', hot_lead: 'teal', blast: 'violet', bs_call: 'violet',
  data_gathering: 'amber', valuation_presented: 'amber',
  loi: 'blue', won: 'green',
  // Formal sell-side process (082) — hues mirror the equivalent buyside step.
  ss_prospect: 'slate', ss_qualified: 'slate', ss_interested: 'teal',
  ss_valuation: 'amber', ss_pitch: 'teal', ss_cim_prep: 'amber',
  ss_buyer_outreach: 'violet', ss_ioi: 'blue', ss_loi: 'blue',
  ss_mgmt_presentation: 'blue', ss_won: 'green',
};

// Which stage family an engagement type draws from: buyer shells get the
// buyer funnel, sell_side deals the formal sell-side process (082), and
// everything else (seller_buyside / other) the classic buyside funnel.
const stagesForDealType = (stages, engType) => {
  const all = stages || [];
  if (engType === 'buyer_buyside') return all.filter((s) => s.category === 'buyer');
  if (engType === 'sell_side') return all.filter((s) => s.category === 'sellside');
  return all.filter((s) => s.category !== 'buyer' && s.category !== 'sellside');
};

// The 3 commercial approaches (engagements.sale_approach, 082) — rendered as
// checkmarks; one at most is checked. full_sellside is the only one with a
// truly different process (the sellside stage family).
const SALE_APPROACHES = [
  { value: 'targeted',             label: 'Targeted approach',
    hint: 'Buyside — buyer pays the fee' },
  { value: 'competitive_targeted', label: 'Competitive targeted',
    hint: 'Runs like targeted; seller pays the fee, no CIM prep' },
  { value: 'full_sellside',        label: 'Full sell-side process',
    hint: 'Formal process — CIM prep, structured market approach' },
];

// Buyer/seller side of the engagement (engagement_type). Hues mirror the
// company role pills (seller=teal, buyer=violet).
const ENG_TYPE_PILL = {
  seller_buyside: { label: 'Seller',    hue: 'teal' },
  buyer_buyside:  { label: 'Buyer',     hue: 'violet' },
  sell_side:      { label: 'Sell-side', hue: 'blue' },
};

// Buyer shells carry their REAL funnel position verbatim from DealCloud
// (engagements.dc_stage, migration 050) — "06. Pending B/S Call", "11. LOI"…
// Strip the ordering prefix for display; hue by outcome so dead pursuits
// read red and won ones green at a glance.
// IndustryPro's on-hold delay categorization (engagements.on_hold_reason,
// migration 071) — what is holding the parked deal up.
const HOLD_REASON_LABEL = { owner_delay: 'Owner delay', business_delay: 'Business delay' };

const cleanDcStage = (s) => (s ? s.replace(/^\d+\.\s*/, '') : null);
const dcStageHue = (s) => {
  const n = (s || '').toLowerCase();
  if (n.includes('won')) return 'green';
  if (n.includes('lost') || n.includes('unqualified')) return 'rose';
  if (n.includes('on hold')) return 'amber';
  return 'violet';
};

// The DealCloud stages a buyer pursuit moves through, in funnel order — the
// pursuit-stage picker's options (keys = app/buyer_pursuit.PURSUIT_STAGES).
// The server turns each into the stage / hold / exit that writes back as
// that DC stage, and lines the interested-buyers roster row up with it:
// Lost = passed, anything else = not passed.
const PURSUIT_STAGE_OPTIONS = [
  { value: 'created',      dc: '01. Engagement Created' },
  { value: 'bs_call',      dc: '06. Pending B/S Call' },
  { value: 'hold_high',    dc: '07. On Hold - High Priority' },
  { value: 'hold_low',     dc: '08. On Hold - Low Priority' },
  { value: 'data_request', dc: '09. Initial Data Request' },
  { value: 'valuation',    dc: '10. Valuation Presented' },
  { value: 'loi',          dc: '11. LOI' },
  { value: 'won',          dc: '12. Won - Nurturing' },
  { value: 'lost',         dc: '14. Lost', description: 'The buyer passed — they move to Passed on the deal' },
].map((o) => ({ ...o, label: cleanDcStage(o.dc), hue: dcStageHue(o.dc) }));
// Stages outside the picker (03. Unqualified, 13. Won - Not Nurturing…) still
// show as their pill; the editor just opens with nothing selected.
const pursuitKeyFor = (dcStage) => (PURSUIT_STAGE_OPTIONS.find((o) => o.dc === dcStage) || {}).value || null;

const DealDetail = ({ dealId, stages, activeUser, back }) => {
  // ── ALL hooks run unconditionally on every render. Never early-return
  //    above this block — React's rules of hooks require a stable order.
  const [deal, setDeal]             = React.useState(null);  // adapted engagement
  const [company, setCompany]       = React.useState(null);
  // A deal guest (backend app/deal_guest.py) reads this page and nothing
  // around it: no edits, no agent/AI/pin actions, no links out to records
  // their access doesn't cover. `denied` = the server said it isn't theirs.
  const guest = !!window.AtlasIsGuest;
  const canOpenCompanies = window.AtlasCanSee ? window.AtlasCanSee('companies') : true;
  const [denied, setDenied]         = React.useState(false);
  const [creatingWorkspace, setCreatingWorkspace] = React.useState(false);
  const [exiting, setExiting] = React.useState(false);
  // "Not acquired": undoes a Company sold exit (and the acquisition behind it).
  const [reversing, setReversing] = React.useState(false);
  // A move to Won goes through the Closing drawer first (202609301300).
  const [wonStage, setWonStage] = React.useState(null);   // pipeline stage | null
  const [workspaces, setWorkspaces] = React.useState(null);
  const [tasks, setTasks]           = React.useState(null);
  const [stageHistory, setStageHistory] = React.useState(null);
  const [buyerEngs, setBuyerEngs]   = React.useState(null);
  const [ersScores, setErsScores]   = React.useState(null);
  const [highlights, setHighlights] = React.useState(null);  // Fireflies soundbites
  const [callAnalytics, setCallAnalytics] = React.useState(null);  // talk time + sentiment
  const [liveMeeting, setLiveMeeting] = React.useState(null);  // Fireflies is capturing now
  const [isPinned, setIsPinned]     = React.useState(null);  // null = unknown (loading)
  // Phone renders PhoneDealPage, which has no ERS card, highlights, call
  // analytics or workspaces — those fetches are skipped there.
  const device = window.useDevice();
  const phone = !!device.phone;
  // ?panel= / ?note= for this deal (record_panels.jsx). Any open pop-out (panel.name:
  // a workspace, or the phone's All details sheet) lifts the page-level action drawers (z70).
  const panel = window.usePanelParam({ kind: 'deal', id: dealId });
  // Full-width buyer outreach tracker (views/outreach.jsx). Lives in the
  // hash as #/deals/<id>?tab=outreach so it can be linked and survives a
  // reload; the router routes on the path and leaves the query alone.
  // Only THIS deal's hash counts: an in-app jump to another deal mounts the
  // new page (app.jsx keys it by id) before the router rewrites the hash.
  const readOutreachTab = () => {
    const m = /^#\/deals\/([^?]+)\?(.*)$/.exec(window.location.hash || '');
    if (!m || !/(^|&)tab=outreach\b/.test(m[2])) return false;
    // A ?panel= pop-out owns the query (views/record_panels.jsx).
    if (/(^|&)panel=/.test(m[2])) return false;
    if (window.recordOutreachAsPanel()) return false;   // v2 + Buyers workspace: the alias owns ?tab=outreach
    try { return decodeURIComponent(m[1]) === String(dealId); } catch (_) { return false; }
  };
  const [outreachOpen, setOutreachOpen] = React.useState(readOutreachTab);
  React.useEffect(() => {
    const onHash = () => setOutreachOpen(readOutreachTab());
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => { window.removeEventListener('hashchange', onHash); window.removeEventListener('popstate', onHash); };
  }, [dealId]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Opening pushes one history entry, marked as ours; closing steps back out
  // of it, so the browser's Back from the deal page never reopens the
  // tracker. A tracker opened from a link (Slack, a bookmark) has no entry
  // of ours behind it — closing strips the tab in place instead.
  const openOutreach = React.useCallback(() => {
    if (!readOutreachTab()) {
      try { history.pushState({ atlasOutreach: String(dealId) }, '', `#/deals/${encodeURIComponent(dealId)}?tab=outreach`); } catch (_) {}
    }
    setOutreachOpen(true);
  }, [dealId]);  // eslint-disable-line react-hooks/exhaustive-deps
  const closeOutreach = React.useCallback(() => {
    let ours = false;
    try { ours = !!(history.state && history.state.atlasOutreach === String(dealId)); } catch (_) {}
    if (ours) {
      try { history.back(); } catch (_) {}
    } else {
      try { history.replaceState(null, '', `#/deals/${encodeURIComponent(dealId)}`); } catch (_) {}
    }
    setOutreachOpen(false);
  }, [dealId]);

  // What "+ New" starts from on this page (app.jsx createPrefillFor): the
  // deal, its company, and — on a buyer's pursuit — the seller deal it
  // pursues.
  React.useEffect(() => {
    if (!deal || !window.publishPageContext) return undefined;
    window.publishPageContext({
      kind: 'deal', id: deal.id, name: deal.name, type: deal.type,
      company: deal.target ? { id: deal.target, name: deal.targetName || '' } : null,
      sellerDeal: deal.sellerEngagementId ? { id: deal.sellerEngagementId, name: deal.sellerEngagementName || '' } : null,
    });
    return () => window.publishPageContext && window.publishPageContext(null, deal.id);
  }, [deal && deal.id, deal && deal.name, deal && deal.type, deal && deal.target, deal && deal.sellerEngagementId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Initial fetch on dealId change.
  React.useEffect(() => {
    let alive = true;
    setDeal(null); setCompany(null); setDenied(false);
    (async () => {
      try {
        const raw = await window.AtlasApi.getEngagement(dealId);
        if (!alive) return;
        const adapted = window.AtlasData.__adaptEngagement(raw);
        setDeal(adapted);
        if (raw.company_id) {
          const co = await window.AtlasApi.getCompany(raw.company_id).catch(() => null);
          if (alive && co) setCompany(window.AtlasData.__adaptCompany(co));
        }
      } catch (e) {
        if (/^403\b/.test(e.message || '')) { if (alive) setDenied(true); return; }
        pushToast({ type: 'error', title: 'Could not load engagement', body: e.message });
      }
    })();
    return () => { alive = false; };
  }, [dealId]);

  // Record-open beacon (api.jsx recordView): once per deal, when its type is
  // known. Never for a deal guest.
  React.useEffect(() => {
    if (!deal || window.AtlasIsGuest || !window.AtlasApi.recordView) return;
    window.AtlasApi.recordView({
      page: 'deal', variant: deal.type === 'buyer_buyside' ? 'shell' : 'seller',
      device: device.kind, layout: window.recordLayout(), panel: 'none', action: 'open', via: 'load',
    });
  }, [deal ? deal.id : null]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Stage family follows the deal's TYPE (buyside/sellside process toggle,
  // 082), not the current stage's category — a deal freshly toggled to the
  // other process still sits on an out-of-family stage until it's moved,
  // and the picker must already offer the new family.
  const pipelineStages = React.useMemo(
    () => stagesForDealType(stages, deal?.type),
    [stages, deal?.type]
  );

  // Another view can queue the exit drawer to open on arrival — e.g. the
  // company page's "marked acquired with an active engagement" prompt.
  React.useEffect(() => {
    if (!deal) return;
    try {
      if (sessionStorage.getItem('atlas.openExitFor') === String(dealId)) {
        sessionStorage.removeItem('atlas.openExitFor');
        setExiting(true);
      }
    } catch (_) {}
  }, [deal ? deal.id : null]);

  // A stage / exit / hold save offers Undo (views/stage_moves.jsx) instead
  // of "Saved"; the Undo is this same save, so history records it too.
  const dealRef = React.useRef(null);
  dealRef.current = deal;
  const reloadStageHistory = React.useCallback(() => {
    window.AtlasApi.listStageHistory(dealId)
      .then((rows) => setStageHistory(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [dealId]);
  const saveDeal = React.useCallback(async (patch, opts) => {
    if (window.AtlasIsGuest) {
      pushToast({ type: 'info', title: 'Read-only', body: 'Your access to this deal is read-only.' });
      return null;
    }
    const L = window.StageMovesLogic;
    const stagey = Boolean(patch) && ['stage_id', 'exit_id', 'on_hold'].some((k) => k in patch);
    const before = stagey && L && dealRef.current ? L.stageSnapshot(dealRef.current._raw) : null;
    try {
      const updated = await window.AtlasApi.updateEngagement(dealId, patch);
      setDeal(window.AtlasData.__adaptEngagement(updated));
      window.dispatchEvent(new CustomEvent('deal-updated', { detail: { id: dealId, updated } }));
      // A Next action save also creates / moves / retitles / clears the deal's
      // Next Action task server-side (sync_next_action_task): re-read the tasks.
      if (patch && ('next_action_date' in patch || 'next_action_notes' in patch)) reloadTasks();
      // An owner change that reached the company (one owner per book): the
      // Target company panel shows the company owner, so re-pull it.
      if (updated && updated.owner_cascade && updated.owner_cascade.companies && updated.company_id) {
        window.AtlasApi.getCompany(updated.company_id)
          .then((co) => { if (co) setCompany(window.AtlasData.__adaptCompany(co)); })
          .catch(() => {});
      }
      if (stagey) reloadStageHistory();
      const offered = before && !(opts && opts.undoing) && window.StageUndo && window.StageUndo.offer({
        id: dealId, name: updated && updated.name, before, after: L.stageSnapshot(updated),
        undo: (back) => saveDeal(back, { undoing: true }),
      });
      if (!offered && !(opts && opts.undoing)) pushToast({ type: 'success', title: 'Saved' });
      return updated;
    } catch (e) {
      // An Undo's failure is toasted once, by the Undo itself.
      if (!(opts && opts.undoing)) pushToast({ type: 'error', title: 'Save failed', body: e.message });
      throw e;
    }
  }, [dealId, reloadStageHistory]);

  // Buyer shells: move the pursuit to one DealCloud stage (the Pursuit stage
  // row). The server picks the stage / hold / exit and lines up the roster.
  const savePursuitStage = React.useCallback(async (stage) => {
    try {
      const updated = await window.AtlasApi.setEngagementPursuitStage(dealId, stage);
      setDeal(window.AtlasData.__adaptEngagement(updated));
      window.dispatchEvent(new CustomEvent('deal-updated', { detail: { id: dealId, updated } }));
      pushToast({ type: 'success', title: 'Saved', body: `Pursuit stage: ${cleanDcStage(updated.pursuit_stage) || 'updated'}` });
      return updated;
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
      throw e;
    }
  }, [dealId]);

  // Refetch the engagement without a PATCH — used after a "Next Action"
  // note save, which updates next_action_notes server-side.
  const reloadDeal = React.useCallback(async () => {
    try {
      const raw = await window.AtlasApi.getEngagement(dealId);
      setDeal(window.AtlasData.__adaptEngagement(raw));
    } catch { /* panel keeps the stale value until next navigation */ }
  }, [dealId]);

  const reloadWorkspaces = React.useCallback(async () => {
    if (window.AtlasIsGuest) { setWorkspaces([]); return; }   // agents aren't theirs
    try {
      const ws = await window.AtlasApi.agentWorkspaces({ engagement_id: dealId });
      setWorkspaces((ws || []).filter((w) => String(w.engagement_id) === String(dealId)));
    } catch { setWorkspaces([]); }
  }, [dealId]);
  React.useEffect(() => { if (!phone) reloadWorkspaces(); }, [reloadWorkspaces, phone]);

  const reloadTasks = React.useCallback(async () => {
    try {
      const res = await window.AtlasApi.listTasks({ engagement_id: dealId, limit: 50 });
      setTasks((res && res.items) || []);
    } catch { setTasks([]); }
  }, [dealId]);
  React.useEffect(() => { reloadTasks(); }, [reloadTasks]);

  React.useEffect(() => {
    setStageHistory(null);
    window.AtlasApi.listStageHistory(dealId)
      .then((rows) => setStageHistory(Array.isArray(rows) ? rows : []))
      .catch(() => setStageHistory([]));
  }, [dealId]);

  React.useEffect(() => {
    if (phone) return;
    window.AtlasApi.engagementHighlights(dealId)
      .then((rows) => setHighlights(Array.isArray(rows) ? rows : []))
      .catch(() => setHighlights([]));
  }, [dealId, phone]);

  React.useEffect(() => {
    if (phone) return;
    window.AtlasApi.engagementCallAnalytics(dealId)
      .then((rows) => setCallAnalytics(Array.isArray(rows) ? rows : []))
      .catch(() => setCallAnalytics([]));
  }, [dealId, phone]);

  // Live-meeting presence — poll once a minute while the page is open. The
  // backend caches the Fireflies listing process-wide, so this stays cheap
  // however many people have deal pages open.
  //
  // No backoff ladder here: this isn't a job aging toward a finish line, it's
  // a presence check that's only ever worth a minute of staleness. What the
  // hook buys us is the visibility gate — a deal page left open in a
  // background tab stops polling, and refetches the moment it's looked at
  // again. It never gives up while the page is open.
  window.usePolling(
    React.useCallback(() => (
      window.AtlasApi.engagementLiveMeeting(dealId)
        .then((res) => { setLiveMeeting(res && res.live ? res.meeting : null); })
        .catch(() => { setLiveMeeting(null); })
    ), [dealId]),
    { key: dealId, first: 60000, immediate: true, giveUpAfter: 0 },
  );

  // Paged notes feed (defined in company_detail.jsx, shared across record pages).
  const { notes, total: notesTotal, loadingMore: notesLoadingMore,
          loadMore: loadMoreNotes, reload: reloadNotes,
          search: notesSearch, setSearch: setNotesSearch, q: notesQ } =
    window.usePagedNotes(dealId ? { engagement_id: dealId, include_linked: true } : null);
  // Log ▾ dot: a composer draft is kept for this deal (views/record_notes.jsx).
  const logDraftDot = window.useRecordDraftFlag('deal', dealId);

  // Pin state — membership in the user's pins list (small; ≤ a dozen rows).
  React.useEffect(() => {
    if (window.AtlasIsGuest) { setIsPinned(false); return; }   // no sidebar, no pins
    window.AtlasApi.listPinnedDeals()
      .then((res) => {
        const items = Array.isArray(res) ? res : ((res && res.items) || []);
        setIsPinned(items.some((p) => String(p.id) === String(dealId)));
      })
      .catch(() => setIsPinned(false));
  }, [dealId]);

  const togglePin = React.useCallback(async () => {
    const next = !isPinned;
    setIsPinned(next);  // optimistic — the call below invalidates the cache
    try {
      await (next ? window.AtlasApi.pinDeal(dealId) : window.AtlasApi.unpinDeal(dealId));
    } catch {
      setIsPinned(!next);
      return;
    }
    window.dispatchEvent(new CustomEvent('pins-changed'));
  }, [dealId, isPinned]);

  // Read at render. v2 and the phone page read the Buyers card off the summary (the
  // workspace loads the board); only the classic page still loads it here.
  const boardViaCard = window.recordCardsOn('DealBuyersCard') || phone;
  React.useEffect(() => {
    if (boardViaCard) return;   // v2: the card reads the summary; the workspace loads the board
    window.AtlasApi.buyersForEngagement(dealId)
      // already filtered to this engagement, server-side, with buyer names
      // (inline edits below merge the PATCH response back into this list)
      .then((be) => setBuyerEngs((be && be.items) || []))
      .catch(() => setBuyerEngs([]));
  }, [dealId, boardViaCard]);

  // Exit readiness scores for this deal or its company, filtered server-side
  // — asked once the deal (and so its company) is known. Never asked for on
  // a guest's behalf; the phone page has no ERS card.
  const dealTarget = deal?.target || null;
  const dealLoaded = !!deal;
  React.useEffect(() => {
    if (!dealLoaded || phone || window.AtlasIsGuest) return;
    window.AtlasApi.listExitReadinessScores({ engagement_id: dealId, company_id: dealTarget, limit: 50 })
      .then((ers) => setErsScores((ers && ers.items) || []))
      .catch(() => setErsScores([]));
  }, [dealId, dealTarget, dealLoaded, phone]);

  // Inline-edit one buyer row. The server records each edited field in
  // manual_fields so the reconcile derivations never overwrite it; the PATCH
  // response is the fresh row, merged back in place (no full refetch).
  // After "Find buyers" adds picks to the deal, re-read the list (server-side
  // filtered + named, same call the mount uses) rather than guessing rows.
  const reloadBuyerEngs = React.useCallback(() => {
    window.AtlasApi.buyersForEngagement(dealId)
      .then((be) => setBuyerEngs((be && be.items) || []))
      .catch(() => {});
  }, [dealId]);

  const mergeBuyerEng = React.useCallback((beId, row) => {
    setBuyerEngs((prev) => (prev || []).map((b) => (b.id === beId ? { ...b, ...row } : b)));
  }, []);
  const patchBuyerEng = React.useCallback(async (beId, patch) => {
    const prev = (buyerEngs || []).find((b) => b.id === beId);
    const updated = await window.AtlasApi.updateBuyerEngagement(beId, patch);
    mergeBuyerEng(beId, updated);
    // Passing (or un-passing) a buyer moves its pursuit record with it —
    // say so, since it reaches DealCloud.
    if (prev && updated && updated.pursuit_stage && updated.pursuit_stage !== prev.pursuit_stage) {
      pushToast({ type: 'success', title: `Pursuit moved to ${cleanDcStage(updated.pursuit_stage)}`,
                  body: `${prev.buyer_name || 'The buyer'}'s pursuit record follows — DealCloud too.` });
    }
  }, [buyerEngs, mergeBuyerEng]);

  // Resizable side panels — drag the dividers between Engagement | middle | Tasks.
  // The middle column flexes (1fr); we resize the two side columns and persist.
  const bodyRef = React.useRef(null);
  const paneDrag = React.useRef(null);   // 'left' | 'right'
  const [panes, setPanes] = React.useState(() => {
    try { const s = JSON.parse(localStorage.getItem('atlas.dealPanes') || 'null'); if (s && s.left && s.right) return s; } catch (_) {}
    return { left: 288, right: 300 };
  });
  React.useEffect(() => {
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    const onMove = (e) => {
      if (!paneDrag.current || !bodyRef.current) return;
      const r = bodyRef.current.getBoundingClientRect();
      setPanes((p) => paneDrag.current === 'left'
        ? { ...p, left: clamp(e.clientX - r.left, 200, r.width - p.right - 280) }
        : { ...p, right: clamp(r.right - e.clientX, 200, r.width - p.left - 280) });
    };
    const onUp = () => {
      if (!paneDrag.current) return;
      paneDrag.current = null; document.body.style.cursor = ''; document.body.style.userSelect = '';
      setPanes((p) => { try { localStorage.setItem('atlas.dealPanes', JSON.stringify(p)); } catch (_) {} return p; });
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);
  const startPane = (which) => (e) => { paneDrag.current = which; document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; e.preventDefault(); };

  // Phone renders PhoneDealPage (one column, same state and handlers); iPad
  // keeps this layout but sizes the columns from the body's OWN width — the
  // sidebar may or may not be docked, so the window width says little. Only
  // measured on a tablet: desktop keeps its fixed three columns untouched.
  const [bodyW, setBodyW] = React.useState(0);
  const bodyShown = !!deal && !device.phone && !outreachOpen;
  React.useEffect(() => {
    const el = bodyRef.current;
    if (!device.tablet || !bodyShown || !el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      setBodyW((prev) => (prev === w ? prev : w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [device.tablet, bodyShown]);

  // The header's Log button and the Next action card's Update open the
  // notes composer through this ({ kind, n } — a fresh object per pick;
  // RecordNotes' composeRequest, the same contract the phone page uses).
  const [composeRequest, setComposeRequest] = React.useState(null);
  const compose = React.useCallback((kind) => setComposeRequest({ kind, n: Date.now() }), []);

  // ── Done with hooks. Safe to branch below.

  if (denied) {
    return (
      <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
        <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>You don't have access to this engagement.</div>
        {guest ? 'Your Atlas access is limited to the deals you\'re on.' : 'Ask an admin if you need it.'}
        <div style={{ marginTop: 10 }}><Btn size="sm" variant="outline" onClick={back}>{guest ? 'Your deals' : 'Engagements'}</Btn></div>
      </div>
    );
  }

  if (!deal) {
    if (device.phone) return <PhoneDealSkeleton />;
    return (
      <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading engagement…</div>
    );
  }

  // The outreach tracker is a full-width campaign surface — on a phone the
  // ?tab=outreach link lands on the deal page with an "on desktop" note.
  if (outreachOpen && window.OutreachTracker && !guest && !device.phone) {
    return <window.OutreachTracker deal={deal} company={company} onBack={closeOutreach} />;
  }

  const stageList = pipelineStages;
  const currentIdx = stageList.findIndex((s) => s.slug === deal.stage);
  const probability = deal.probability != null ? deal.probability : (stageList[currentIdx]?.probability_pct ?? null);
  // Exit affordance: reasons come from the CURRENT stage's exit definitions
  // (a deal dies where it stands — prospect exits differ from LOI exits).
  // The current exit's recoverability decides whether Reopen is offered.
  // Buyer shells are the exception: their Atlas stage is force-mapped onto
  // the seller funnel, so its exit reasons ("Seller withdrew"…) make no
  // sense — a shell exit means THIS buyer passed. Offer only "Buyer passed"
  // (bs_call's definition); the backend mirrors it onto the interested-
  // buyers roster row as interest_level='passed'.
  const isBuyerShell = deal.type === 'buyer_buyside';
  const buyerPassExit = isBuyerShell
    ? (stages || []).flatMap((s) => s.exits || []).find((x) => x.slug === 'buyer_passed' && x.active !== false)
    : null;
  const stageExits = isBuyerShell
    ? (buyerPassExit ? [buyerPassExit] : [])
    : (stageList[currentIdx]?.exits || []).filter((x) => x.active !== false);
  const currentExit = deal._raw?.exit_id
    ? (stages || []).flatMap((s) => s.exits || []).find((x) => x.id === deal._raw.exit_id)
    : null;
  const dealCompanyId = deal._raw?.company_id || deal.target || null;

  // After Confirm / Not it on the header's "Acquired?" pill: a confirm exits
  // this deal as Company sold and files a note — re-pull the deal, the
  // company and the notes (same as the reversal drawer's onDone).
  const afterAcquisitionDecision = async () => {
    try {
      const fresh = await window.AtlasApi.getEngagement(dealId);
      setDeal(window.AtlasData.__adaptEngagement(fresh));
      window.dispatchEvent(new CustomEvent('deal-updated', { detail: { id: dealId, updated: fresh } }));
    } catch (_) {}
    if (dealCompanyId) {
      window.AtlasApi.getCompany(dealCompanyId)
        .then((co) => { if (co) setCompany(window.AtlasData.__adaptCompany(co)); })
        .catch(() => {});
    }
    reloadNotes();
  };
  // The deal's company may have been bought (a portfolio page, the ownership
  // check, a web signal or Apollo says so) and nobody has confirmed it yet.
  // Hidden once the deal is exited, for buyer shells, and for guests (who
  // cannot decide); views/acquisition_confirm.jsx.
  const acquisitionPill = window.AcquisitionHeaderPill && dealCompanyId && !guest ? (
    <window.AcquisitionHeaderPill companyId={dealCompanyId} companyName={deal.targetName || (company || {}).name}
      hidden={Boolean(deal.exitName) || deal.type === 'buyer_buyside' || Boolean(company && company._raw && company._raw.is_acquired)}
      onDecided={afterAcquisitionDecision} style={device.phone ? { marginTop: 10 } : null}
      drawerZIndex={panel.name ? 70 : undefined} />
  ) : null;

  const daysInStage = (() => {
    const ts = deal._raw?.last_stage_update;
    if (!ts) return null;
    return Math.round((Date.now() - new Date(ts).getTime()) / (1000 * 60 * 60 * 24));
  })();

  // The one stage-move path (the stage rail, the Properties Stage picker and
  // the phone Stage sheet): a move to Won opens the Closing drawer (the PATCH
  // carries the closing); anything else confirms first.
  const requestStage = (s, i) => {
    if (i === currentIdx || guest) return;
    if (window.isWonSlug && window.isWonSlug(s.slug)) { setWonStage(s); return; }
    showConfirm({
      title: `${i > currentIdx ? 'Advance' : 'Move back'} to ${s.name}?`,
      body: `"${deal.name}" moves from ${deal.stageName || deal.stage} to ${s.name}.`,
      onConfirm: () => saveDeal({ stage_id: s.id }),
    });
  };
  const reopenDeal = () => {
    showConfirm({
      title: 'Reopen this deal?',
      body: `"${deal.name}" was exited (${deal.exitName}). Reopening returns it to the live pipeline at ${deal.stageName || deal.stage}.`
        + (deal._raw?.dealcloud_id ? ' If DealCloud still marks it Lost, the next sync will exit it again.' : ''),
      confirmLabel: 'Reopen deal',
    }).then((ok) => { if (ok) saveDeal({ exit_id: null, exit_notes: null }); });
  };

  // PanelHost (record_panels.jsx): the one gate + host for ?panel= on both layouts.
  const panelFacts = { shell: deal.type === 'buyer_buyside', companyId: deal.target || null };
  const panelCtx = { companyId: dealCompanyId, reloadNotes, reloadRecord: reloadDeal, company,
    onCompanyChange: (raw) => setCompany(window.AtlasData.__adaptCompany(raw)), stageList, currentIdx, requestStage,
    saveDeal, savePursuitStage };   // All details on a phone (DealDetailsBody); v2 spreads its own on top

  // Drawers both layouts open: workspace creator, Won → closing, exit.
  const overlays = (
    <>
      {creatingWorkspace && (
        <WorkspaceCreatorDrawer
          deal={deal}
          activeUser={activeUser}
          onClose={() => setCreatingWorkspace(false)}
          onCreated={() => { setCreatingWorkspace(false); reloadWorkspaces(); }}
        />
      )}

      {window.ClosingDrawer && (
        <window.ClosingDrawer
          zIndex={panel.name ? 70 : undefined}
          open={!!wonStage}
          deal={deal}
          closing={null}
          onClose={() => setWonStage(null)}
          onSubmit={async (closing) => {
            await saveDeal({ stage_id: wonStage.id, closing });
            setWonStage(null);
          }}
        />
      )}
      <ExitDealDrawer
        zIndex={panel.name ? 70 : undefined}
        open={exiting}
        deal={deal}
        exits={stageExits}
        onClose={() => setExiting(false)}
        onExit={async (exit_id, exit_notes) => {
          await saveDeal({ exit_id, exit_notes: exit_notes || null });
          setExiting(false);
        }}
      />
      {window.ReverseAcquisitionDrawer && (
        <window.ReverseAcquisitionDrawer
          open={reversing}
          companyId={dealCompanyId}
          companyName={deal.targetName || (company && company.name)}
          onClose={() => setReversing(false)}
          onDone={async () => {
            setReversing(false);
            // The deal is open again, the company unflagged, and a note filed.
            try {
              const fresh = await window.AtlasApi.getEngagement(dealId);
              setDeal(window.AtlasData.__adaptEngagement(fresh));
              window.dispatchEvent(new CustomEvent('deal-updated', { detail: { id: dealId, updated: fresh } }));
            } catch (_) {}
            window.AtlasApi.getCompany(dealCompanyId)
              .then((co) => { if (co) setCompany(window.AtlasData.__adaptCompany(co)); })
              .catch(() => {});
            reloadNotes();
          }}
        />
      )}
      <window.PanelHost kind="deal" id={dealId} record={deal} facts={panelFacts}
        variant={deal.type === 'buyer_buyside' ? 'shell' : 'seller'} ctx={panelCtx} />
      <window.NoteHost kind="deal" id={dealId} onChange={reloadNotes} />
    </>
  );

  if (device.phone) {
    return (
      <>
        <PhoneDealPage
          deal={deal} company={company} stages={stages} stageList={stageList} currentIdx={currentIdx}
          stageExits={stageExits} currentExit={currentExit} probability={probability} daysInStage={daysInStage}
          activeUser={activeUser} guest={guest} canOpenCompanies={canOpenCompanies} liveMeeting={liveMeeting}
          isPinned={isPinned} onTogglePin={togglePin} outreachLinked={!guest && !panel.name && panel.params.tab === 'outreach'}
          onSave={saveDeal} onPursuitStage={savePursuitStage} onRequestStage={requestStage}
          onExit={() => setExiting(true)} onReopen={reopenDeal} onReloadDeal={reloadDeal}
          onNotAcquired={deal.exitName && currentExit?.slug === 'company_sold' && dealCompanyId ? () => setReversing(true) : null}
          acquisitionPill={acquisitionPill}
          tasks={tasks} onTasksChange={reloadTasks} stageHistory={stageHistory}
          notes={notes} notesTotal={notesTotal} notesLoadingMore={notesLoadingMore} onLoadMoreNotes={loadMoreNotes}
          notesSearch={notesSearch} onNotesSearch={setNotesSearch} notesQ={notesQ} onNotesChange={reloadNotes}
        />
        {overlays}
      </>
    );
  }

  // Wave D (step 10): the new layout, when the team's record layout is new
  // (Settings → Feature flags; an admin's ⌘K preview; never a phone: the surface is
  // 'phone' there). The v2 page renders RecordPage, whose PanelHost and NoteHost
  // replace the ones in `overlays`; the action drawers in `overlays` are
  // re-mounted by the v2 page from these handlers (views/deal_page_v2.jsx).
  if (window.recordSurface() === 'v2' && typeof window.DealPageV2 === 'function') {
    return (
      <window.DealPageV2 deal={deal} company={company} stages={stages} stageList={stageList} currentIdx={currentIdx}
        stageExits={stageExits} currentExit={currentExit} probability={probability} daysInStage={daysInStage}
        activeUser={activeUser} guest={guest} canOpenCompanies={canOpenCompanies} liveMeeting={liveMeeting}
        isPinned={isPinned} onTogglePin={togglePin} back={back}
        onSave={saveDeal} onPursuitStage={savePursuitStage} onRequestStage={requestStage} onReopen={reopenDeal} onReloadDeal={reloadDeal}
        tasks={tasks} onTasksChange={reloadTasks} stageHistory={stageHistory} workspaces={workspaces} callAnalytics={callAnalytics}
        feed={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: loadMoreNotes, reload: reloadNotes,
          search: notesSearch, setSearch: setNotesSearch, q: notesQ }}
        composeRequest={composeRequest} onCompose={guest ? null : compose} logDraftDot={logDraftDot}
        panel={{ facts: panelFacts, ctx: panelCtx }}
        shared={{ acquisitionPill, onCompanyChange: (raw) => setCompany(window.AtlasData.__adaptCompany(raw)) }}
        drawers={{
          exiting, onExitClose: () => setExiting(false),
          onExit: async (exit_id, exit_notes) => { await saveDeal({ exit_id, exit_notes: exit_notes || null }); setExiting(false); },
          onExitOpen: () => setExiting(true),
          wonStage, onWonClose: () => setWonStage(null),
          onWon: async (closing) => { await saveDeal({ stage_id: wonStage.id, closing }); setWonStage(null); },
          reversing, onReverseOpen: () => setReversing(true), onReverseClose: () => setReversing(false),
          onReverseDone: afterAcquisitionDecision,
          creatingWorkspace, onWorkspaceOpen: () => setCreatingWorkspace(true), onWorkspaceClose: () => setCreatingWorkspace(false),
          onWorkspaceCreated: () => { setCreatingWorkspace(false); reloadWorkspaces(); },
        }} />
    );
  }

  // iPad: pick the column layout from the body's measured width (0 = not
  // measured yet, or desktop → the classic three columns).
  //   ≥1100px  three columns, side panes clamped so the center keeps ≥320px
  //   720–1100 Properties + center; Tasks/Files/Workspaces lead the center
  //            (720, not 760, so an iPad mini in portrait — 744 — gets it)
  //   <720     one scrolling column (Split View, Slide Over)
  const tablet = !!device.tablet;
  const cols = !tablet || !bodyW ? 3 : bodyW >= 1100 ? 3 : bodyW >= 720 ? 2 : 1;
  const sideMax = tablet && bodyW ? Math.max(200, Math.floor((bodyW - 320) / 2)) : Infinity;
  const leftW = cols === 2 ? Math.min(panes.left, 300) : Math.min(panes.left, sideMax);
  const rightW = Math.min(panes.right, sideMax);
  // A drag divider can't be dragged by a finger — none on touch.
  const showDividers = !device.touch;
  // Six KPI tiles need ~720px; on a narrower iPad page they wrap to rows.
  const kpiWrap = tablet && bodyW > 0 && bodyW < 900;
  const kpiMinW = kpiWrap ? 120 : undefined;

  const propertiesEl = (
    <PropertiesPanel
      deal={deal}
      company={company}
      pipelineStages={pipelineStages}
      onSave={saveDeal}
      onPursuitStage={savePursuitStage}
      onStageSelect={(s) => requestStage(s, pipelineStages.findIndex((x) => x.id === s.id))}
    />
  );
  const centerEl = (
    <CenterPane
      deal={deal}
      activeUser={activeUser}
      stageHistory={stageHistory}
      buyerEngs={buyerEngs}
      onPatchBuyerEng={guest ? null : patchBuyerEng}
      onBuyerEngUpdate={mergeBuyerEng}
      onBuyersAdded={reloadBuyerEngs}
      ersScores={ersScores}
      highlights={highlights}
      callAnalytics={callAnalytics}
      notes={notes}
      notesTotal={notesTotal}
      notesLoadingMore={notesLoadingMore}
      onLoadMoreNotes={loadMoreNotes}
      notesSearch={notesSearch}
      onNotesSearch={setNotesSearch}
      notesQ={notesQ}
      onNotesChange={reloadNotes}
      onDealChange={reloadDeal}
      onTasksChange={reloadTasks}
      onOpenOutreach={openOutreach}
      onSave={saveDeal}
      composeRequest={composeRequest}
      onCompose={guest ? null : compose}
    />
  );
  const railEl = (
    <>
      <TasksPanel
        deal={deal}
        activeUser={activeUser}
        tasks={tasks}
        onChange={reloadTasks}
      />
      <window.FilesPanel
        scope={{ engagement_id: dealId }}
        title="Files"
      />
      {!guest && <WorkspacesPanel
        deal={deal}
        workspaces={workspaces}
        onNew={() => setCreatingWorkspace(true)}
      />}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface-2)' }}>
      {/* Header */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px 0', fontSize: 11.5, color: 'var(--text-3)' }}>
          <button onClick={back} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5 }}>
            <Icon.ChevronL size={11} /> {guest ? 'Your deals' : 'Engagements'}
          </button>
          <Icon.ChevronR size={11} color="var(--text-4)" />
          <span>{deal.name}</span>
        </div>

        <div style={{ padding: '10px 16px 0', display: 'flex', alignItems: 'flex-start', gap: 14, ...(tablet ? { flexWrap: 'wrap', rowGap: 10 } : null) }}>
          <Logo name={(company || {}).name || deal.name} color={(company || {}).logo} size={40} />
          {/* iPad: the title block keeps a real width and the action
              buttons wrap under it instead of squeezing it to nothing. */}
          <div style={{ flex: 1, minWidth: tablet ? 260 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {guest
                ? <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.015em' }}>{deal.name}</h1>
                : <EditableTitle value={deal.name} onSave={(name) => saveDeal({ name })} />}
              {ENG_TYPE_PILL[deal.type] && <Pill hue={ENG_TYPE_PILL[deal.type].hue} sz="sm">{ENG_TYPE_PILL[deal.type].label}</Pill>}
              {deal.type !== 'buyer_buyside' && (
                <Pill hue={STAGE_DOT_HUE[deal.stage] || 'slate'} dot>{deal.stageName || deal.stage}</Pill>
              )}
              {/* Shells show DealCloud's real buyer-funnel stage instead of
                  the force-mapped seller stage hidden above. */}
              {deal.type === 'buyer_buyside' && deal.dcStage && (
                <Pill hue={dcStageHue(deal.dcStage)} dot title="Pursuit stage (verbatim from DealCloud)">{cleanDcStage(deal.dcStage)}</Pill>
              )}
              {deal.onHold && (
                <Pill hue="amber" sz="sm">
                  On hold · {deal.onHold}{deal.onHoldReason ? ` · ${HOLD_REASON_LABEL[deal.onHoldReason] || deal.onHoldReason}` : ''}
                </Pill>
              )}
              {acquisitionPill}
              {liveMeeting && (
                <a href={liveMeeting.meeting_link || undefined} target="_blank" rel="noreferrer"
                   title={liveMeeting.title || 'Meeting'}
                   style={{ textDecoration: 'none' }}>
                  <Pill hue="green" dot sz="sm">In a meeting now</Pill>
                </a>
              )}
              {deal.stale && deal.exitName && <Pill hue="rose" sz="sm">Exited · {deal.exitName}</Pill>}
              {deal.archived && <Pill hue="amber" sz="sm">{deal.archivedReason === 'reconciled_duplicate' ? 'Archived · duplicate' : 'Archived'}</Pill>}
              <SyncBadge
                lastSyncedAt={deal.lastSyncedAt} updatedAt={deal.updatedAt}
                dealcloudId={deal.dealcloudId || deal._raw?.dealcloud_id}
                recordType="engagement" recordId={guest ? null : dealId}
                onSynced={async () => {
                  const fresh = await window.AtlasApi.getEngagement(dealId);
                  setDeal(window.AtlasData.__adaptEngagement(fresh));
                  window.dispatchEvent(new CustomEvent('deal-updated', { detail: { id: dealId, updated: fresh } }));
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 4, fontSize: 12, color: 'var(--text-3)', flexWrap: 'wrap' }}>
              {(company || {}).name && !canOpenCompanies && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.Building size={12} /> {company.name}</span>
              )}
              {(company || {}).name && canOpenCompanies && (
                <span
                  onClick={() => window.AtlasNav?.goToCompany?.(company.id || deal.target)}
                  title={`Open ${company.name}`}
                  onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                  onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', textUnderlineOffset: 2 }}
                ><Icon.Building size={12} /> {company.name}</span>
              )}
              {deal.sector && deal.sector !== '—' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.Tag size={12} /> {deal.sector}</span>}
              {deal.owner && deal.owner !== '—' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.User size={12} /> <UserLink id={deal._raw?.dealmaker_id} name={deal.owner} /></span>}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.Clock size={12} /> Updated {deal.updated}</span>
            </div>
          </div>
          {!guest && <div style={{ display: 'flex', gap: 6, ...(tablet ? { flexWrap: 'wrap' } : null) }}>
            <Btn size="sm" variant={isPinned ? 'primary' : 'outline'}
              icon={<Icon.Pin size={12} />}
              disabled={isPinned === null}
              title={isPinned ? 'Unpin from sidebar' : 'Pin to your sidebar'}
              onClick={togglePin}>{isPinned ? 'Pinned' : 'Pin'}</Btn>
            {/* Exit is a deal-level outcome, deliberately NOT next to the
                on-hold control — a deal can die without ever being parked. */}
            {!deal.exitName && stageExits.length > 0 && (
              <Btn size="sm" variant="outline" icon={<Icon.Close size={12} />}
                style={{ color: 'var(--rose-fg, oklch(0.50 0.14 25))' }}
                onClick={() => setExiting(true)}>Exit deal…</Btn>
            )}
            {deal.exitName && currentExit?.is_recoverable && (
              <Btn size="sm" variant="outline" onClick={reopenDeal}>Reopen deal</Btn>
            )}
            {/* Company sold is final by hand — but the acquisition can be wrong
                (an owner's own holding company, a name match). This reopens the
                deal, clears the acquired flag and REQUIRES a reason. */}
            {deal.exitName && currentExit?.slug === 'company_sold' && dealCompanyId && (
              <Btn size="sm" variant="outline"
                title="The company was not actually acquired — reopen the deal and record why"
                onClick={() => setReversing(true)}>Not acquired…</Btn>
            )}
            {/* CIM Command Center — seller deals only; a merged-away
                duplicate's work lives on the kept deal. */}
            {deal.type !== 'buyer_buyside' && !deal._raw?.is_duplicate && (
              <Btn size="sm" variant="outline" icon={<Icon.Layers size={12} />}
                title="CIM Command Center"
                onClick={() => window.AtlasNav?.goToCim?.(deal.id)}>CIM</Btn>
            )}
            <Btn size="sm" variant="outline" icon={<Icon.Sparkle size={12} />}
              title="Ask Atlas about this deal"
              onClick={() => window.dispatchEvent(new CustomEvent('ask-atlas', { detail: { query: '' } }))}>Ask about this deal</Btn>
            <Btn size="sm" variant="outline" icon={<Icon.Sparkle size={12} />} onClick={() => setCreatingWorkspace(true)}>New workspace</Btn>
            {/* The page's main action: log a note / call / email / meeting,
                add a task or update the next action (views/log_button.jsx). */}
            {window.LogButton && <window.LogButton onPick={compose} disabled={!activeUser} dot={logDraftDot} />}
          </div>}
        </div>

        {/* Buyer-shell banner — a buyer_buyside record is DealCloud's per-
            pursuit file on ONE seller deal (migration 048), not a standalone
            engagement; without this it reads like the buyer's own deal. */}
        {deal.type === 'buyer_buyside' && deal.sellerEngagementId && (
          <div style={{
            margin: '12px 16px 0', padding: '9px 12px', borderRadius: 8,
            background: 'var(--violet-bg, var(--surface-2))', color: 'var(--violet-fg, var(--text-2))',
            fontSize: 12, lineHeight: 1.5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <Icon.Users size={13} />
            <span>
              This is a buyer-side pursuit record — activity
              by {(company && company.name) || deal.name} on one deal, not a standalone engagement.
            </span>
            <button
              onClick={() => window.AtlasNav?.goToDeal?.(deal.sellerEngagementId)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                color: 'inherit', fontSize: 12, fontWeight: 600,
                textDecoration: 'underline', textUnderlineOffset: 2, whiteSpace: 'nowrap',
              }}
            >Open {deal.sellerEngagementName || 'the deal'} →</button>
          </div>
        )}

        {/* Archived banner — an archived engagement (usually a reconciled
            DealCloud duplicate) must not read like the live record. */}
        {deal.archived && (
          <div style={{
            margin: '12px 16px 0', padding: '9px 12px', borderRadius: 8,
            background: 'var(--amber-bg)', color: 'var(--amber-fg)',
            fontSize: 12, lineHeight: 1.5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <Icon.Layers size={13} />
            <span>
              {deal._raw?.is_duplicate
                ? 'This deal was merged into another deal at the same company — its notes, tasks, history and buyers are there now.'
                : deal.archivedReason === 'reconciled_duplicate'
                ? 'This is an archived duplicate — DealCloud had a second engagement record for this company. The live engagement is on the company page.'
                : `This engagement is archived${deal.archivedReason ? ` (${deal.archivedReason.replace(/_/g, ' ')})` : ''} and excluded from live pipeline views.`}
            </span>
            {deal._raw?.is_duplicate && deal._raw?.merged_into_id && (
              <Btn size="xs" onClick={() => window.AtlasNav?.goToDeal?.(deal._raw.merged_into_id)}>Open the kept deal</Btn>
            )}
            {(company || deal.target) && (
              <button
                onClick={() => window.AtlasNav?.goToCompany?.((company && company.id) || deal.target)}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                  color: 'inherit', fontSize: 12, fontWeight: 600,
                  textDecoration: 'underline', textUnderlineOffset: 2, whiteSpace: 'nowrap',
                }}
              >Open {(company && company.name) || 'company'} →</button>
            )}
          </div>
        )}

        {/* Stage rail — hidden on buyer shells: their DC stages are force-
            mapped onto the seller funnel by the import (STAGE_MAP anchors
            unknown-history stages at ds_call), so the chevrons would show a
            fictional seller-pipeline position for what is one buyer's
            pursuit of one deal. Same for the funnel KPIs below. */}
        {stageList.length > 0 && deal.type !== 'buyer_buyside' && (
          <div style={{ padding: '14px 16px 0' }}>
            <StageRail
              stages={stageList}
              currentIdx={currentIdx}
              exited={Boolean(deal.exitName)}
              onSelect={requestStage}
              history={stageHistory} raw={deal._raw}
            />
          </div>
        )}

        {/* KPIs */}
        {deal.type !== 'buyer_buyside' && (
        <div style={{ display: 'flex', padding: '14px 16px 0', ...(kpiWrap ? { flexWrap: 'wrap', rowGap: 10 } : null) }}>
          <Kpi label="Est. fees"        value={fmtMoney(deal.size)} minW={kpiMinW} />
          <Kpi label="Weighted"         value={fmtMoney(deal.weighted)} minW={kpiMinW} />
          <Kpi label="Probability"      value={probability != null ? `${probability}%` : '—'} minW={kpiMinW} />
          {/* Revenue/EBITDA: DealCloud's engagement-level Reported* fields are
              sparse — usually null for sellsides where the data lives on the
              linked Company. Fall back to company.revenue_est with a small
              "via Company" subline so users know where it came from. */}
          <Kpi
            label="Revenue"
            value={fmtMoney(deal.reportedRevenue ?? deal.companyRevenueEst)}
            sub={deal.reportedRevenue == null && deal.companyRevenueEst != null ? 'via Company' : null}
            minW={kpiMinW}
          />
          <Kpi
            label="EBITDA"
            value={fmtMoney(deal.reportedEbitda ?? deal.companyEbitdaEst)}
            sub={deal.reportedEbitda == null && deal.companyEbitdaEst != null ? 'via Company' : null}
            minW={kpiMinW}
          />
          <Kpi label="Days in stage"    value={daysInStage != null ? `${daysInStage}d` : '—'} minW={kpiMinW} />
        </div>
        )}

        <div style={{ height: 14 }} />
      </div>

      {/* Body — three columns (iPad: two or one when narrow, see `cols`) */}
      <div ref={bodyRef} style={{
        flex: 1, overflow: cols === 1 ? 'auto' : 'hidden', display: 'grid', gap: 0, position: 'relative',
        gridTemplateColumns: cols === 3 ? `${leftW}px minmax(0, 1fr) ${rightW}px`
          : cols === 2 ? `${leftW}px minmax(0, 1fr)` : 'minmax(0, 1fr)',
        ...(cols === 1 ? { alignContent: 'start', background: 'var(--surface-2)', WebkitOverflowScrolling: 'touch' } : null),
      }}>
        {cols === 3 && showDividers && <div onMouseDown={startPane('left')} title="Drag to resize" style={{ position: 'absolute', top: 0, bottom: 0, left: leftW - 3, width: 7, cursor: 'col-resize', zIndex: 5 }} />}
        {cols === 3 && showDividers && <div onMouseDown={startPane('right')} title="Drag to resize" style={{ position: 'absolute', top: 0, bottom: 0, right: rightW - 3, width: 7, cursor: 'col-resize', zIndex: 5 }} />}
        {cols === 1 ? (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>{propertiesEl}</div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>{railEl}</div>
            {centerEl}
          </div>
        ) : (
          <>
            <div style={{ borderRight: '1px solid var(--border)', overflow: 'auto', background: 'var(--surface)' }}>
              {propertiesEl}
            </div>
            <div style={{ overflow: 'auto', padding: 16, background: 'var(--surface-2)' }}>
              {/* Two columns: the right rail's panels lead the center. */}
              {cols === 2 && (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>{railEl}</div>
              )}
              {centerEl}
            </div>
            {cols === 3 && (
              <div style={{ borderLeft: '1px solid var(--border)', overflow: 'auto', background: 'var(--surface)' }}>
                {railEl}
              </div>
            )}
          </>
        )}
      </div>

      {overlays}
    </div>
  );
};

// ============ Phone deal page ============
// What DealDetail renders on a phone: one scrolling column over the SAME
// state and handlers as the desktop page (DealDetail passes them in), so
// every business rule stays in one place — a stage pick goes through
// `onRequestStage` (Won → Closing drawer, confirm + DealCloud caveat), hold
// through the PATCH + HoldReasonPrompt, exit through ExitDealDrawer, a next
// action is a 'Next Action' note, a dealmaker change goes through
// saveOwnerWithScope. What a dealmaker needs before/after a call leads
// (stage, next action, contact, key facts, tasks, notes); the shared blocks
// (RecordTasks, LazyFold, the read-only Buyers card, the All details and
// Files sheets through ?panel=) are the desktop's own.
const pdpTap = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };
const pdpCard = { background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' };
const PHONE_HOLD_OPTS = [
  { value: 'high',      label: 'On hold · high priority' },
  { value: 'low',       label: 'On hold · low priority' },
  { value: 'repricing', label: 'On hold · repricing' },
];

const PhoneDealSkeleton = () => (
  <div style={{ height: '100%', overflow: 'hidden', background: 'var(--bg)' }} aria-busy="true">
    <div style={{ ...pdpCard, borderTop: 'none', padding: 16, display: 'flex', gap: 12 }}>
      <window.Skeleton w={44} h={44} style={{ borderRadius: 8, flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <window.Skeleton w="70%" h={18} />
        <window.Skeleton w="45%" h={13} />
        <window.Skeleton w="100%" h={44} style={{ borderRadius: 12, marginTop: 6 }} />
      </div>
    </div>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{ ...pdpCard, marginTop: 24, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <window.Skeleton w="35%" h={12} />
        <window.Skeleton w="90%" h={14} />
        <window.Skeleton w="60%" h={14} />
      </div>
    ))}
  </div>
);

// Label · value row in a grouped list. `value` may be an EditableCell (which
// opens its own sheet on a phone) or plain text.
const PhoneFactRow = ({ label, children, first }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 48, padding: '6px 16px', borderTop: first ? 'none' : '1px solid var(--border)' }}>
    <span style={{ width: 112, flexShrink: 0, fontSize: 15, color: 'var(--text-3)' }}>{label}</span>
    <span style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end', fontSize: 15, color: 'var(--text)', textAlign: 'right' }}>{children}</span>
  </div>
);

// Call / Email pill buttons — real tel: / mailto: links; a missing value
// renders a muted, non-interactive pill so the layout doesn't jump.
const PhoneContactAction = ({ href, icon, label }) => {
  const style = {
    ...pdpTap, flex: 1, minWidth: 0, height: 44, borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    fontSize: 15, fontWeight: 600, textDecoration: 'none',
    background: href ? 'var(--accent-soft)' : 'var(--surface-2)', color: href ? 'var(--accent-text)' : 'var(--text-4)',
  };
  return href
    ? <a href={href} onClick={(e) => e.stopPropagation()} style={style}>{icon}{label}</a>
    : <span aria-disabled="true" style={style}>{icon}{label}</span>;
};

const PhoneDealPage = ({
  deal, company, stages, stageList, currentIdx, stageExits, currentExit, probability, daysInStage,
  activeUser, guest, canOpenCompanies, liveMeeting, isPinned, onTogglePin, outreachLinked,
  onSave, onPursuitStage, onRequestStage, onExit, onReopen, onReloadDeal, onNotAcquired, acquisitionPill,
  tasks, onTasksChange, stageHistory,
  notes, notesTotal, notesLoadingMore, onLoadMoreNotes, notesSearch, onNotesSearch, notesQ, onNotesChange,
}) => {
  const [stageOpen, setStageOpen] = React.useState(false);
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const [nextOpen, setNextOpen] = React.useState(false);
  const [hotOpen, setHotOpen] = React.useState(false);
  const [renaming, setRenaming] = React.useState(false);
  const [contactsOpen, setContactsOpen] = React.useState(false);
  const [addingContact, setAddingContact] = React.useState(false);
  const [askingHoldReason, setAskingHoldReason] = React.useState(false);
  const [composeRequest, setComposeRequest] = React.useState(null);
  const [naExpanded, setNaExpanded] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [contacts, setContacts] = React.useState(null);   // all company contacts, lazy
  const buyersRef = React.useRef(null);                     // the Buyers card: the outreach banner scrolls to it
  React.useEffect(() => {
    if (guest) return;
    window.AtlasApi.listTeamMembers()
      .then((m) => setUsers(Array.isArray(m) ? m.filter((u) => u.active !== false) : []))
      .catch(() => setUsers([]));
  }, [guest]);
  React.useEffect(() => {
    // Guests can't list a company's contacts (the API denies it) — the
    // "All N" action is hidden for them, so never fetch.
    if (guest || !contactsOpen || contacts !== null || !deal.target) return;
    window.AtlasApi.listContacts({ company_id: deal.target, limit: 200 })
      .then((r) => setContacts((r && r.items) || []))
      .catch(() => setContacts([]));
  }, [guest, contactsOpen, contacts, deal.target]);

  const nav = window.AtlasNav || {};
  const dcLocked = Boolean(deal._raw?.dealcloud_id);
  const isBuyerShell = deal.type === 'buyer_buyside';
  const exited = Boolean(deal.exitName);
  const companyName = (company && company.name) || deal.targetName;
  const compose = (kind) => setComposeRequest({ kind, n: Date.now() });

  // Hold is its own field (the stage stays put): PATCH on_hold, then ask the
  // delay type when a deal was just parked without one — same contract as
  // the Properties "On hold" row and the grid's hold picks.
  const setHold = async (on_hold) => {
    try {
      await onSave({ on_hold });
      if (on_hold && (!deal.onHold || !deal.onHoldReason)) setAskingHoldReason(true);
    } catch (_) { /* saveDeal already toasted */ }
  };

  const saveNextAction = async ({ body, date }) => {
    await window.AtlasApi.createNote({
      body,
      note_type: 'general',
      dc_note_subtype: 'Next Action',
      next_action_date: date || null,
      engagement_id: deal.id,
      company_id: deal.target || null,
      author_id: activeUser?.id || null,
      author_name: activeUser?.full_name || null,
    });
    await onReloadDeal();
    onNotesChange && onNotesChange();
  };

  // ── Next action (date colour mirrors the grid's NextActionCell)
  const today = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; })();
  const naDate = deal._raw?.next_action_date ? String(deal._raw.next_action_date).slice(0, 10) : null;
  const naNote = deal.nextActionNotes || '';
  const naOverdue = naDate && naDate < today;
  const naToday = naDate === today;
  const naLong = naNote.length > 220;

  const stageHue = STAGE_DOT_HUE[deal.stage] || 'slate';
  const stagePill = isBuyerShell
    ? (deal.dcStage ? <Pill hue={dcStageHue(deal.dcStage)} dot>{cleanDcStage(deal.dcStage)}</Pill> : <Pill>No pursuit stage</Pill>)
    : <Pill hue={stageHue} dot>{deal.stageName || deal.stage}</Pill>;
  const canMoveStage = !guest && !exited && (isBuyerShell ? !!onPursuitStage : stageList.length > 0);

  const closedStage = (stageList || []).find((x) => x.slug === deal.stage);
  const estCloseNode = deal.estCloseDate
    ? (
      <span style={{ display: 'inline-flex', alignItems: 'baseline', columnGap: 6 }}>
        <EstCloseValue value={deal.estCloseDate} closed={dealIsClosed(deal, closedStage)} />
        {!isBuyerShell && window.CloseDateHint && <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}><window.CloseDateHint deal={deal} /></span>}
      </span>
    )
    : undefined;

  // ── '…' sheet
  const actionItems = [
    { key: 'call', label: 'Log a call', icon: <Icon.Phone size={18} />, onClick: () => { setActionsOpen(false); compose('call'); } },
    { key: 'note', label: 'Add a note', icon: <Icon.Doc size={18} />, onClick: () => { setActionsOpen(false); compose('general'); } },
    { key: 'task', label: 'Add a task', icon: <Icon.Check size={18} />, onClick: () => { setActionsOpen(false); compose('task'); } },
    { key: 'next', label: 'Update next action', icon: <Icon.Calendar size={18} />, onClick: () => { setActionsOpen(false); setNextOpen(true); } },
    { key: 'pin', label: isPinned ? 'Unpin from sidebar' : 'Pin to sidebar', icon: <Icon.Pin size={18} />, disabled: isPinned === null,
      onClick: () => { setActionsOpen(false); onTogglePin(); } },
    { key: 'ask', label: 'Ask Atlas about this deal', icon: <Icon.Sparkle size={18} />,
      onClick: () => { setActionsOpen(false); window.dispatchEvent(new CustomEvent('ask-atlas', { detail: { query: '' } })); } },
    !isBuyerShell && !deal._raw?.is_duplicate && { key: 'cim', label: 'CIM', icon: <Icon.Layers size={18} />,
      onClick: () => { setActionsOpen(false); nav.goToCim && nav.goToCim(deal.id); } },
    { key: 'rename', label: 'Rename', icon: <Icon.Edit size={18} />, onClick: () => { setActionsOpen(false); setRenaming(true); } },
    companyName && canOpenCompanies && deal.target && { key: 'company', label: `Open ${companyName}`, icon: <Icon.Building size={18} />,
      onClick: () => { setActionsOpen(false); nav.goToCompany && nav.goToCompany((company && company.id) || deal.target); } },
    exited && currentExit?.is_recoverable && { key: 'reopen', label: 'Reopen deal', icon: <Icon.Refresh size={18} />,
      onClick: () => { setActionsOpen(false); onReopen(); } },
    exited && onNotAcquired && { key: 'notacquired', label: 'Not acquired…', sub: 'Reopen it and record why', icon: <Icon.Refresh size={18} />,
      onClick: () => { setActionsOpen(false); onNotAcquired(); } },
    !exited && stageExits.length > 0 && { key: 'exit', label: 'Exit deal…', danger: true, icon: <Icon.Close size={18} />,
      onClick: () => { setActionsOpen(false); onExit(); } },
  ];

  // ── Stage sheet rows
  const check = <Icon.Check size={18} color="var(--accent-text)" />;
  const stageItems = isBuyerShell
    ? PURSUIT_STAGE_OPTIONS.map((o) => ({
      key: o.value, label: o.label, sub: o.description || null,
      icon: <span style={{ width: 10, height: 10, borderRadius: 999, background: (HUE[o.hue] || HUE.slate).dot }} />,
      trailing: pursuitKeyFor(deal.dcStage) === o.value ? check : null,
      onClick: () => {
        setStageOpen(false);
        if (pursuitKeyFor(deal.dcStage) !== o.value) onPursuitStage(o.value).catch(() => {});
      },
    }))
    : stageList.map((st, i) => ({
      key: st.id, label: st.name,
      sub: window.isWonSlug && window.isWonSlug(st.slug) && i !== currentIdx ? 'Asks what it closed at' : null,
      icon: <span style={{ width: 10, height: 10, borderRadius: 999, background: (HUE[STAGE_DOT_HUE[st.slug] || 'slate'] || HUE.slate).dot }} />,
      trailing: i === currentIdx ? check : null,
      onClick: () => { setStageOpen(false); if (i !== currentIdx) onRequestStage(st, i); },
    }));
  const holdItems = isBuyerShell ? [] : [
    ...PHONE_HOLD_OPTS.filter((o) => o.value !== deal.onHold).map((o) => ({
      key: 'hold:' + o.value, label: o.label, onClick: () => { setStageOpen(false); setHold(o.value); },
    })),
    deal.onHold && { key: 'hold:off', label: 'Take off hold', onClick: () => { setStageOpen(false); setHold(null); } },
  ];

  const sectionGap = { marginTop: 12 };
  const facts = { shell: isBuyerShell, companyId: deal.target || null };
  const filesAt = window.RecordPanelsCore.filesPanelFor('deal', facts, { guest });
  const openPanel = (name, params) => window.openRecordPanel(name, params || {}, { via: 'button' });
  // ?tab=outreach (a Slack link, the weekly report): the tracker is the desktop's Buyers
  // workspace, so the banner scrolls to the read-only Buyers card and drops the param.
  const goOutreach = () => {
    try { if (buyersRef.current) buyersRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (_) {}
    window.setRecordParams({ kind: 'deal', id: deal.id }, { tab: null }, { via: 'button' });
  };
  const bannerText = window.DealPageKit ? window.DealPageKit.bannerText(deal) : null;   // 'Archived' | 'Archived · duplicate' | …
  // One line (v2 BannerLine's copy): text · an optional bold link label; the whole row is the tap target.
  const phoneBanner = (text, link, hue) => (
    <button type="button" disabled={!link} onClick={link ? link.onClick : undefined} data-phone-banner={hue}
      style={{ ...pdpTap, display: 'flex', alignItems: 'center', gap: 8, width: 'calc(100% - 32px)', minHeight: 44, margin: '12px 16px 0', padding: '8px 14px',
               borderRadius: 12, border: 'none', textAlign: 'left', fontSize: 14, cursor: link ? 'pointer' : 'default', fontFamily: 'inherit',
               ...(hue === 'violet' ? { background: 'var(--violet-bg, var(--surface-2))', color: 'var(--violet-fg, var(--text-2))' }
                 : hue === 'amber' ? { background: 'var(--amber-bg)', color: 'var(--amber-fg)' }
                 : { background: 'var(--surface)', color: 'var(--text-2)', border: '1px solid var(--border)' }) }}>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {text}{link && link.label ? <b> · {link.label}</b> : null}
      </span>
      {link && <Icon.ChevronR size={13} />}
    </button>
  );

  return (
    <window.PhoneScroll style={{ background: 'var(--bg)' }}>
      {/* ── Header */}
      <div style={{ ...pdpCard, borderTop: 'none', padding: '14px 16px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Logo name={companyName || deal.name} color={(company || {}).logo} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 20, fontWeight: 650, lineHeight: 1.25, color: 'var(--text)', letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>{deal.name}</h1>
            {companyName && (canOpenCompanies && deal.target ? (
              <button type="button" onClick={() => nav.goToCompany && nav.goToCompany((company && company.id) || deal.target)}
                style={{ ...pdpTap, display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 0, minHeight: 44, padding: 0, border: 'none',
                         background: 'transparent', color: 'var(--accent-text)', fontSize: 15, cursor: 'pointer', maxWidth: '100%' }}>
                <Icon.Building size={14} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{companyName}</span>
                <Icon.ChevronR size={12} />
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4, fontSize: 15, color: 'var(--text-2)' }}>
                <Icon.Building size={14} /> {companyName}
              </div>
            ))}
            <div style={{ fontSize: 13.5, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.4 }}>
              {[deal.owner && deal.owner !== '—' ? deal.owner : null, deal.sector && deal.sector !== '—' ? deal.sector : null, `updated ${deal.updated}`].filter(Boolean).join(' · ')}
            </div>
          </div>
          {!guest && (
            <button type="button" aria-label="More actions" onClick={() => setActionsOpen(true)}
              style={{ ...pdpTap, width: 44, height: 44, marginTop: -6, marginRight: -8, flexShrink: 0, border: 'none', borderRadius: 10,
                       background: 'transparent', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <Icon.More size={22} />
            </button>
          )}
        </div>

        {(deal.onHold || exited || deal.archived || liveMeeting || ENG_TYPE_PILL[deal.type]) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {ENG_TYPE_PILL[deal.type] && deal.type !== 'seller_buyside' && <Pill hue={ENG_TYPE_PILL[deal.type].hue}>{ENG_TYPE_PILL[deal.type].label}</Pill>}
            {deal.onHold && <Pill hue="amber">On hold · {deal.onHold}{deal.onHoldReason ? ` · ${HOLD_REASON_LABEL[deal.onHoldReason] || deal.onHoldReason}` : ''}</Pill>}
            {exited && <Pill hue="rose">Exited · {deal.exitName}</Pill>}
            {deal.archived && <Pill hue="amber">{deal.archivedReason === 'reconciled_duplicate' ? 'Archived · duplicate' : 'Archived'}</Pill>}
            {liveMeeting && <Pill hue="green" dot>In a meeting now{liveMeeting.title ? ` · ${liveMeeting.title}` : ''}</Pill>}
          </div>
        )}
        {acquisitionPill}

        {/* Stage — one control for stage, hold and exit. */}
        <button type="button" disabled={guest} onClick={() => setStageOpen(true)}
          style={{ ...pdpTap, display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 50, marginTop: 14, padding: '8px 14px',
                   borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)',
                   textAlign: 'left', cursor: guest ? 'default' : 'pointer' }}>
          <span style={{ fontSize: 13, color: 'var(--text-3)', flexShrink: 0 }}>{isBuyerShell ? 'Pursuit' : 'Stage'}</span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
            {stagePill}
            {!isBuyerShell && probability != null && <span style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{probability}%</span>}
            {daysInStage != null && <span style={{ fontSize: 13, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{daysInStage}d</span>}
          </span>
          {!guest && <Icon.ChevronD size={16} color="var(--text-3)" />}
        </button>
      </div>

      {/* ── Banners: one line each (BannerLine's copy), the link is the row */}
      {isBuyerShell && deal.sellerEngagementId && phoneBanner('Buyer pursuit',
        { label: `Open ${deal.sellerEngagementName || 'the deal'}`, onClick: () => nav.goToDeal && nav.goToDeal(deal.sellerEngagementId) }, 'violet')}
      {deal.archived && phoneBanner(bannerText || 'Archived',
        deal._raw?.is_duplicate && deal._raw?.merged_into_id ? { label: 'Open the kept deal', onClick: () => nav.goToDeal && nav.goToDeal(deal._raw.merged_into_id) } : null, 'amber')}
      {outreachLinked && !isBuyerShell && phoneBanner('Buyer outreach', { label: null, onClick: goOutreach }, 'plain')}

      {/* ── Next action */}
      <window.PhoneSectionHeader title="Next action"
        action={!guest && (
          <button type="button" onClick={() => setNextOpen(true)}
            style={{ ...pdpTap, minWidth: 44, height: 44, margin: '-12px -4px -12px 0', padding: '0 4px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            {naNote || naDate ? 'Update' : 'Add'}
          </button>
        )} />
      <div style={{ ...pdpCard, padding: '12px 16px' }}>
        {naNote || naDate ? (
          <>
            {naDate && (
              <div style={{ fontSize: 14, fontWeight: 600, color: naOverdue ? 'var(--rose-fg)' : naToday ? 'var(--amber-fg)' : 'var(--text-2)', marginBottom: naNote ? 4 : 0 }}>
                {fmtDate(naDate)}{naOverdue ? ' · overdue' : naToday ? ' · today' : ''}
              </div>
            )}
            {naNote && (
              <div style={{ fontSize: 15, color: 'var(--text)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                            ...(naLong && !naExpanded ? { display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : null) }}>
                {naNote}
              </div>
            )}
            {naLong && (
              <button type="button" onClick={() => setNaExpanded((v) => !v)}
                style={{ ...pdpTap, marginTop: 0, minWidth: 44, height: 44, padding: 0, border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 14, cursor: 'pointer' }}>
                {naExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        ) : (
          <div style={{ fontSize: 15, color: 'var(--text-3)' }}>No next action logged.</div>
        )}
      </div>

      {/* ── Contact */}
      <window.PhoneSectionHeader title="Contact"
        action={!guest && deal.contactCount > 1 && (
          <button type="button" onClick={() => setContactsOpen(true)}
            style={{ ...pdpTap, minWidth: 44, height: 44, margin: '-12px -4px -12px 0', padding: '0 4px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            All {deal.contactCount}
          </button>
        )} />
      <div style={{ ...pdpCard, padding: '12px 16px' }}>
        {deal.contactName ? (
          <>
            <button type="button" disabled={!deal.contactId || !nav.goToContact || guest}
              onClick={() => nav.goToContact(deal.contactId)}
              style={{ ...pdpTap, display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 48, padding: 0, border: 'none', background: 'transparent',
                       color: 'var(--text)', textAlign: 'left', cursor: deal.contactId && !guest ? 'pointer' : 'default' }}>
              <Avatar name={deal.contactName} size={40} color={window.hashColor ? window.hashColor(deal.contactName) : undefined} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.contactName}</span>
                <span style={{ display: 'block', fontSize: 13.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[deal.contactTitle, (phoneNumbers(deal.contactPhone)[0] || {}).display || null].filter(Boolean).join(' · ') || 'Primary contact'}
                </span>
              </span>
              {deal.contactId && !guest && <Icon.ChevronR size={14} color="var(--text-4)" />}
            </button>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <PhoneContactAction href={(phoneNumbers(deal.contactPhone)[0] || {}).tel || null} icon={<Icon.Phone size={16} />} label="Call" />
              <PhoneContactAction href={deal.contactEmail ? `mailto:${deal.contactEmail}` : null} icon={<Icon.Mail size={16} />} label="Email" />
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1, fontSize: 15, color: 'var(--text-3)' }}>{deal.target ? 'No contact on file.' : 'No company linked.'}</span>
            {!guest && deal.target && window.NewPersonDrawer && (
              <Btn variant="outline" icon={<Icon.Plus size={12} />} onClick={() => setAddingContact(true)}>Add contact</Btn>
            )}
          </div>
        )}
      </div>

      {/* ── Key facts */}
      <window.PhoneSectionHeader title="Details" />
      <div style={pdpCard}>
        <PhoneFactRow label="Est. close" first>
          <EditableCell type="date" label="Est. close" value={deal.estCloseDate} locked={dcLocked} align="right"
            displayAs={estCloseNode} onSave={(estimated_close_date) => onSave({ estimated_close_date })} />
        </PhoneFactRow>
        {!isBuyerShell && (
          <PhoneFactRow label="LOI signed">
            <EditableCell type="date" label="LOI signed" value={deal._raw?.loi_signed_date || null} align="right"
              displayAs={!deal._raw?.loi_signed_date && deal._raw?.loi_date ? <span className="num">{fmtDate(deal._raw.loi_date)}</span> : undefined}
              onSave={(loi_signed_date) => onSave({ loi_signed_date })} />
          </PhoneFactRow>
        )}
        {!isBuyerShell && window.RetainerCell && (
          <PhoneFactRow label="Retainer">
            <window.RetainerCell deal={deal} canWrite={window.recordCanWrite()} onSave={onSave} align="right" />
          </PhoneFactRow>
        )}
        <PhoneFactRow label="Est. fees">
          <EditableCell type="currency" label="Est. fees" value={deal.size} locked={dcLocked} align="right"
            onSave={(estimated_fees) => onSave({ estimated_fees })} />
        </PhoneFactRow>
        <PhoneFactRow label="Hot score">
          <button type="button" disabled={guest} onClick={() => setHotOpen(true)}
            style={{ ...pdpTap, display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: 0, border: 'none', background: 'transparent',
                     color: deal.hotScore != null ? 'var(--text)' : 'var(--text-4)', fontSize: 15, cursor: guest ? 'default' : 'pointer' }}>
            {deal.hotScore != null ? `🔥 ${deal.hotScore} / 10` : 'Not scored'}
            {!guest && <Icon.Edit size={13} color="var(--text-4)" />}
          </button>
        </PhoneFactRow>
        {!isBuyerShell && (
          <PhoneFactRow label="Weighted">
            <span className="num" style={{ color: 'var(--text-2)' }}>{fmtMoney(deal.weighted)}</span>
          </PhoneFactRow>
        )}
        <PhoneFactRow label="Dealmaker">
          <EditableCell type="user" label="Dealmaker" value={deal._raw?.dealmaker_id || null} users={users} align="right"
            fallbackLabel={deal._raw?.dealmaker_name} locked={dcLocked}
            onSave={(dealmaker_id) => window.saveOwnerWithScope({
              kind: 'engagement', field: 'dealmaker_id', value: dealmaker_id, users,
              companyName, hasCompany: !!deal.target, save: onSave,
            })} />
        </PhoneFactRow>
        <PhoneFactRow label="Deal team">
          <EditableCell type="users" label="Deal team" value={deal._raw?.deal_team || []} users={users} align="right"
            fallbackLabel={deal._raw?.deal_team_names || []} onSave={(deal_team) => onSave({ deal_team })} />
        </PhoneFactRow>
        {(deal.reportedRevenue ?? deal.companyRevenueEst) != null && (
          <PhoneFactRow label="Revenue">
            <span className="num" style={{ color: 'var(--text-2)' }}>
              {fmtMoney(deal.reportedRevenue ?? deal.companyRevenueEst)}{deal.reportedRevenue == null ? <span style={{ fontSize: 12, color: 'var(--text-4)' }}> via company</span> : null}
            </span>
          </PhoneFactRow>
        )}
        {(deal.reportedEbitda ?? deal.companyEbitdaEst) != null && (
          <PhoneFactRow label="EBITDA">
            <span className="num" style={{ color: 'var(--text-2)' }}>
              {fmtMoney(deal.reportedEbitda ?? deal.companyEbitdaEst)}{deal.reportedEbitda == null ? <span style={{ fontSize: 12, color: 'var(--text-4)' }}> via company</span> : null}
            </span>
          </PhoneFactRow>
        )}
        <button type="button" onClick={() => openPanel('details')}
          style={{ ...pdpTap, display: 'flex', alignItems: 'center', width: '100%', minHeight: 48, padding: '6px 16px', border: 'none',
                   borderTop: '1px solid var(--border)', background: 'transparent', color: 'var(--accent-text)', fontSize: 15, cursor: 'pointer' }}>
          <span style={{ flex: 1, textAlign: 'left' }}>All details</span>
          <Icon.ChevronR size={14} />
        </button>
      </div>

      {/* ── Closing (Won deals) */}
      {window.ClosingSection && (
        <div style={{ ...sectionGap, padding: '0 16px' }}>
          <window.ClosingSection deal={deal} sectionId="phone.deal.closing" />
        </div>
      )}
      {window.CloseTimelineCard && (
        <div style={{ ...sectionGap, padding: '0 16px' }}>
          <window.CloseTimelineCard deal={deal} canWrite={window.recordCanWrite()} onSave={onSave} />
        </div>
      )}

      {/* ── Tasks (views/record_tasks.jsx) */}
      {window.RecordTasks && (
        <window.RecordTasks kind="deal" presentation="phone" deal={deal} tasks={tasks} onTasksChange={onTasksChange}
          users={users} activeUser={activeUser} canWrite={!guest} />
      )}

      {/* ── Notes + composer (Log call is one tap away) */}
      {!guest && (
        <div style={{ display: 'flex', gap: 10, padding: '20px 16px 0' }}>
          <Btn variant="outline" icon={<Icon.Phone size={14} />} onClick={() => compose('call')} disabled={!activeUser} style={{ flex: 1, justifyContent: 'center' }}>Log call</Btn>
          <Btn variant="outline" icon={<Icon.Doc size={14} />} onClick={() => compose('general')} disabled={!activeUser} style={{ flex: 1, justifyContent: 'center' }}>Note</Btn>
        </div>
      )}
      <div style={{ padding: '12px 16px 0' }}>
        <window.RecordNotes kind="deal" recordId={deal.id}
          feed={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: onLoadMoreNotes,
            reload: onNotesChange, search: notesSearch, setSearch: onNotesSearch, q: notesQ }}
          attach={{ engagement_id: deal.id, company_id: deal.target || null }}
          activeUser={activeUser} members={users} deal={deal} hideCompanyId={deal.target}
          onDealChange={onReloadDeal} onTasksChange={onTasksChange}
          composeRequest={composeRequest} />
      </div>

      {/* ── Reference, folded */}
      <div style={{ ...sectionGap, padding: '0 16px' }}>
        <window.SellerBriefSection deal={deal} sectionId="phone.deal.seller_brief" />
        {!window.AtlasIsGuest && deal.type !== 'buyer_buyside' && deal.target && window.CompanyVaultSection && (
          <div style={{ marginTop: 14 }}>
            <window.CompanyVaultSection companyId={deal.target} sectionId="phone.deal.vault" phone panelRecord={{ kind: 'deal', id: deal.id }}
              engagementId={!deal._raw?.is_duplicate ? deal.id : null} />
          </div>
        )}
      </div>
      {/* Files: the vault's All attachments (seller deal with a company), else the Files workspace. */}
      {filesAt && (
        <button type="button" onClick={() => openPanel(filesAt.name, filesAt.params)}
          style={{ ...pdpTap, ...pdpCard, display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 52, marginTop: 12, padding: '8px 16px',
                   borderLeft: 'none', borderRight: 'none', color: 'var(--text)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 15.5, fontWeight: 600 }}>Files</span>
          <Icon.ChevronR size={14} color="var(--text-4)" />
        </button>
      )}
      {stageHistory && stageHistory.length > 0 && (
        <window.LazyFold id="phone.deal.stage_history" variant="card" title="Stage history" meta={stageHistory.length}>
          {stageHistory.map((h, i) => (
            <div key={h.id} style={{ padding: '10px 16px', borderTop: i ? '1px solid var(--border)' : 'none', fontSize: 14, lineHeight: 1.45 }}>
              <div>
                <span style={{ color: 'var(--text-3)' }}>{h.from_stage_name || h.from_stage_slug || '(new)'}</span>
                <span style={{ margin: '0 6px', color: 'var(--text-4)' }}>→</span>
                <span style={{ fontWeight: 600 }}>{h.to_stage_name || h.to_stage_slug}</span>
                {h.exit_name && <span style={{ marginLeft: 6 }}><Pill hue="rose" sz="sm">{h.exit_name}</Pill></span>}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                {new Date(h.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                {h.changed_by_name ? ` · ${h.changed_by_name}` : ''}
              </div>
            </div>
          ))}
        </window.LazyFold>
      )}
      {/* Buyers: the desktop card, read-only (no workspace on a phone; the summary feeds it). */}
      {!isBuyerShell && window.DealBuyersCard && (
        <div ref={buyersRef} style={{ ...sectionGap, padding: '0 16px', scrollMarginTop: 12 }}>
          <window.DealBuyersCard deal={deal} variant="card" readOnly sectionId="phone.deal.buyers" />
        </div>
      )}
      {!isBuyerShell && window.LoiCompareCard && (
        <div style={{ ...sectionGap, padding: '0 16px' }}><window.LoiCompareCard deal={deal} sectionId="phone.deal.lois" /></div>
      )}
      <div style={{ height: 24 }} />

      {/* ── Sheets */}
      <window.PhoneSheet open={stageOpen} onClose={() => setStageOpen(false)} title={isBuyerShell ? 'Pursuit stage' : 'Stage'}>
        {exited ? (
          <div style={{ padding: '0 18px 14px', fontSize: 15, color: 'var(--text-2)', lineHeight: 1.5 }}>
            Exited · {deal.exitName}. {currentExit?.is_recoverable ? 'Reopen it to move its stage.' : 'This exit is final.'}
            {currentExit?.is_recoverable && (
              <div style={{ marginTop: 12 }}>
                <Btn variant="outline" onClick={() => { setStageOpen(false); onReopen(); }}>Reopen deal</Btn>
              </div>
            )}
          </div>
        ) : canMoveStage ? (
          <>
            <window.PhoneActionList items={stageItems} />
            {holdItems.filter(Boolean).length > 0 && (
              <>
                <window.PhoneSectionHeader title="On hold" style={{ padding: '10px 18px 2px' }} />
                <window.PhoneActionList items={holdItems} />
              </>
            )}
            {stageExits.length > 0 && (
              <window.PhoneActionList items={[{ key: 'exit', label: 'Exit deal…', sub: 'Mark it lost, with a reason', danger: true,
                icon: <Icon.Close size={18} />, onClick: () => { setStageOpen(false); onExit(); } }]} />
            )}
          </>
        ) : (
          <div style={{ padding: '0 18px 14px', fontSize: 15, color: 'var(--text-3)' }}>No stages to move to.</div>
        )}
      </window.PhoneSheet>

      <window.PhoneSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title={deal.name}>
        <window.PhoneActionList items={actionItems} />
      </window.PhoneSheet>

      <window.PhoneSheet open={hotOpen} onClose={() => setHotOpen(false)} title="Hot score">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, padding: '8px 16px 12px' }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
            const on = deal.hotScore === n;
            return (
              <button key={n} type="button"
                onClick={() => { setHotOpen(false); if (!on) onSave({ hot_score: n }).catch(() => {}); }}
                style={{ ...pdpTap, height: 48, borderRadius: 12, cursor: 'pointer', fontSize: 17, fontWeight: 600,
                         border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
                         background: on ? 'var(--accent-soft)' : 'var(--surface)', color: on ? 'var(--accent-text)' : 'var(--text)' }}>
                {n}
              </button>
            );
          })}
        </div>
        {deal.hotScore != null && (
          <window.PhoneActionList items={[{ key: 'clear', label: 'Clear score', danger: true,
            onClick: () => { setHotOpen(false); onSave({ hot_score: null }).catch(() => {}); } }]} />
        )}
      </window.PhoneSheet>

      <window.PhoneSheet open={contactsOpen} onClose={() => setContactsOpen(false)} title={companyName ? `${companyName} contacts` : 'Contacts'}>
        {contacts === null && <div style={{ padding: '6px 18px 16px', fontSize: 15, color: 'var(--text-3)' }}>Loading…</div>}
        {contacts && contacts.length === 0 && <div style={{ padding: '6px 18px 16px', fontSize: 15, color: 'var(--text-3)' }}>No contacts at this company.</div>}
        {(contacts || []).map((c, i) => {
          const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—';
          // One number → the call button sits on the contact's row; several →
          // one row per number under the name, each with its own label.
          const nums = phoneNumbers(c.phone);
          const dial = nums.length === 1 ? nums[0].tel : null;
          const iconBtn = { ...pdpTap, width: 44, height: 44, borderRadius: 12, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--accent-soft)', color: 'var(--accent-text)', textDecoration: 'none' };
          return (
            <div key={c.id} style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 18px' }}>
                <button type="button" disabled={guest || !nav.goToContact}
                  onClick={() => { setContactsOpen(false); nav.goToContact(c.id); }}
                  style={{ ...pdpTap, flex: 1, minWidth: 0, minHeight: 44, padding: '4px 0', border: 'none', background: 'transparent', color: 'var(--text)', textAlign: 'left', cursor: 'pointer' }}>
                  <span style={{ display: 'block', fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}{c.id === deal.contactId ? <span style={{ fontSize: 12, color: 'var(--text-4)' }}>  · primary</span> : null}
                  </span>
                  {c.title && <span style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>}
                </button>
                {dial && <a href={dial} aria-label={`Call ${name}`} style={iconBtn}><Icon.Phone size={17} /></a>}
                {c.email && <a href={`mailto:${c.email}`} aria-label={`Email ${name}`} style={iconBtn}><Icon.Mail size={17} /></a>}
              </div>
              {nums.length > 1 && nums.map((n) => (
                <a key={n.tel} href={n.tel} aria-label={`Call ${name} at ${n.display}`}
                  style={{ ...pdpTap, display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '2px 18px 6px 28px', color: 'var(--text)', textDecoration: 'none' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.display}</span>
                    {n.label && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-3)', textTransform: 'capitalize' }}>{n.label}</span>}
                  </span>
                  <span style={iconBtn}><Icon.Phone size={17} /></span>
                </a>
              ))}
            </div>
          );
        })}
      </window.PhoneSheet>

      <window.PhoneNextActionSheet open={nextOpen} note={naNote} date={naDate}
        onSave={saveNextAction} onClose={() => setNextOpen(false)} />

      <PhoneFieldSheet open={renaming} type="text" label="Engagement name" value={deal.name} saving={false}
        onCancel={() => setRenaming(false)}
        onSave={(name) => { setRenaming(false); if (name && name !== deal.name) onSave({ name }).catch(() => {}); }} />

      <HoldReasonPrompt
        open={askingHoldReason}
        reason={deal.onHoldReason} cadence={deal.holdCheckinCadence}
        onPick={async (patch) => {
          setAskingHoldReason(false);
          await onSave(patch).catch(() => {});
        }}
        onClose={() => setAskingHoldReason(false)}
      />

      {addingContact && window.NewPersonDrawer && (
        <window.NewPersonDrawer
          initialCompany={{ id: deal.target, name: companyName || '' }}
          onClose={() => setAddingContact(false)}
          onCreated={() => {
            setAddingContact(false);
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
            onReloadDeal();
          }}
        />
      )}
    </window.PhoneScroll>
  );
};

// ============ Exit deal drawer ============
// Marking a deal lost/exited. Reasons are the CURRENT stage's exit
// definitions (a deal dies where it stands); recoverable exits can be
// reopened from the header afterwards, hard exits can't.
const ExitDealDrawer = ({ open, deal, exits, onClose, onExit, zIndex }) => {
  // Phone: finger-sized reason rows (the Drawer itself is a full-screen sheet).
  const phone = !!window.useDevice().phone;
  const [exitId, setExitId] = React.useState(null);
  const [notes, setNotes]   = React.useState('');
  const [busy, setBusy]     = React.useState(false);
  React.useEffect(() => {
    if (open) { setExitId(null); setNotes(''); setBusy(false); }
  }, [open]);
  const chosen = exits.find((x) => x.id === exitId);
  const isBuyerShell = deal.type === 'buyer_buyside';
  return (
    <Drawer
      open={open}
      onClose={onClose}
      zIndex={zIndex}
      title="Exit deal"
      subtitle={isBuyerShell
        ? `Is "${deal.name}" passing on ${deal.sellerEngagementName || 'this deal'}?`
        : `Why is "${deal.name}" leaving the pipeline at ${deal.stageName || deal.stage}?`}
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn size="sm" variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn size="sm" variant="primary" disabled={!exitId || busy}
            style={{ background: 'oklch(0.50 0.14 25)', borderColor: 'oklch(0.50 0.14 25)' }}
            onClick={async () => {
              setBusy(true);
              try { await onExit(exitId, notes.trim()); } finally { setBusy(false); }
            }}>
            {busy ? 'Exiting…' : 'Exit deal'}
          </Btn>
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {exits.map((x) => (
          <label key={x.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: phone ? '12px 14px' : '9px 12px',
            border: `1px solid ${exitId === x.id ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 8, cursor: 'pointer',
            background: exitId === x.id ? 'var(--accent-soft)' : 'var(--surface)',
          }}>
            <input type="radio" name="exit-reason" checked={exitId === x.id}
              onChange={() => setExitId(x.id)} style={{ accentColor: 'var(--accent)' }} />
            <span style={{ flex: 1, fontSize: phone ? 15 : 12.5, color: 'var(--text)' }}>{x.name}</span>
            <Pill hue={x.is_recoverable ? 'amber' : 'rose'} sz="sm">
              {x.is_recoverable ? 'recoverable' : 'final'}
            </Pill>
          </label>
        ))}
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Note (optional — saved as a note on the deal and in stage history)</div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="e.g. Owner decided to hold for two more years of growth"
            style={{
              width: '100%', resize: 'vertical', padding: '8px 10px', fontSize: 12.5,
              background: 'var(--surface)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'inherit',
            }}
          />
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.5, marginTop: 4 }}>
          {chosen && !chosen.is_recoverable && (
            <span style={{ color: 'var(--rose-fg, oklch(0.50 0.14 25))' }}>
              {chosen.slug === 'company_sold'
                ? 'This exit is final unless the acquisition proves wrong — then use “Not acquired” on the deal, which asks why.'
                : "This exit is final — the deal can't be reopened from the UI."}{' '}
            </span>
          )}
          {chosen && chosen.slug === 'company_sold' && (
            <span>The company will also be marked as acquired.{' '}</span>
          )}
          {isBuyerShell && (
            <span>This buyer moves to Passed on the seller deal's interested-buyers list.</span>
          )}
        </div>
      </div>
    </Drawer>
  );
};

// ============ Stage rail ============
// Pass `onSelect(stage, idx)` to make the non-current chips clickable — the
// parent owns the confirm + PATCH (see DealDetail). With `history` (the
// deal's stage_history) and `raw` (its payload), hovering or focusing a chip
// shows its visit card (views/stage_moves.jsx): days there, who moved it,
// when, why. Chips with a card or a move are tab stops; Enter moves.
const StageRail = ({ stages, currentIdx, exited, onSelect, currentLabel, onCurrentLabel, history, raw }) => {
  const [tip, setTip] = React.useState(null);   // { slug, el, anchor } | null
  const tipId = React.useId();
  const L = window.StageMovesLogic;
  const visits = React.useMemo(() => {
    if (!L || !raw || !Array.isArray(history)) return {};
    const m = {};
    stages.forEach((s) => { m[s.slug] = L.stageVisit({ history, stages, slug: s.slug, raw }); });
    return m;
  }, [history, raw, stages]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!tip) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setTip(null); };
    // Tabbing along the rail scrolls it: the card follows its chip.
    const follow = () => setTip((t) => (t && t.el.isConnected ? { ...t, anchor: t.el.getBoundingClientRect() } : null));
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', follow, true);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('scroll', follow, true); };
  }, [Boolean(tip)]);   // eslint-disable-line react-hooks/exhaustive-deps
  const show = (slug) => (e) => {
    const el = e.currentTarget;
    if (visits[slug]) setTip({ slug, el, anchor: el.getBoundingClientRect() });
  };
  const hide = () => setTip(null);
  const tipStage = tip && stages.find((s) => s.slug === tip.slug);
  return (
  <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, height: 28, overflowX: 'auto', overflowY: 'hidden' }}>
    {stages.map((s, i) => {
      const passed = i < currentIdx;
      const current = i === currentIdx;
      const h = HUE[STAGE_DOT_HUE[s.slug] || 'slate'];
      const showCheck = passed && !exited;
      const clickable = Boolean(onSelect) && !current;
      const hasCard = Boolean(visits[s.slug]);
      const restBg = current ? h.bg : passed ? 'var(--surface-2)' : 'var(--surface)';
      const tipOpen = tip && tip.slug === s.slug;
      return (
        <div key={s.slug}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable || hasCard ? 0 : undefined}
          aria-label={clickable ? `${s.name} — move here` : hasCard ? s.name : undefined}
          aria-current={current ? 'step' : undefined}
          aria-describedby={tipOpen ? tipId : undefined}
          onClick={clickable ? () => { hide(); onSelect(s, i); } : undefined}
          onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hide(); onSelect(s, i); } } : undefined}
          title={clickable && !hasCard ? `Move to ${s.name}` : undefined}
          onMouseEnter={(e) => { if (clickable) e.currentTarget.style.background = 'var(--surface-hover)'; show(s.slug)(e); }}
          onMouseLeave={(e) => { if (clickable) e.currentTarget.style.background = restBg; hide(); }}
          onFocus={(e) => {
            if (e.currentTarget.matches(':focus-visible')) e.currentTarget.style.outline = '2px solid var(--accent)';
            show(s.slug)(e);
          }}
          onBlur={(e) => { e.currentTarget.style.outline = 'none'; hide(); }}
          style={{
          flexShrink: 0, whiteSpace: 'nowrap', padding: '0 16px', position: 'relative',
          background: restBg,
          color: current ? h.fg : passed ? 'var(--text-2)' : 'var(--text-4)',
          fontSize: 11, fontWeight: current ? 600 : 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          cursor: clickable ? 'pointer' : hasCard ? 'help' : 'default',
          outline: 'none', outlineOffset: -3,
          clipPath: i === stages.length - 1
            ? 'polygon(0 0, calc(100% - 0px) 0, 100% 50%, calc(100% - 0px) 100%, 0 100%, 8px 50%)'
            : i === 0
            ? 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 0 50%)'
            : 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)',
          marginLeft: i === 0 ? 0 : -1,
          border: '1px solid var(--border)',
          borderLeftColor: i === 0 ? 'var(--border)' : 'transparent',
          opacity: exited ? 0.55 : 1,
        }}>
          {showCheck && <Icon.Check size={10} />}
          {current && <Icon.Dot size={9} />}
          <span style={{ paddingLeft: i === 0 ? 0 : 6, whiteSpace: 'nowrap' }}>{s.name}</span>
          {current && currentLabel && (
            <span onClick={onCurrentLabel ? (e) => { e.stopPropagation(); onCurrentLabel(); } : undefined}
              title={onCurrentLabel ? 'Stage moves' : undefined}
              style={{ fontWeight: 500, opacity: 0.8, whiteSpace: 'nowrap', cursor: onCurrentLabel ? 'pointer' : 'default', textDecoration: onCurrentLabel ? 'underline' : 'none', textUnderlineOffset: 2 }}>{currentLabel}</span>
          )}
        </div>
      );
    })}
    {tipStage && window.StageVisitTip && (
      <window.StageVisitTip id={tipId} anchor={tip.anchor} stageName={tipStage.name} visit={visits[tip.slug]} />
    )}
  </div>
  );
};

// ============ KPI tile ============
// `minW` (iPad, narrow) lets the strip wrap to rows instead of overlapping.
const Kpi = ({ label, value, sub, minW }) => (
  <div style={{ flex: 1, padding: '0 14px 12px', borderRight: '1px solid var(--border)', minWidth: minW || 0 }}>
    <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
      <span style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)', letterSpacing: '-0.015em', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      {sub && (
        <span style={{ fontSize: 10.5, color: 'var(--text-4)', fontWeight: 500 }}>{sub}</span>
      )}
    </div>
  </div>
);

// ============ Editable cell ==================================================
// One reusable cell that flips between read and edit modes. Save on blur or
// Enter, cancel on Escape. The parent owns persistence — pass `onSave(value)`
// which must return a Promise; we show a faint spinner until it resolves.
//
// Variants:
//   type="text"     — single-line text
//   type="multiline" — long text; reads as a clamped pre-wrap preview, edits in
//                      a centered modal (pass `label` for its title). Enter makes
//                      a newline, ⌘/Ctrl+Enter saves. Locked cells skip the inline
//                      confirm — the modal carries the synced-from-DC warning.
//   type="currency" — number input, displays via fmtMoney when read
//   type="date"     — <input type="date">, displays via fmtDate when read
//   type="select"   — uses `options` [{value,label,hue?}]; displays the matching label
//   type="user"     — uses `users` [{id, full_name, email}]; renders avatar+name
//   type="email"    — text editor; reads as a mailto: link + copy-to-clipboard
//   type="url"      — text editor; reads as a link that opens in a new tab
//
// Pass `editable={false}` to render the cell as plain read-only.
// For select/user cells whose value is an id, pass `fallbackLabel` — the
// server-resolved name off the same record (industry_name, dealmaker_name, …).
// It's what the cell shows when the id matches no option, which happens for
// every deactivated industry and departed team member. See StaleRef.

// Small copy-to-clipboard affordance for values that get cut off by the cell's
// ellipsis (long emails) — flips to a check for a moment as feedback.
const CopyIconBtn = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const touch = !!(window.AtlasDevice && window.AtlasDevice.touch);
  const phone = !!(window.AtlasDevice && window.AtlasDevice.phone);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        try { navigator.clipboard?.writeText(text); } catch (_) { /* clipboard unavailable */ }
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      title={copied ? 'Copied' : 'Copy to clipboard'}
      // Touch has no hover title, so name the control explicitly there.
      aria-label={touch ? 'Copy to clipboard' : undefined}
      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
               display: 'inline-flex', alignItems: 'center', flexShrink: 0,
               color: copied ? 'var(--accent-text)' : 'var(--text-4)',
               // Phone: a 44px square, positioned so it sits above the cell's own tap box.
               // Touch (iPad): a finger-sized hit area; the negative margin keeps the row height.
               ...(phone ? { position: 'relative', width: 44, height: 44, justifyContent: 'center', margin: '-10px 0' }
                 : touch ? { padding: 8, marginTop: -8, marginBottom: -8, marginRight: -8, marginLeft: -4 } : null) }}
    >
      {copied ? <Icon.Check size={11} /> : <Icon.Copy size={11} />}
    </button>
  );
};

// Phone numbers. DealCloud phones arrive as free text — "(916) 468-8893
// (Work)", "555-1234 x12", two numbers in one field, or junk like ".
// (Work)". Parsing is window.AtlasPhone.parsePhones (mobile/people.jsx):
// one entry per dialable number, the "(Work)" label split off, extensions
// dialled after a pause. phoneNumbers is that list ([] when nothing is
// dialable — never a dead tel: link); phoneDial keeps its old string
// contract (callers write `tel:${dial}`) and dials the first number;
// phoneDisplay collapses the raw spacing for plain-text display.
const phoneDisplay = (raw) => String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
const phoneNumbers = (raw) => (raw && window.AtlasPhone ? window.AtlasPhone.parsePhones(raw) : []);
const phoneDial = (raw) => {
  const first = phoneNumbers(raw)[0];
  return first ? first.tel.replace(/^tel:/, '') : null;
};

// Phone editor for EditableCell (type text/phone/email/url/number/currency/
// date/multiline): a bottom sheet with the field's label, a 16px control
// (no iOS focus-zoom) and Cancel/Save in the sheet's own header, so Save is
// never under the keyboard. It only collects — `onSave(value)` is the cell's
// commit(), the same path the inline editors use. Currency parses the
// shorthand the desktop input takes ("4M", "100k"); a draft that doesn't
// parse stays open with a message instead of saving a wrong number.
const PHONE_SHEET_TYPES = new Set(['text', 'phone', 'email', 'url', 'number', 'currency', 'date', 'multiline']);
const PhoneFieldSheet = ({ open, type, label, value, saving, list, onSave, onCancel }) => {
  const [draft, setDraft] = React.useState('');
  const [err, setErr] = React.useState(null);
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    setErr(null);
    setDraft(value == null ? ''
      : type === 'currency' ? window.formatNumInput(value)
      : type === 'date' ? String(value).slice(0, 10)
      : String(value));
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps
  const save = () => {
    if (saving) return;
    const raw = typeof draft === 'string' ? draft : String(draft ?? '');
    if (type === 'currency') {
      const parsed = window.parseNumInput(raw);
      if (parsed === undefined) { setErr('Not a number — try 4M, 100k or 4000000'); return; }
      onSave(parsed);
      return;
    }
    if (type === 'number') {
      if (raw.trim() === '') { onSave(null); return; }
      const n = Number(raw);
      if (!Number.isFinite(n)) { setErr('Not a number'); return; }
      onSave(n);
      return;
    }
    onSave(type === 'multiline' ? raw : raw.trim());
  };
  const control = {
    width: '100%', fontSize: 16, padding: '11px 12px', borderRadius: 10,
    border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text)',
    fontFamily: 'inherit', outline: 'none',
  };
  const inputProps = {
    text:     { type: 'text' },
    phone:    { type: 'tel', inputMode: 'tel', autoComplete: 'tel' },
    email:    { type: 'email', inputMode: 'email', autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false },
    url:      { type: 'url', inputMode: 'url', autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false },
    number:   { type: 'text', inputMode: 'decimal' },
    // Text, not decimal: the iOS decimal pad has no letters, and "4M" is how
    // people type money here.
    currency: { type: 'text', inputMode: 'text', autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false },
    date:     { type: 'date' },
  }[type] || { type: 'text' };
  const headBtn = { height: 44, padding: '0 6px', border: 'none', background: 'transparent', fontSize: 16, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' };
  const preview = type === 'currency' && draft.trim() ? window.parseNumInput(draft) : undefined;
  if (!window.PhoneSheet) return null;
  return (
    <window.PhoneSheet open={open} onClose={() => { if (!saving) onCancel(); }} zIndex={140} maxHeight="92%">
      <div onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 6px' }}>
          <button type="button" onClick={onCancel} disabled={saving} style={{ ...headBtn, color: 'var(--text-2)' }}>Cancel</button>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 16, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label || 'Edit'}
          </div>
          <button type="button" onClick={save} disabled={saving} style={{ ...headBtn, color: 'var(--accent-text)', fontWeight: 600 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <div style={{ padding: '4px 16px 18px' }}>
          {type === 'multiline' ? (
            <textarea ref={inputRef} autoFocus value={draft} onChange={(e) => { setDraft(e.target.value); setErr(null); }}
              rows={8} placeholder="Add text…"
              style={{ ...control, minHeight: 180, maxHeight: 'calc(var(--vvh, 100vh) * 0.45)', resize: 'none', lineHeight: 1.5 }} />
          ) : (
            <input ref={inputRef} autoFocus={type !== 'date'} value={draft} list={list}
              {...inputProps}
              enterKeyHint="done"
              onChange={(e) => { setDraft(e.target.value); setErr(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
              placeholder={type === 'currency' ? 'e.g. 4M or 250k' : type === 'date' ? '' : 'Add…'}
              style={{ ...control, minHeight: 46, WebkitAppearance: 'none', appearance: 'none' }} />
          )}
          {type === 'currency' && preview != null && !err && (
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 6 }}>= {fmtMoney(preview)}</div>
          )}
          {err && <div style={{ fontSize: 13, color: 'var(--rose-fg)', marginTop: 6 }}>{err}</div>}
          {(type === 'date' || type === 'currency' || type === 'number') && value != null && value !== '' && (
            <button type="button" onClick={() => onSave(null)} disabled={saving}
              style={{ marginTop: 10, height: 40, padding: 0, border: 'none', background: 'transparent', color: 'var(--rose-fg)', fontSize: 15, cursor: 'pointer' }}>
              Clear {(label || 'value').toLowerCase()}
            </button>
          )}
        </div>
      </div>
    </window.PhoneSheet>
  );
};

// Phone editor for type="users" (the deal team): a checklist sheet that
// saves per tap, like UsersEditor does on desktop. Anyone active can be
// staffed; an id whose member row is gone still shows so it can be removed.
const PhoneUsersSheet = ({ open, label, value, users, onSave, onClose }) => {
  const [ids, setIds] = React.useState(value || []);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { if (open) setIds(value || []); }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (id) => {
    if (saving) return;
    const prev = ids;
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    setIds(next);
    setSaving(true);
    try { await onSave(next); }
    catch { setIds(prev); }
    finally { setSaving(false); }
  };
  if (!window.PhoneSheet) return null;
  const byId = Object.fromEntries((users || []).map((u) => [u.id, u]));
  const check = <Icon.Check size={18} color="var(--accent-text)" />;
  const rows = [
    ...ids.filter((id) => !byId[id]).map((id) => ({ key: id, label: 'Unknown member', trailing: check, onClick: () => toggle(id) })),
    ...(users || [])
      .filter((u) => u.active !== false || ids.includes(u.id))
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
      .map((u) => ({
        key: u.id, label: u.full_name, sub: (u.role || '').replace(/_/g, ' ') || null,
        trailing: ids.includes(u.id) ? check : null, onClick: () => toggle(u.id),
      })),
  ];
  return (
    <window.PhoneSheet open={open} onClose={onClose} zIndex={140} title={saving ? 'Saving…' : (label || 'Deal team')}>
      <window.PhoneActionList items={rows} />
    </window.PhoneSheet>
  );
};

// Centered modal editor for multiline cells — a side-rail row is too cramped
// for paragraph-length text, so the row keeps a clamped preview and this gives
// a full-size box. Portaled to <body> so a transformed ancestor can't trap the
// fixed overlay; z 300 matches ConfirmHost (Combobox menus portal above at 400).
const LongTextModal = ({ label, initial, saving, onSave, onClose }) => {
  const [draft, setDraft] = React.useState(initial ?? '');
  const boxRef = React.useRef(null);
  const dirty = (draft || '') !== (initial ?? '');
  useFocusTrap(true, boxRef);
  useEscapeKey(true, () => { if (!saving) onClose(); });
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'oklch(0.2 0.01 270 / 0.28)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      // Backdrop click only closes a pristine modal — a stray click must not
      // eat an unsaved draft. Esc (above) behaves the same via Cancel.
      onClick={() => { if (!dirty && !saving) onClose(); }}
    >
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-label={label || 'Edit text'}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', border: '1px solid var(--border-strong)',
          borderRadius: 10, boxShadow: 'var(--shadow-lg)',
          width: 640, maxWidth: '94vw', padding: '14px 16px 12px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', flex: 1, minWidth: 0 }}>
            {label || 'Edit text'}
          </h3>
        </div>
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => { const n = e.target.value.length; e.target.setSelectionRange(n, n); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !saving) onSave(draft); }}
          placeholder="Add text…"
          style={{
            width: '100%', minHeight: 260, maxHeight: '60vh', resize: 'vertical',
            padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5,
            border: '1px solid var(--accent)', borderRadius: 6,
            fontFamily: 'inherit', outline: 'none',
            background: 'var(--surface)', color: 'var(--text)',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10.5, color: 'var(--text-4)', flex: 1 }}>
            <Kbd>⌘</Kbd>+<Kbd>↵</Kbd> to save · <Kbd>Esc</Kbd> to cancel
          </span>
          <Btn size="sm" variant="outline" disabled={saving} onClick={onClose}>Cancel</Btn>
          <Btn size="sm" variant="accent" disabled={saving} onClick={() => onSave(draft)}>
            {saving ? 'Saving…' : 'Save'}
          </Btn>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Clamped pre-wrap preview for multiline read mode. Reports its own overflow
// so the cell can show an "expand" hint only when text is actually cut off.
const ClampedText = ({ text, lines = 6 }) => {
  const ref = React.useRef(null);
  const [clipped, setClipped] = React.useState(false);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (el) setClipped(el.scrollHeight > el.clientHeight + 1);
  }, [text, lines]);
  return (
    <span style={{ display: 'block', minWidth: 0 }}>
      <span
        ref={ref}
        style={{
          whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
          display: '-webkit-box', WebkitBoxOrient: 'vertical',
          WebkitLineClamp: lines, overflow: 'hidden',
        }}
      >{text}</span>
      {clipped && (
        <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-4)', marginTop: 2 }}>
          … click to see all
        </span>
      )}
    </span>
  );
};

// A stored id that matches none of the cell's options — a deactivated
// industry, a departed team member. The option lists the callers build are
// active-only (`/api/industries` defaults to only_active, the user pickers
// filter `active !== false`), so ~39k companies on collapsed industries and
// ~29k owned by former dealmakers used to render their raw UUID here.
// Every such record already carries the server-resolved name alongside the id
// (CompanyRead.industry_name / assigned_user_name, ContactRead
// .contact_owner_name, EngagementRead.dealmaker_name, …) — callers pass it as
// `fallbackLabel` and we show that, dimmed, instead of the id.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isOpaqueId = (v) => typeof v === 'string' && UUID_RE.test(v);

const StaleRef = ({ label, id }) => (
  label
    ? (
      <span title={`${label} — no longer on the active list`} style={{ color: 'var(--text-3)' }}>
        {label}
        <span style={{ color: 'var(--text-4)', marginLeft: 4 }}>(inactive)</span>
      </span>
    )
    : <span title={`Unresolved reference: ${id}`} style={{ color: 'var(--text-4)' }}>Unknown</span>
);

// `fill` stretches the read view across its container so the WHOLE cell opens
// the editor, not just the text. Table cells want this — a click in a wide
// column's empty space used to hit dead space, and in a narrow column whose
// value is a link there was no free pixel left to click at all. It also
// stretches vertically, so a grid cell wrapper styled with EDIT_CELL_WRAP
// (full row height) hands every pixel of the cell to the editor — the bands
// above and below the text used to belong to the row and opened the record
// when someone aimed at the pencil and missed. `align="right"` keeps money
// columns right-aligned inside a filled cell.
// Grid cell wrapper style: fills the row's height (rows center their cells
// by default, leaving the wrapper as tall as its text) and centers the
// EditableCell inside. Spread into the wrapper div of every editable cell.
const EDIT_CELL_WRAP = { alignSelf: 'stretch', display: 'flex', alignItems: 'center' };
// Est. close date, read view — the same treatment NextActionCell gives its
// due date: red + "overdue" once the date is in the past and the deal has
// NOT closed (no closed date, no exit, not a terminal stage, not archived);
// amber inside the 10-day window the est. close watch DMs the admins about
// (est_close_watch.py — keep EST_CLOSE_SOON_DAYS in step with its default).
// Won / exited / archived deals keep a plain date: a past est. close on a
// closed deal is history, not a miss. Compares LOCAL calendar dates —
// slicing toISOString() (UTC) would call a close set for today "overdue"
// for anyone working after 7pm Central.
const EST_CLOSE_SOON_DAYS = 10;
const localToday = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const estCloseStatus = (dateStr, { closed = false } = {}) => {
  if (!dateStr || closed) return null;
  const d = String(dateStr).slice(0, 10);
  const today = localToday();
  if (d < today) return { status: 'overdue', days: Math.round((Date.parse(today) - Date.parse(d)) / 86400000) };
  const days = Math.round((Date.parse(d) - Date.parse(today)) / 86400000);
  if (days === 0) return { status: 'today', days: 0 };
  if (days <= EST_CLOSE_SOON_DAYS) return { status: 'soon', days };
  return null;
};
// "Closed" for est. close purposes. `stage` is the deal's pipeline stage row
// (carries is_terminal); callers look it up from whatever stage list they hold.
const dealIsClosed = (deal, stage) => Boolean(
  deal?.closedDate || deal?.exitName || deal?.archived || (stage && stage.is_terminal)
);
// `stacked` = the grid's two-line shape (date, then a small status line —
// the same layout NextActionCell uses one column over); inline otherwise,
// wrapping so the status never truncates in the narrow properties panel.
const EstCloseValue = ({ value, closed = false, stacked = false }) => {
  if (!value) return null;
  const st = estCloseStatus(value, { closed });
  const color = !st ? undefined
    : st.status === 'overdue' ? 'var(--rose-fg)'
    : 'var(--amber-fg)';
  const label = !st ? ''
    : st.status === 'overdue' ? 'overdue'
    : st.status === 'today' ? 'today'
    : `in ${st.days}d`;
  const title = !st ? undefined
    : st.status === 'overdue' ? `Est. close was ${st.days} day${st.days === 1 ? '' : 's'} ago and the deal hasn't closed`
    : st.status === 'today' ? 'Est. close is today'
    : `Est. close in ${st.days} day${st.days === 1 ? '' : 's'}`;
  if (stacked) {
    return (
      <div title={title} style={{ lineHeight: 1.25, minWidth: 0 }}>
        <div style={{ color, fontWeight: st ? 500 : undefined, whiteSpace: 'nowrap' }}>{fmtDate(value)}</div>
        {st && <div style={{ fontSize: 10.5, fontWeight: 500, color, whiteSpace: 'nowrap' }}>{label}</div>}
      </div>
    );
  }
  return (
    <span title={title} style={{ color, fontWeight: st ? 500 : undefined, display: 'inline-flex', flexWrap: 'wrap', columnGap: 4 }}>
      <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(value)}</span>
      {st && <span style={{ whiteSpace: 'nowrap' }}>· {label}</span>}
    </span>
  );
};

// Read view for EditableCell type="users" (the deal team). Named avatar
// chips on the deal page's rail, where there's room; in the grid `compact`
// gives an avatar stack + an overflow count, because the column clips.
const UserChips = ({ ids, names, compact = false, placeholder = '\u2014' }) => {
  const people = (ids || []).map((id, i) => ({ id, name: (names || [])[i] || '?' }));
  if (!people.length) return <span style={{ color: 'var(--text-4)' }}>{placeholder}</span>;
  const title = people.map((p) => p.name).join(', ');
  if (compact) {
    const shown = people.slice(0, 3);
    return (
      <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        {shown.map((p) => <Avatar key={p.id} name={p.name} size={18} color="oklch(0.50 0.13 200)" />)}
        {people.length > shown.length && (
          <span style={{ fontSize: 10.5, color: 'var(--text-3)', marginLeft: 3 }}>+{people.length - shown.length}</span>
        )}
      </span>
    );
  }
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, rowGap: 4 }}>
      {people.map((p) => (
        <UserLink key={p.id} id={p.id} name={p.name} avatar size={16} color="oklch(0.50 0.13 200)" />
      ))}
    </span>
  );
};

// Editor for EditableCell type="users": removable chips plus one Combobox to
// add. Each add/remove saves the whole array immediately and the panel stays
// open, so staffing three people is three picks and not three edit sessions
// (the task drawer's co-assignee control, app.jsx, works the same way — one
// pick at a time — but it lives inside a form that saves on submit).
// The add-box is the only keepOpen + autoOpen Combobox in the app: it opens
// with the editor and survives each pick, so a whole deal team is one open.
// Whoever was just picked drops out of `addable` on the next render; the
// chips above stay the record of what is chosen.
const UsersEditor = ({ value, users, onSave, onClose }) => {
  const [ids, setIds]     = React.useState(value || []);
  const [saving, setSaving] = React.useState(false);
  const rootRef = React.useRef(null);
  // Click-away closes, same as the Combobox's own dismissal.
  React.useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      // The Combobox menu is portaled to <body>, so it is not inside rootRef.
      if (e.target.closest?.('[data-combobox-menu]')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const push = async (next) => {
    const prev = ids;
    setIds(next);
    setSaving(true);
    try { await onSave(next); }
    catch { setIds(prev); }
    finally { setSaving(false); }
  };

  const byId = Object.fromEntries((users || []).map((u) => [u.id, u]));
  // Anyone active can be staffed on a deal — the owner-eligible gate is about
  // who can OWN a record, which is a different question. An id whose member
  // row is gone still renders so it can be seen and removed.
  const addable = (users || [])
    .filter((u) => u.active !== false && !ids.includes(u.id))
    .map((u) => ({ value: u.id, label: u.full_name }));

  return (
    <span ref={rootRef} onClick={(e) => e.stopPropagation()}
      style={{ display: 'inline-flex', flexDirection: 'column', gap: 6, width: '100%', minWidth: 0 }}>
      {ids.length > 0 && (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 5 }}>
          {ids.map((id) => (
            <span key={id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 7px',
              borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border)',
              fontSize: 11.5, color: 'var(--text-2)',
            }}>
              {byId[id] ? byId[id].full_name : 'Unknown member'}
              <button onClick={() => push(ids.filter((x) => x !== id))} title="Remove from deal team"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                         color: 'var(--text-3)', fontSize: 11, padding: 0, lineHeight: 1 }}>&#10005;</button>
            </span>
          ))}
        </span>
      )}
      <Combobox dense autoOpen keepOpen menuMaxHeight={440} menuMinWidth={300}
        value="" placeholder={saving ? 'Saving\u2026' : 'Add to deal team\u2026'}
        options={addable}
        onChange={(e) => { if (e.target.value) push([...ids, e.target.value]); }}
        style={{ width: '100%', minWidth: 0 }} />
    </span>
  );
};

// Money editor for EditableCell's `currency` cells. Split out of the branch
// because it needs its own draft state: the raw keystrokes have to survive
// until blur so shorthand ("4M", "100k", "$1.5M", "4,000,000") can be parsed
// whole. It's type="text" — <input type="number"> drops the letter as you
// type it and rejects it outright on paste, which is how "4M" used to save
// as 4. inputMode="decimal" keeps the numeric keypad on mobile.
//
// On commit: valid → save the full number and show it comma-grouped, so the
// cell reads "4,000,000" before you look away. Invalid → revert and save
// nothing. No inline hint here; grid cells are too tight for one, and the
// normalised text is the feedback.
const CurrencyInput = ({ value, onCommit, onCancel, style }) => {
  const [raw, setRaw] = React.useState(value == null ? '' : window.formatNumInput(value));
  const commitRaw = () => {
    const parsed = window.parseNumInput(raw);
    if (parsed === undefined) { setRaw(value == null ? '' : window.formatNumInput(value)); onCancel(); return; }
    setRaw(parsed == null ? '' : window.formatNumInput(parsed));
    onCommit(parsed);
  };
  return (
    <input
      autoFocus type="text" inputMode="decimal" value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={commitRaw}
      onKeyDown={(e) => {
        if (e.key === 'Enter')  commitRaw();
        if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
      }}
      placeholder="0"
      style={style}
    />
  );
};

// `anyone` (type="user"): offer every active member, not just owner-eligible
// ones — the deal's Associate slot and task assignees. Default = owners only
// (Settings → Users → "Can own records"); the current value always stays
// listed so an existing owner who lost the flag still renders and can be
// kept or replaced.
// Phone: a link inside a cell (mailto / tel / website) is a 44px line, positioned so it sits
// above the cell's own tap box (PhoneHit); the negative margin keeps the row height.
const EC_LINK_PHONE = { position: 'relative', display: 'inline-block', lineHeight: '44px', margin: '-12px 0' };
const EditableCell = ({ type, value, options, users, onSave, placeholder = '—', editable: editableProp = true, displayAs, list, label, fill = false, align = 'left', fallbackLabel = null, anyone = false }) => {
  // A deal guest (backend app/deal_guest.py) is read-only everywhere.
  const editable = editableProp && !window.AtlasIsGuest;
  // Touch never waits on hover: the edit affordance is always drawn, and no
  // state is set from a (synthetic, never-left) mouseenter. A phone edits in
  // a bottom sheet (PhoneFieldSheet) instead of the inline input. Desktop
  // (touch false) renders exactly what it always has.
  const device = window.useDevice();
  const phone = !!device.phone;
  const touch = !!device.touch;
  const hoverOk = !touch || device.canHover !== false;
  const [editing, setEditing]       = React.useState(false);
  const [draft, setDraft]           = React.useState(value);
  const [saving, setSaving]         = React.useState(false);
  const [hovered, setHovered]       = React.useState(false);
  React.useEffect(() => { setDraft(value); }, [value]);

  // No "Synced from DealCloud — edit anyway?" gate (removed 2026-10-20: DealCloud
  // is being retired and Atlas edits win — ui_pins keeps them through the
  // reconcile). Callers may still pass `locked`; it's ignored.
  const startEdit = () => {
    if (!editable) return;
    // The read view unmounts here, so its mouseleave never fires — clear the
    // hover flag by hand or the pencil chip stays lit after the edit.
    setHovered(false);
    setEditing(true);
  };

  const commit = async (next) => {
    // Treat empty inputs as null so we can clear a field
    const normalized = next === '' || next === undefined ? null : next;
    if (normalized === value || (normalized == null && value == null)) {
      setEditing(false); return;
    }
    setSaving(true);
    try { await onSave(normalized); }
    catch { setDraft(value); }  // revert on failure
    finally { setSaving(false); setEditing(false); }
  };

  const readView = (() => {
    if (displayAs) return displayAs;
    if (value == null || value === '') {
      return <span style={{ color: 'var(--text-4)' }}>{placeholder}</span>;
    }
    if (type === 'currency') return <span className="num">{fmtMoney(value)}</span>;
    if (type === 'number')   return <span className="num">{String(value)}</span>;
    if (type === 'date')     return <span>{fmtDate(value)}</span>;
    if (type === 'multiline') return <ClampedText text={String(value)} />;
    if (type === 'select') {
      const opt = (options || []).find((o) => o.value === value);
      // Unmatched slug-valued selects still read fine raw ('on_hold'); an
      // unmatched *id* is the leak we're guarding against.
      if (!opt) {
        return (fallbackLabel || isOpaqueId(value))
          ? <StaleRef label={fallbackLabel} id={value} />
          : <span>{String(value)}</span>;
      }
      if (opt.hue) return <Pill hue={opt.hue} dot sz="sm">{opt.label}</Pill>;
      return <span>{opt.label}</span>;
    }
    if (type === 'user') {
      const u = (users || []).find((x) => x.id === value);
      if (!u) return <StaleRef label={fallbackLabel} id={value} />;
      return <UserLink id={u.id} name={u.full_name} avatar size={16} color="oklch(0.50 0.13 200)" />;
    }
    // Multi-user (the deal team). `fallbackLabel` carries the server's
    // resolved names so a cell renders correctly before /users has loaded.
    if (type === 'users') {
      return <UserChips ids={value} names={fallbackLabel} compact={fill} placeholder={placeholder} />;
    }
    if (type === 'email') {
      const email = String(value).trim();
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, maxWidth: '100%' }}>
          <a
            href={`mailto:${email}`}
            onClick={(e) => e.stopPropagation()}
            title={`Email ${email}`}
            style={{ color: 'var(--accent-text)', textDecoration: 'none',
                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, ...(phone ? EC_LINK_PHONE : null) }}
            onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >{email}</a>
          <CopyIconBtn text={email} />
        </span>
      );
    }
    // type="phone": a tel: link (tap to call on a phone) + copy. A value with
    // no dialable number reads as plain text — it's still editable, never a
    // dead link. A field holding several numbers links the first and lists
    // the rest, each its own tel: link.
    if (type === 'phone') {
      const nums = phoneNumbers(value);
      if (!nums.length) return <span>{phoneDisplay(value)}</span>;
      const telLink = (n, extra) => (
        <a
          key={n.tel}
          href={n.tel}
          onClick={(e) => e.stopPropagation()}
          title={`Call ${n.display}${n.label ? ` (${n.label})` : ''}`}
          style={{ color: 'var(--accent-text)', textDecoration: 'none',
                   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                   ...(phone ? EC_LINK_PHONE : null), ...extra, ...(phone && extra ? { margin: 0 } : null) }}
          onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
        >{n.display}</a>
      );
      const first = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, maxWidth: '100%' }}>
          {telLink(nums[0])}
          <CopyIconBtn text={nums[0].display} />
        </span>
      );
      if (nums.length === 1) return first;
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: align === 'right' ? 'flex-end' : 'flex-start', gap: 2, minWidth: 0, maxWidth: '100%' }}>
          {first}
          {nums.slice(1).map((n) => telLink(n, { display: 'block', maxWidth: '100%', fontSize: '0.92em' }))}
        </span>
      );
    }
    if (type === 'url') {
      const raw = String(value).trim();
      const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%' }}>
          <a
            href={href} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`Open ${href} in a new tab`}
            style={{ color: 'var(--accent-text)', textDecoration: 'none',
                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, ...(phone ? EC_LINK_PHONE : null) }}
            onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >{raw}</a>
          <Icon.External size={10} color="var(--text-4)" />
        </span>
      );
    }
    return <span>{String(value)}</span>;
  })();

  // The read view (also the tap target that opens the editor).
  const readCell = (
    <span
      onClick={startEdit} role={phone && editable ? 'button' : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        minWidth: 0, maxWidth: '100%',
        ...(fill ? { width: '100%', alignSelf: 'stretch', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' } : null),
        cursor: editable ? 'pointer' : 'default',
        padding: '2px 4px', marginLeft: -4, borderRadius: 4,
        transition: 'background .1s', ...(phone ? { position: 'relative', minWidth: 48 } : null),   // 48: its −4px margin sits under the row's clip
      }}
      onMouseEnter={(e) => { if (editable && hoverOk) { e.currentTarget.style.background = 'var(--surface-hover)'; setHovered(true); } }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; setHovered(false); }}
      title={editable ? 'Click to edit' : undefined}
    >
      {/* Phone: a ≥44×44 tap box centred on the value (rows keep their height). */}
      {phone && editable && window.PhoneHit && <window.PhoneHit />}
      {/* The read view shrinks; the affordances after it never do. A long
          value used to push the pencil out of a narrow cell — and when the
          value is a LINK (which swallows clicks on its own text), that left
          no clickable spot to open the editor at all. */}
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {readView}
      </span>
      {/* The per-field "synced from DealCloud" glyph that sat here is gone
          (2026-10-05, DealCloud is being retired): the research wand next
          to a field (views/research.jsx) took its place. */}
      {/* Discoverability: a pencil chip appears on hover so users can tell
          the field is editable at all — without it, EditableCells read as
          static text (we kept "missing" features that were here all along).
          The pencil means "click to edit". It's an 18px chip rather
          than a bare 10px glyph: people aim at it, and in a filled cell it
          sits at the far edge so it's in the same spot on every row. The
          whole cell opens the editor anyway — hitting the chip isn't
          required, it just has to look like it can be hit. */}
      {editable && hovered && !saving && (
        <span style={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 18, height: 18, borderRadius: 4,
          background: 'var(--surface)', border: '1px solid var(--border)',
          marginLeft: fill && align !== 'right' ? 'auto' : 0,
        }}><Icon.Edit size={11} color="var(--text-3)" /></span>
      )}
      {/* Touch: no hover, so the affordance is always there — a muted pencil
          with a finger-sized hit area (negative margin keeps row heights). */}
      {editable && touch && !hovered && !saving && (
        <span aria-hidden style={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: 8, marginTop: -8, marginBottom: -8, marginRight: -8, marginLeft: fill && align !== 'right' ? 'auto' : -4,
        }}><Icon.Edit size={phone ? 13 : 11} color="var(--text-4)" /></span>
      )}
      {saving && <span style={{ fontSize: 10, color: 'var(--text-4)', flexShrink: 0 }}>saving…</span>}
    </span>
  );

  // Phone: the scalar editors and the deal-team picker
  // are bottom sheets over the (unchanged) read view. select / user fall
  // through to their Combobox branches below, which already present as
  // sheets on a phone.
  const editType = type || 'text';
  if (phone && editable && (PHONE_SHEET_TYPES.has(editType) || editType === 'users')) {
    return (
      <>
        {readCell}
        {/* The sheets are portaled, but React still bubbles their taps to
            this cell's ancestors (a row that opens a record) — stop here. */}
        <span style={{ display: 'contents' }} onClick={(e) => e.stopPropagation()}>
          {PHONE_SHEET_TYPES.has(editType) && (
            <PhoneFieldSheet open={editing} type={editType} label={label} value={value}
              saving={saving} list={list} onSave={commit}
              onCancel={() => { setDraft(value); setEditing(false); }} />
          )}
          {editType === 'users' && (
            <PhoneUsersSheet open={editing} label={label} value={value || []} users={users}
              onSave={onSave} onClose={() => setEditing(false)} />
          )}
        </span>
      </>
    );
  }

  if (!editable || !editing) return readCell;

  // Edit mode. Escape cancels just the edit, not the drawer or workspace
  // hosting the cell (useEscapeKey listens on document; Combobox does the same).
  const onKey = (e) => {
    if (e.key === 'Enter')  commit(draft);
    if (e.key === 'Escape') { e.stopPropagation(); setDraft(value); setEditing(false); }
  };
  const inputStyle = {
    width: '100%', padding: '3px 6px', fontSize: 12,
    border: '1px solid var(--accent)', borderRadius: 4,
    fontFamily: 'inherit', outline: 'none',
  };

  // The current value has to be IN the dropdown, even when the option list no
  // longer carries it — otherwise the editor opens showing whatever sits first
  // (usually "— Unassigned —"), which reads as "this record has no owner" and
  // invites a keystroke that overwrites a real assignment.
  const staleOptionLabel = `${fallbackLabel || 'Current value'} (inactive)`;
  // Pickers are the shared Combobox (Atlas-styled, searchable past 6 options,
  // keyboard-driven) rather than a native <select> — the browser's own
  // dropdown was the one control in the grid that ignored the app's design.
  // autoOpen skips the redundant second click; onClose (pick, Escape, click
  // away) drops the cell back to its read view.
  const pickerStyle = { flex: 1, minWidth: 0, width: '100%' };
  if (type === 'select') {
    const opts = options || [];
    const editOpts = (draft == null || draft === '' || opts.some((o) => o.value === draft))
      ? opts
      : [...opts, { value: draft, label: staleOptionLabel }];
    // Options carrying a `group` render under a section header AFTER the
    // plain ones, in first-appearance order. The engagements grid's stage
    // picker uses this for its "On hold" sublist — a hold pick rides the
    // same dropdown as the stage but edits a different field, so it must
    // read as a sub-section, not as one more stage.
    const groups = [];
    editOpts.forEach((o) => { if (o.group && !groups.includes(o.group)) groups.push(o.group); });
    // A null option value (the "— Unassigned —" row) travels as '' — commit
    // turns it back into null.
    const asOpt = (o) => ({ value: o.value == null ? '' : o.value, label: o.label, hue: o.hue, description: o.description });
    const pickerOpts = [
      ...editOpts.filter((o) => !o.group).map(asOpt),
      ...groups.flatMap((g) => [{ header: g }, ...editOpts.filter((o) => o.group === g).map(asOpt)]),
    ];
    return (
      <Combobox autoOpen dense value={draft == null ? '' : draft} options={pickerOpts}
        placeholder={phone ? (label || 'Choose') : undefined}
        onChange={(e) => commit(e.target.value)} onClose={() => setEditing(false)} style={pickerStyle} />
    );
  }
  if (type === 'user') {
    const us = (users || []).filter((u) => u.id === value || (anyone ? u.active !== false : window.isOwnerEligible(u)));
    const editUsers = (!draft || us.some((u) => u.id === draft))
      ? us
      : [...us, { id: draft, full_name: staleOptionLabel }];
    const pickerOpts = [
      { value: '', label: '— Unassigned —' },
      ...editUsers.map((u) => ({ value: u.id, label: u.full_name })),
    ];
    return (
      <Combobox autoOpen dense value={draft || ''} options={pickerOpts}
        placeholder={phone ? (label || 'Choose someone') : undefined}
        onChange={(e) => commit(e.target.value || null)} onClose={() => setEditing(false)} style={pickerStyle} />
    );
  }
  if (type === 'users') {
    // Saves per pick rather than through commit(), which is built around one
    // scalar value and closes the editor once it has it.
    return (
      <UsersEditor value={value || []} users={users} onSave={onSave}
        onClose={() => setEditing(false)} />
    );
  }
  if (type === 'date') {
    return (
      <input
        autoFocus type="date" value={draft || ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        onKeyDown={onKey}
        style={inputStyle}
      />
    );
  }
  if (type === 'multiline') {
    // The row keeps its (dimmed) preview underneath; the modal owns the draft.
    return (
      <>
        <span style={{ display: 'block', minWidth: 0, opacity: 0.5 }}>{readView}</span>
        <LongTextModal
          label={label}
          initial={value ?? ''}
          saving={saving}
          onSave={commit}
          onClose={() => { setDraft(value); setEditing(false); }}
        />
      </>
    );
  }
  if (type === 'currency') {
    return (
      <CurrencyInput
        value={value}
        onCommit={commit}
        onCancel={() => { setDraft(value); setEditing(false); }}
        style={inputStyle}
      />
    );
  }
  if (type === 'number') {
    // Bare number (no money formatting) — the shared record-list renderer
    // uses it for hot_score; the server's range check (1–10) is what a bad
    // value hits, and the cell reverts on the 422.
    return (
      <input
        autoFocus type="number" inputMode="numeric" step="any" value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value === '' ? null : Number(e.target.value))}
        onBlur={() => commit(draft)}
        onKeyDown={onKey}
        style={inputStyle}
      />
    );
  }
  return (
    <input
      autoFocus value={draft ?? ''} list={list}
      type={type === 'phone' ? 'tel' : undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit(draft)}
      onKeyDown={onKey}
      style={inputStyle}
    />
  );
};

// ============ Editable title ============
// Inline rename for the engagement (108): the header h1 grows the same faint
// hover pencil as EditableCell; clicking it swaps in an input styled like the
// h1. Enter/blur saves via PATCH {name}, Escape cancels. The rename is
// Atlas-owned (name_source='ui') — the reconcile keeps it and the write-back
// pushes EngagementName while DealCloud is still around.
const EditableTitle = ({ value, onSave }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const [hovered, setHovered] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  const commit = async () => {
    const next = (draft || '').trim();
    setEditing(false);
    if (!next || next === value) { setDraft(value); return; }
    setSaving(true);
    try { await onSave(next); } catch (_) { setDraft(value); }
    setSaving(false);
  };
  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        style={{
          fontSize: 22, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.015em',
          fontFamily: 'inherit', background: 'var(--surface)', border: '1px solid var(--accent)',
          borderRadius: 6, padding: '0 6px', margin: '-1px 0 -1px -7px',
          width: `${Math.min(60, Math.max(14, (draft || '').length + 2))}ch`, outline: 'none',
        }}
      />
    );
  }
  // Touch (iPad): no hover to reveal the pencil — keep it visible.
  const touch = !!(window.AtlasDevice && window.AtlasDevice.touch);
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}
      onMouseEnter={window.hoverOnly(() => setHovered(true))} onMouseLeave={() => setHovered(false)}
    >
      <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.015em' }}>{value}</h1>
      {saving
        ? <span style={{ fontSize: 10, color: 'var(--text-4)' }}>saving…</span>
        : (
          <button
            onClick={() => setEditing(true)}
            title="Rename engagement"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer', padding: 2,
              display: 'inline-flex', opacity: hovered || touch ? 1 : 0, transition: 'opacity 120ms',
              ...(touch ? { padding: 10, margin: -8 } : null),
            }}
          ><Icon.Edit size={12} color="var(--text-4)" /></button>
        )}
    </span>
  );
};

// ============ Hold-reason prompt ============
// Asked when a deal is parked: IndustryPro categorizes every hold as an
// owner-side or business-side delay (engagements.on_hold_reason — Atlas-only;
// DealCloud's on-hold stages 07/08 carry priority, not a delay type), and
// picks how often the owner gets a check-in call while it waits
// (engagements.hold_checkin_cadence → one recurring task, app/checkin_cadence.py).
// Both stay editable afterwards via the "Delay type" and "Check-in" rows.
// onPick gets the PATCH: { on_hold_reason?, hold_checkin_cadence }.
const HOLD_CADENCE_OPTIONS = [
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'none',      label: 'None' },
];
const HOLD_CADENCE_LABEL = { monthly: 'Monthly', quarterly: 'Quarterly', none: 'None' };
const HOLD_CADENCE_TIP = 'A recurring call task for the deal owner while the deal is on hold. Completing it schedules the next one; taking the deal off hold ends it.';
const _noEsc = () => {};
// WAI-ARIA radio group: arrow keys move the choice and the focus (wrapping);
// only the checked radio (or the first, with none checked) is a Tab stop.
const radioArrows = (values, current, set) => (e) => {
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
  if (!step) return;
  e.preventDefault();
  const i = values.indexOf(current);
  const next = i < 0 ? (step > 0 ? 0 : values.length - 1) : (i + step + values.length) % values.length;
  set(values[next]);
  const btns = e.currentTarget.querySelectorAll('[role="radio"]');
  if (btns[next]) btns[next].focus();
};
const radioTab = (on, current, i) => (on || (current == null && i === 0) ? 0 : -1);
const HOLD_DELAY_OPTIONS = [
  ['owner_delay', 'Owner Delay', 'The owner is the holdup — timing, health, second thoughts, availability.'],
  ['business_delay', 'Business Delay', 'The business is — financials in flux, key hire pending, seasonal cycle.'],
];

const HoldReasonPrompt = ({ open, onPick, onClose, reason = null, cadence = null }) => {
  const phone = !!(window.AtlasDevice && window.AtlasDevice.phone);
  const [pick, setPick] = React.useState(reason);
  const [every, setEvery] = React.useState(cadence || 'monthly');
  const boxRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    setPick(reason || null); setEvery(cadence || 'monthly');
    // Focus moves into the dialog (the delay type's Tab stop) and back to
    // whatever opened it on close.
    const back = document.activeElement;
    const raf = requestAnimationFrame(() => {
      const first = boxRef.current && boxRef.current.querySelector('[role="radio"][tabindex="0"]');
      if (first) first.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      if (back && back.isConnected && typeof back.focus === 'function') back.focus();
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  (window.useEscapeKey || _noEsc)(!!open, onClose);
  if (!open) return null;
  const save = () => onPick({ ...(pick ? { on_hold_reason: pick } : {}), hold_checkin_cadence: every });
  const label = { fontSize: phone ? 13.5 : 11.5, fontWeight: 600, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 };
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'oklch(0.2 0.01 270 / 0.28)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        ref={boxRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-labelledby="hold-prompt-title"
        style={{
          background: 'var(--surface)', border: '1px solid var(--border-strong)',
          borderRadius: 10, boxShadow: 'var(--shadow-lg)',
          width: 400, maxWidth: '92vw', padding: '16px 18px',
        }}
      >
        <h3 id="hold-prompt-title" style={{ fontSize: phone ? 17 : 14, fontWeight: 600, color: 'var(--text)' }}>What's delaying this deal?</h3>
        <div role="radiogroup" aria-label="Delay type"
          onKeyDown={radioArrows(HOLD_DELAY_OPTIONS.map((o) => o[0]), pick, setPick)}
          style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
          {HOLD_DELAY_OPTIONS.map(([v, text, sub], i) => {
            const on = pick === v;
            return (
              <button
                key={v}
                role="radio" aria-checked={on} tabIndex={radioTab(on, pick, i)}
                onClick={() => setPick(on ? null : v)}
                onMouseEnter={window.hoverOnly((e) => { if (!on) e.currentTarget.style.borderColor = 'var(--border-strong)'; })}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = on ? 'var(--accent)' : 'var(--border)'; }}
                style={{
                  textAlign: 'left', padding: phone ? '12px 14px' : '9px 12px', borderRadius: 8, cursor: 'pointer',
                  background: on ? 'var(--accent-soft)' : 'var(--surface)',
                  border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                }}
              >
                <div style={{ fontSize: phone ? 15 : 12.5, fontWeight: 600, color: 'var(--text)' }}>{text}</div>
                <div style={{ fontSize: phone ? 13.5 : 11.5, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={label} id="hold-prompt-cadence">
            Check-in call
            {window.HelpTip && <window.HelpTip text={HOLD_CADENCE_TIP} size={12} />}
          </div>
          <div role="radiogroup" aria-labelledby="hold-prompt-cadence"
            onKeyDown={radioArrows(HOLD_CADENCE_OPTIONS.map((o) => o.value), every, setEvery)}
            style={{ display: 'flex', marginTop: 6, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            {HOLD_CADENCE_OPTIONS.map((o, i) => {
              const on = every === o.value;
              return (
                <button key={o.value} role="radio" aria-checked={on} tabIndex={radioTab(on, every, i)}
                  onClick={() => setEvery(o.value)}
                  style={{
                    flex: 1, border: 'none', borderLeft: i ? '1px solid var(--border)' : 'none', cursor: 'pointer',
                    outlineOffset: -2,
                    padding: phone ? '10px 0' : '6px 0', fontSize: phone ? 14 : 12, fontWeight: on ? 600 : 500,
                    background: on ? 'var(--accent-soft)' : 'transparent', color: on ? 'var(--accent-text)' : 'var(--text-2)',
                  }}>{o.label}</button>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <Btn size="sm" variant="outline" onClick={onClose}>Skip</Btn>
          <Btn size="sm" variant="primary" onClick={save}>Save</Btn>
        </div>
      </div>
    </div>
  );
};

// ============ Left rail: properties ============
// "Merge another deal into this one" (POST /engagements/{id}/merge,
// app/deal_merge.py): the other deal's notes, tasks, history and buyers move
// here and it's archived as merged. Same company only. When both are in
// DealCloud the merge is done there too, which deletes the duplicate there —
// hence the confirm.
const DealMergeRow = ({ deal, initialOpen = false }) => {
  const [open, setOpen] = React.useState(initialOpen);
  const [others, setOthers] = React.useState(null);
  const [pick, setPick] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const companyId = deal._raw?.company_id;
  const buyer = deal._raw?.engagement_type === 'buyer_buyside';

  React.useEffect(() => {
    if (!open || !companyId) return;
    window.AtlasApi.listEngagements({ company_id: companyId, limit: 50 })
      .then((r) => setOthers(((r && r.items) || []).filter((e) => e.id !== deal.id && !e.is_duplicate
        && ((e.engagement_type === 'buyer_buyside') === buyer))))
      .catch(() => setOthers([]));
  }, [open, companyId, deal.id, buyer]);

  const picked = (others || []).find((e) => e.id === pick);
  const doMerge = async () => {
    if (!picked || busy) return;
    const bothDc = Boolean(deal._raw?.dealcloud_id && picked.dealcloud_id);
    const ok = await window.showConfirm({
      title: `Merge “${picked.name}” into this deal?`,
      body: `Its notes, tasks, stage history and buyers move to this deal, empty fields here are filled from it, and it is archived as merged.`
        + (bothDc ? ' Both are in DealCloud, so they are merged there too — DealCloud deletes the duplicate, and that can’t be undone.' : ''),
      confirmLabel: 'Merge',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const out = await window.AtlasApi.mergeEngagements(deal.id, picked.id);
      pushToast({ type: 'success', title: 'Deals merged',
                  body: out && out.dealcloud_merge_queued ? 'Queued for DealCloud too.' : undefined });
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      pushToast({ type: 'error', title: 'Merge failed', body: e.message });
      setBusy(false);
    }
  };

  if (!companyId || deal._raw?.is_duplicate) return null;
  return (
    <div style={{ padding: '10px 14px 12px', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Duplicates</div>
      {!open ? (
        <button onClick={() => setOpen(true)} title="A second deal for the same company? Fold it into this one."
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
          {window.PhoneHit && <window.PhoneHit />}
          Merge another deal into this one
        </button>
      ) : others === null ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
      ) : !others.length ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No other {buyer ? 'pursuit' : 'deal'} at this company.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Combobox value={pick} onChange={(e) => setPick(e.target.value)} placeholder="Pick the duplicate…"
            options={others.map((e) => ({ value: e.id, label: `${e.name}${e.dealcloud_id ? ` · ${e.dealcloud_id}` : ''}${e.is_archived ? ' · archived' : ''}` }))} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn size="xs" variant="accent" onClick={doMerge} disabled={busy || !picked}>{busy ? 'Merging…' : 'Merge'}</Btn>
            <button onClick={() => { setOpen(false); setPick(''); }}
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-3)', fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// All details on a phone (?panel=details, a full-screen sheet): finger-sized rows.
const rowPhone = () => !!(window.AtlasDevice && window.AtlasDevice.phone);
// One row of the properties panel below. Module scope so its identity is
// stable across the panel's renders; `rs` is the panel's research controller
// (window.useFieldResearch), read only when the row has `research`.
const Row = ({ label, value, research, rs }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: rowPhone() ? '0 0 0 16px' : '6px 14px', minHeight: rowPhone() ? 48 : 30 }}>
    <span style={{ width: 110, fontSize: rowPhone() ? 14 : 11.5, color: 'var(--text-3)', flexShrink: 0 }}>{label}</span>
    <span style={{ flex: 1, fontSize: rowPhone() ? 15 : 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis',
      ...(research ? { display: 'inline-flex', alignItems: 'center', gap: 5 } : {}),
      ...(rowPhone() ? { alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: 5, minHeight: 48, paddingRight: 16 } : {}) }}>
      {value == null || value === '' || value === '—'
        ? <span style={{ color: 'var(--text-4)' }}>—</span>
        : value}
      {research && <window.ResearchField ctl={rs} label={label} {...research} />}
    </span>
  </div>
);

// `onStageSelect(stage)` routes the Stage pick through the deal page's stage
// handler — Won → Closing drawer — instead of a bare stage_id PATCH (the
// backend 422s a move to Won without a closing). The desktop page and the
// phone page's "All details" sheet both pass it.
const PropertiesPanel = ({ deal, company, pipelineStages, onSave, onPursuitStage, onStageSelect, exitNotes = false }) => {
  // Hide "M&A" — it's IndustryPro's only service line right now and adds noise.
  // Surfaces automatically when a non-M&A engagement (e.g. Compass Equity) lands.
  const showServiceLine = deal.sponsor && deal.sponsor !== '—' && deal.sponsor !== 'M&A';
  // DealCloud-mapped fields show a "lock" hint + confirm-before-edit popover.
  // All Atlas engagement fields except is_archived come from DealCloud.
  const dcLocked = Boolean(deal._raw?.dealcloud_id);
  // Deal guest: every row reads, nothing edits (EditableCell checks too).
  const guest = !!window.AtlasIsGuest;
  const canOpenCompanies = window.AtlasCanSee ? window.AtlasCanSee('companies') : true;

  // Live team-member list for the user pickers (dealmaker / associate).
  const [users, setUsers] = React.useState([]);
  // Quick "add contact at the target company" — reuses the People drawer.
  const [addingContact, setAddingContact] = React.useState(false);
  // Parking a deal asks WHAT is delaying it (owner vs business) — the
  // IndustryPro hold taxonomy. Opens right after the on-hold save lands.
  const [askingHoldReason, setAskingHoldReason] = React.useState(false);
  React.useEffect(() => {
    window.AtlasApi.listTeamMembers()
      .then((m) => setUsers(Array.isArray(m) ? m.filter((u) => u.active !== false) : []))
      .catch(() => setUsers([]));
  }, []);

  const stageOptions = (pipelineStages || []).map((s) => ({
    value: s.id, label: s.name,
    hue: STAGE_DOT_HUE[s.slug] || 'slate',
  }));
  const onHoldOptions = [
    { value: null,        label: 'Active' },
    { value: 'high',      label: 'On hold · high', hue: 'amber' },
    { value: 'low',       label: 'On hold · low',  hue: 'amber' },
    { value: 'repricing', label: 'On hold · repricing', hue: 'amber' },
  ];
  const typeOptions = [
    { value: 'seller_buyside', label: 'Seller (buyside)' },
    { value: 'buyer_buyside',  label: 'Buyer (buyside)' },
    { value: 'sell_side',      label: 'Sell-side' },
    { value: 'other',          label: 'Other' },
  ];
  // Research wand (views/research.jsx): `research` = { field, kind, value,
  // onApply } puts the wand (and the ✨ badge once used) after the value.
  const rs = window.useFieldResearch('engagement', deal._raw?.id);
  // The deal's own reported numbers (what the seller told us) — editable
  // here with a wand each; the header KPIs fall back to the company's.
  const moneyRow = (label, field) => (
    <Row label={label}
      research={{ field, kind: 'money', value: deal._raw?.[field], onApply: (v) => onSave({ [field]: v }) }} rs={rs}
      value={<EditableCell type="currency" value={deal._raw?.[field] ?? null} locked={dcLocked}
        onSave={(v) => onSave({ [field]: v })} />} />
  );

  return (
    <div>
      <div style={{ padding: '12px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Engagement</div>
      {/* Stage is seller-funnel semantics — meaningless (and misleading to
          edit) on a buyer shell. Shells get their DealCloud buyer-funnel
          stage instead, editable through the pursuit-stage endpoint: it
          writes the stage / hold / exit that pushes back as that DC stage
          and keeps the seller deal's roster row in step (Lost = passed). */}
      {deal.type !== 'buyer_buyside' && <Row label="Stage" value={
        <EditableCell
          type="select"
          value={deal._raw?.stage_id}
          options={stageOptions}
          // The stage handler's own confirm carries the DealCloud caveat.
          locked={dcLocked && !onStageSelect}
          onSave={(stage_id) => {
            const st = onStageSelect && (pipelineStages || []).find((x) => x.id === stage_id);
            return st ? onStageSelect(st) : onSave({ stage_id });
          }}
        />
      } />}
      {deal.type === 'buyer_buyside' && (
        <Row label="Pursuit stage" value={
          <EditableCell
            type="select"
            value={pursuitKeyFor(deal.dcStage)}
            options={PURSUIT_STAGE_OPTIONS}
            editable={!!onPursuitStage}
            onSave={onPursuitStage}
            displayAs={deal.dcStage ? <Pill hue={dcStageHue(deal.dcStage)} sz="sm">{cleanDcStage(deal.dcStage)}</Pill> : undefined}
          />
        } />
      )}
      <Row label="On hold" value={
        <EditableCell
          type="select"
          value={deal.onHold || null}
          options={onHoldOptions}
          locked={dcLocked}
          onSave={async (on_hold) => {
            await onSave({ on_hold });
            // Freshly parked (or never categorized) → ask the delay and the check-in cadence.
            if (on_hold && (!deal.onHold || !deal.onHoldReason)) setAskingHoldReason(true);
          }}
        />
      } />
      {deal.onHold && (
        <Row label="Delay type" value={
          <EditableCell
            type="select"
            value={deal.onHoldReason || null}
            options={[
              { value: 'owner_delay',    label: 'Owner Delay',    hue: 'amber' },
              { value: 'business_delay', label: 'Business Delay', hue: 'amber' },
            ]}
            onSave={(on_hold_reason) => onSave({ on_hold_reason })}
          />
        } />
      )}
      {deal.onHold && (
        <Row label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Check-in
          {window.HelpTip && <window.HelpTip text={HOLD_CADENCE_TIP} size={11} />}</span>} value={
          <EditableCell
            type="select"
            value={deal.holdCheckinCadence || null}
            options={HOLD_CADENCE_OPTIONS.map((o) => ({ ...o, hue: o.value === 'none' ? 'slate' : 'blue' }))}
            onSave={(hold_checkin_cadence) => onSave({ hold_checkin_cadence: hold_checkin_cadence || 'none' })}
          />
        } />
      )}
      <HoldReasonPrompt
        open={askingHoldReason}
        reason={deal.onHoldReason} cadence={deal.holdCheckinCadence}
        onPick={async (patch) => {
          setAskingHoldReason(false);
          await onSave(patch);
        }}
        onClose={() => setAskingHoldReason(false)}
      />
      {deal.exitName && <Row label="Exit" value={<Pill hue="rose" sz="sm">{deal.exitName}</Pill>} />}
      {exitNotes && deal.exitName && <Row label="Exit notes" value={<EditableCell type="multiline" label="Exit notes" value={deal._raw?.exit_notes || ''} locked={dcLocked} onSave={(exit_notes) => onSave({ exit_notes: exit_notes || null })} />} />}
      <Row label="Type" value={
        <EditableCell
          type="select"
          value={deal.type}
          options={typeOptions}
          locked={dcLocked}
          onSave={(engagement_type) => onSave({ engagement_type })}
        />
      } />
      {/* Commercial approach (082) — checkmarks, at most one checked. The
          full sell-side approach is the only one with a truly different
          process, so picking it offers to switch the deal onto the sellside
          stage family (the backend carries the stage to its equivalent). */}
      {deal.type !== 'buyer_buyside' && <Row label="Approach" value={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '2px 0' }}>
          {SALE_APPROACHES.map((a) => {
            const checked = deal._raw?.sale_approach === a.value;
            return (
              <button key={a.value} title={a.hint}
                disabled={guest}
                onClick={async () => {
                  if (guest) return;
                  const next = checked ? null : a.value;
                  const patch = { sale_approach: next };
                  if (next) {
                    const wantsSellside = next === 'full_sellside';
                    if ((deal.type === 'sell_side') !== wantsSellside) {
                      const ok = await showConfirm({
                        title: wantsSellside
                          ? 'Switch to the sell-side process?'
                          : 'Switch to the buyside process?',
                        body: wantsSellside
                          ? 'The full sell-side approach runs the sell-side stages. The current stage is carried to its equivalent.'
                          : 'Targeted approaches run the buyside stages. The current stage is carried to its equivalent.',
                        confirmLabel: 'Switch process',
                      });
                      if (ok) patch.engagement_type = wantsSellside ? 'sell_side' : 'seller_buyside';
                    }
                  }
                  onSave(patch);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, background: 'transparent',
                  border: 'none', padding: '2px 0', cursor: guest ? 'default' : 'pointer', fontSize: 12,
                  color: checked ? 'var(--text)' : 'var(--text-3)', textAlign: 'left',
                  ...(rowPhone() ? { minHeight: 44, fontSize: 15, gap: 10 } : null),
                }}>
                <span style={{
                  width: 13, height: 13, borderRadius: 3, flexShrink: 0,
                  border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-strong)'}`,
                  background: checked ? 'var(--accent)' : 'transparent',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 9, lineHeight: 1,
                }}>{checked ? '✓' : ''}</span>
                {a.label}
              </button>
            );
          })}
        </div>
      } />}
      <Row label="Dealmaker" value={
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <EditableCell
            type="user"
            value={deal._raw?.dealmaker_id || null}
            users={users}
            fallbackLabel={deal._raw?.dealmaker_name}
            locked={dcLocked}
            onSave={(dealmaker_id) => window.saveOwnerWithScope({
              kind: 'engagement', field: 'dealmaker_id', value: dealmaker_id, users,
              companyName: (company && company.name) || deal.targetName, hasCompany: !!deal.target,
              save: onSave,
            })}
          />
          {deal._raw?.owner_locked && <window.OwnerLockHint />}
        </span>
      } />
      <Row label="Associate" value={
        <EditableCell
          type="user"
          anyone
          value={deal._raw?.associate_id || null}
          users={users}
          fallbackLabel={deal._raw?.associate_name}
          locked={dcLocked}
          onSave={(associate_id) => onSave({ associate_id })}
        />
      } />
      {/* Deal team (202609151200) — who is actually working this deal, which
          varies deal by deal. Dealmaker above stays the single owner; this
          neither reads from nor writes to Settings → Teams (the standing org
          chart). Atlas-only, so never DC-locked. */}
      <Row label="Deal team" value={
        <EditableCell
          type="users"
          value={deal._raw?.deal_team || []}
          users={users}
          fallbackLabel={deal._raw?.deal_team_names || []}
          onSave={(deal_team) => onSave({ deal_team })}
        />
      } />
      <Row label="Industry" value={deal.sector && deal.sector !== '—' ? <Pill hue="teal" sz="sm">{deal.sector}</Pill> : '—'} />
      {/* Lead source (142) — inherited from the company at creation; editable
          here when this deal's origin differs. Atlas-only, never DC-locked. */}
      {guest ? (
        <>
          <Row label="Lead source" value={deal._raw?.lead_source_name} />
          <Row label="Event" value={deal._raw?.lead_event_name} />
        </>
      ) : <window.LeadSourceFields raw={deal._raw || {}} onSave={onSave} Row={Row} />}
      {showServiceLine && <Row label="Service line" value={deal.sponsor} />}
      {/* Hot score (202609161000): the team's manual 1–10 priority — Atlas-only,
          never synced, so no DealCloud lock. Same picker as the grid cell. */}
      <Row label="Hot score" value={<window.HotScoreCell value={deal._raw?.hot_score ?? null} editable={!guest} onSave={(hot_score) => onSave({ hot_score })} />} />
      <Row label="Est. fees" value={
        <EditableCell
          type="currency"
          value={deal.size}
          locked={dcLocked}
          onSave={(estimated_fees) => onSave({ estimated_fees })}
        />
      } />
      <Row label="Weighted" value={<span className="num">{fmtMoney(deal.weighted)}</span>} />
      {moneyRow('Reported revenue', 'reported_revenue')}
      {moneyRow('Reported EBITDA', 'reported_ebitda')}
      {moneyRow('Transaction size', 'transaction_size')}
      {deal.totalFeesInvoiced != null && <Row label="Fees invoiced" value={<span className="num">{fmtMoney(deal.totalFeesInvoiced)}</span>} />}
      {deal.totalFeesReceived != null && <Row label="Fees received" value={<span className="num">{fmtMoney(deal.totalFeesReceived)}</span>} />}
      <Row label="Est. close" value={
        <EditableCell
          type="date"
          value={deal.estCloseDate}
          locked={dcLocked}
          displayAs={deal.estCloseDate
            ? (
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 6 }}>
                <EstCloseValue value={deal.estCloseDate}
                  closed={dealIsClosed(deal, (pipelineStages || []).find((s) => s.slug === deal.stage))} />
                {deal.type !== 'buyer_buyside' && window.CloseDateHint && <span style={{ fontSize: 10.5, color: 'var(--text-4)', whiteSpace: 'nowrap' }}><window.CloseDateHint deal={deal} /></span>}
              </span>
            )
            : undefined}
          onSave={(estimated_close_date) => onSave({ estimated_close_date })}
        />
      } />
      {deal.type !== 'buyer_buyside' && (
        <Row label="LOI signed" value={
          <EditableCell type="date" label="LOI signed" value={deal._raw?.loi_signed_date || null}
            displayAs={!deal._raw?.loi_signed_date && deal._raw?.loi_date ? <span className="num">{fmtDate(deal._raw.loi_date)}</span> : undefined}
            onSave={(loi_signed_date) => onSave({ loi_signed_date })} />
        } />
      )}
      {deal.type !== 'buyer_buyside' && window.RetainerCell && (
        <Row label="Retainer" value={<window.RetainerCell deal={deal} canWrite={window.recordCanWrite()} onSave={onSave} />} />
      )}
      {deal.liveDate          && <Row label="Live since"     value={fmtDate(deal.liveDate)} />}
      {deal.closingProcessDate && <Row label="Closing since" value={fmtDate(deal.closingProcessDate)} />}
      {deal.closedDate        && <Row label="Closed on"     value={fmtDate(deal.closedDate)} />}
      <Row label="Next action" value={
        <EditableCell
          type="date"
          value={deal._raw?.next_action_date}
          locked={dcLocked}
          onSave={(next_action_date) => onSave({ next_action_date })}
        />
      } />
      {/* Pushes Engagement.NextActionNotes to DealCloud — the field DC shows
          as "Next Action". Editing the imported automation note doesn't. */}
      <Row label="Next action note" value={
        <EditableCell
          type="multiline"
          label="Next action note"
          value={deal._raw?.next_action_notes || ''}
          locked={dcLocked}
          onSave={(next_action_notes) => onSave({ next_action_notes })}
        />
      } />
      {deal.lastActivityAt && (
        <Row label="Last activity" value={
          <span title="Most recent note linked to this engagement or its company" style={{ color: 'var(--text-2)' }}>
            {fmtDate(deal.lastActivityAt)}
          </span>
        } />
      )}
      <Row label="Archived" value={
        guest ? (deal.archived ? <Pill hue="slate" sz="sm">{deal.archivedReason || 'archived'}</Pill> : 'No') :
        deal.archived ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Pill hue="slate" sz="sm">{deal.archivedReason || 'archived'}</Pill>
            <button
              onClick={() => onSave({ is_archived: false, archived_reason: null })}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--accent-text)', fontSize: 11, padding: 0,
                textDecoration: 'underline', textUnderlineOffset: 2, ...(window.phoneHitHost ? window.phoneHitHost() : null),
              }}
            >{window.PhoneHit && <window.PhoneHit />}Un-archive</button>
          </span>
        ) : (
          <button
            onClick={() => onSave({ is_archived: true, archived_reason: 'manual' })}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-3)', fontSize: 11.5, padding: 0, ...(window.phoneHitHost ? window.phoneHitHost() : null),
            }}
          >{window.PhoneHit && <window.PhoneHit />}Archive…</button>
        )
      } />
      <Row label="ID" value={<window.RecordId value={deal._raw?.dealcloud_id} />} />
      {!guest && <DealMergeRow deal={deal} />}

      {/* Milestones — the deal's stage signals, mirroring the company-detail
          widget (scripts/compute_engagement_signals.py mines them). */}
      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Milestones</div>
      <div style={{ padding: '4px 14px 14px' }}>
        <window.SignalsChips signals={deal._raw?.signals || {}} />
      </div>

      {(company || {}).name && (
        <>
          <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Target company</div>
          <Row label="Name" value={!canOpenCompanies ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Logo name={company.name} color={company.logo} size={16} />
              <span style={{ fontWeight: 500 }}>{company.name}</span>
            </span>
          ) :
            <span
              onClick={() => window.AtlasNav?.goToCompany?.(company.id)}
              title={`Open ${company.name}`}
              onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', textUnderlineOffset: 2 }}
            >
              <Logo name={company.name} color={company.logo} size={16} />
              <span style={{ fontWeight: 500 }}>{company.name}</span>
              <Icon.ChevronR size={10} color="var(--text-4)" />
            </span>
          } />
          <Row label="Domain" value={company.domain ? (
            <a
              href={`https://${company.domain}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--accent-text)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}
              onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >{window.PhoneHit && <window.PhoneHit />}{company.domain}<Icon.External size={10} /></a>
          ) : '—'} />
          <Row label="HQ" value={company.hq} />
          <Row label="Industry" value={company.industry && company.industry !== '—' ? <Pill hue="teal" sz="sm">{company.industry}</Pill> : '—'} />
          <Row label="Revenue" value={<span className="num">{fmtMoney(company.revenue)}</span>} />
          <Row label="EBITDA" value={<span className="num">{fmtMoney(company.ebitda)}</span>} />
          <Row label="Owner" value={company.owner} />
          {!guest && <Row label="Contacts" value={
            <button
              onClick={() => setAddingContact(true)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--accent-text)', fontSize: 11.5, padding: 0,
                display: 'inline-flex', alignItems: 'center', gap: 4, ...(window.phoneHitHost ? window.phoneHitHost() : null),
              }}
            >{window.PhoneHit && <window.PhoneHit />}<Icon.Plus size={10} /> Add contact…</button>
          } />}
          {addingContact && (
            <window.NewPersonDrawer
              initialCompany={{ id: company.id, name: company.name }}
              onClose={() => setAddingContact(false)}
              onCreated={() => {
                setAddingContact(false);
                window.dispatchEvent(new CustomEvent('atlas-data-changed'));
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

// ============ Center: next action card ============
// The deal's next action, right above the notes: next_action_date (never
// deal.closeBy — that falls back from the est. close date) and the note.
// Both edit in place with the same PATCH + DealCloud-lock confirm as the
// Properties rows; the chips move the date to today + N (local calendar
// date); Update opens the notes composer on the 'Next Action' subtype,
// which rewrites both fields server-side and keeps the old one in Notes.
// Shown on every deal — a missing next action is the thing to notice.
const NEXT_ACTION_BUMPS = [['+1d', 1], ['+1w', 7], ['+2w', 14]];
const NextActionCard = ({ deal, onSave, onCompose }) => {
  const guest = !!window.AtlasIsGuest;
  const dcLocked = Boolean(deal._raw?.dealcloud_id);
  const naDate = deal._raw?.next_action_date ? String(deal._raw.next_action_date).slice(0, 10) : null;
  const naNote = deal._raw?.next_action_notes || '';
  const today = localToday();
  const naOverdue = !!naDate && naDate < today;
  const naToday = naDate === today;
  const naWhen = naDate ? `${fmtDate(naDate)}${naOverdue ? ' · overdue' : naToday ? ' · today' : ''}` : null;
  const notePreview = naNote ? String(naNote).replace(/\s+/g, ' ').slice(0, 140) : null;
  // Written by the daily automation (backend app/next_action_auto.py)? The
  // trail comes back null once a person has edited it.
  const [auto, setAuto] = React.useState(null);
  const [whyOpen, setWhyOpen] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    setAuto(null);
    if (!naNote || !window.AtlasApi.engagementNextActionAuto) return undefined;
    window.AtlasApi.engagementNextActionAuto(deal.id)
      .then((r) => { if (live) setAuto((r && r.auto) || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [deal.id, naNote]);
  const autoTip = auto
    ? `Written by ${auto.author} on ${fmtDate(auto.at)} from ${auto.evidence.length} quoted source${auto.evidence.length === 1 ? '' : 's'}.\nRewritten only after this date passes, and only if there's new activity. Any edit takes it over.`
    : null;
  const bump = async (days) => {
    const d = new Date(); d.setDate(d.getDate() + days);
    const next_action_date = window.localISODate(d);
    try { await onSave({ next_action_date }); } catch (_) { /* saveDeal already toasted */ }
  };
  const chip = {
    border: '1px solid var(--border-strong)', borderRadius: 5, background: 'var(--surface)',
    cursor: 'pointer', padding: '1px 7px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-2)',
  };
  return (
    <Section title="Next action" collapsible id="deal.next_action"
      preview={[naWhen, notePreview].filter(Boolean).join(' — ') || '—'}
      action={!guest && onCompose ? (
        <Btn size="xs" variant="outline" icon={<Icon.Calendar size={10} />} onClick={() => onCompose('next_action')}>Update</Btn>
      ) : null}>
      <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
          <Icon.Calendar size={11} color="var(--text-3)" />
          <EditableCell
            type="date"
            label="Next action"
            value={deal._raw?.next_action_date}
            locked={dcLocked}
            displayAs={naDate ? (
              <span title={autoTip || undefined} style={{ fontWeight: 600, color: naOverdue ? 'var(--rose-fg)' : naToday ? 'var(--amber-fg)' : 'var(--text-2)' }}>{naWhen}</span>
            ) : undefined}
            onSave={(next_action_date) => onSave({ next_action_date })}
          />
          {auto && (
            <button type="button" title={autoTip} aria-expanded={whyOpen}
              aria-label={`Automatic next action, ${auto.evidence.length} sources`}
              onClick={() => setWhyOpen((v) => !v)}
              style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' }}>
              <Pill hue="violet" sz="sm">
                <Icon.Sparkle size={10} /> Auto · {auto.evidence.length} source{auto.evidence.length === 1 ? '' : 's'}
              </Pill>
            </button>
          )}
          {!guest && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {NEXT_ACTION_BUMPS.map(([label, days]) => (
                <button key={label} type="button" onClick={() => bump(days)} style={chip}>{label}</button>
              ))}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.55, minWidth: 0 }}>
          <EditableCell
            type="multiline"
            label="Next action note"
            value={naNote}
            locked={dcLocked}
            onSave={(next_action_notes) => onSave({ next_action_notes })}
          />
        </div>
        {auto && whyOpen && <NextActionAutoTrail auto={auto} />}
      </div>
    </Section>
  );
};

// The automatic next action's sources: each quote with the note it came
// from (a click opens that note), then how the date was picked.
const NextActionAutoTrail = ({ auto }) => {
  const kindLabel = { email: 'Email', meeting: 'Meeting', calendar: 'Calendar', call: 'Call', note: 'Note' };
  const row = { display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '4px 0', cursor: 'pointer', color: 'inherit', font: 'inherit' };
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11.5 }}>
      {auto.evidence.map((ev, i) => (
        <button key={`${ev.note_id}:${i}`} type="button" style={row}
          title="Open this note" onClick={() => window.openRecordNote && window.openRecordNote(ev.note_id, { via: 'button' })}>
          <div style={{ color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[kindLabel[ev.kind] || 'Note', ev.at ? fmtDate(ev.at) : null, ev.title].filter(Boolean).join(' · ')}
          </div>
          <div style={{ color: 'var(--text-2)', lineHeight: 1.45, overflowWrap: 'anywhere' }}>“{ev.quote}”</div>
        </button>
      ))}
      <div style={{ color: 'var(--text-3)', paddingTop: 2, overflowWrap: 'anywhere' }}>
        {auto.date_basis === 'quoted' && auto.date_quote
          ? <>Date from “{auto.date_quote.quote}”</>
          : auto.date_basis === 'hold' ? <>Date: hold check-in</> : <>Date: follow-up interval</>}
        {' · '}{auto.considered} read since {auto.since ? fmtDate(auto.since) : '—'}
      </div>
    </div>
  );
};

// ============ Center: notes + stage history ============
const CenterPane = ({ deal, activeUser, stageHistory, buyerEngs, onPatchBuyerEng, onBuyerEngUpdate, onBuyersAdded, ersScores, highlights, callAnalytics, notes, notesTotal, notesLoadingMore, onLoadMoreNotes, notesSearch, onNotesSearch, notesQ, onNotesChange, onDealChange, onTasksChange, onOpenOutreach, onSave, composeRequest, onCompose }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Every section below folds to its header + a one-line preview and
          remembers the choice per person; this row does them all at once. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginBottom: -6 }}>
        <Btn size="xs" variant="ghost" onClick={() => window.setAllSections(true)}>Expand all</Btn>
        <span style={{ color: 'var(--text-4)', fontSize: 11, alignSelf: 'center' }}>·</span>
        <Btn size="xs" variant="ghost" onClick={() => window.setAllSections(false)}>Collapse all</Btn>
      </div>
      {window.ClosingSection && <window.ClosingSection deal={deal} />}
      {window.CloseTimelineCard && <window.CloseTimelineCard deal={deal} canWrite={window.recordCanWrite()} onSave={onSave} />}
      {/* A deal guest (backend app/deal_guest.py) gets the deal's own record,
          not the working surfaces around it: the outreach tracker (writes
          everywhere), comps (other companies' sales) and the audit trail. */}
      {window.OutreachSection && !window.AtlasIsGuest && (window.recordCardsOn('DealBuyersCard') ? null : <window.OutreachSection deal={deal} onOpen={onOpenOutreach} />)}
      {/* LOIs side by side (loi_compare.jsx) — seller deals with buyers; gates itself. */}
      {window.LoiCompareCard && <window.LoiCompareCard deal={deal} />}
      {window.recordCardsOn('IntelCard') ? <window.IntelCard deal={deal} /> : <window.SellerBriefSection deal={deal} />}
      {/* The company's data vault — the CIM working set (company_vault.jsx). */}
      {!window.AtlasIsGuest && deal.type !== 'buyer_buyside' && deal.target && window.CompanyVaultSection && (
        <window.CompanyVaultSection companyId={deal.target} sectionId="deal.vault" panelRecord={{ kind: 'deal', id: deal.id }}
          engagementId={!deal._raw?.is_duplicate ? deal.id : null} />
      )}
      {!window.AtlasIsGuest && (window.recordCardsOn('MarketCard') ? <window.MarketCard deal={deal} /> : <window.CompsSection deal={deal} />)}
      <ErsCard scores={ersScores} />
      {window.recordCardsOn('DealBuyersCard') ? <window.DealBuyersCard deal={deal} /> : <window.BuyerEngagementsSection deal={deal} buyerEngs={buyerEngs} onPatch={onPatchBuyerEng} onRowUpdate={onBuyerEngUpdate} onBuyersAdded={onBuyersAdded} />}
      {!window.AtlasIsGuest && window.BlastNetworkCard && <window.BlastNetworkCard deal={deal} variant="card" canWrite={window.recordCanWrite()} />}
      <HighlightsSection items={highlights} />
      <CallAnalyticsSection items={callAnalytics} />

      <NextActionCard deal={deal} onSave={onSave} onCompose={activeUser ? onCompose : null} />

      <window.RecordNotes kind="deal" recordId={deal.id} sectionId="deal.notes"
        feed={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: onLoadMoreNotes,
          reload: onNotesChange, search: notesSearch, setSearch: onNotesSearch, q: notesQ }}
        attach={{ engagement_id: deal.id, company_id: deal.target || null }}
        activeUser={activeUser} deal={deal} hideCompanyId={deal.target}
        talkTime={window.talkTimeByNote(callAnalytics)}
        onDealChange={onDealChange} onTasksChange={onTasksChange}
        composeRequest={composeRequest} />

      {!window.AtlasIsGuest && <window.AuditTimeline params={{ object_type: 'engagement', object_id: deal.id }} sectionId="deal.history" />}

      {stageHistory && stageHistory.length > 0 && (
        <Section title={`Stage history · ${stageHistory.length}`} collapsible id="deal.stage_history" defaultOpen={false}
          preview={`now ${stageHistory[0].to_stage_name || stageHistory[0].to_stage_slug} · ${new Date(stageHistory[0].created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}`}>
          <div>
            {stageHistory.map((h, i) => (
              <div key={h.id} style={{
                display: 'flex', gap: 10, padding: '8px 14px',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                alignItems: 'center',
              }}>
                <div style={{ width: 18, height: 18, borderRadius: 999, background: HUE.blue.bg, color: HUE.blue.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon.Pipeline size={10} />
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                  <span style={{ color: 'var(--text-3)' }}>{h.from_stage_name || h.from_stage_slug || '(new)'}</span>
                  <span style={{ margin: '0 6px', color: 'var(--text-4)' }}>→</span>
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>{h.to_stage_name || h.to_stage_slug}</span>
                  {h.exit_name && <span style={{ marginLeft: 6 }}><Pill hue="rose" sz="sm">{h.exit_name}</Pill></span>}
                  {h.changed_by_name && <span style={{ marginLeft: 6, color: 'var(--text-3)' }}>· {h.changed_by_name}</span>}
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>
                  {new Date(h.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

// ============ Highlights — Fireflies soundbites on this deal ============
// Rendered only when a meeting linked to this deal has flagged soundbites
// (GET /engagements/{id}/highlights). Each row deep-links to the clip in
// Fireflies (preview URL) or, when that's missing, the meeting itself.
const HighlightsSection = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Section title={`Highlights · ${items.length}`} collapsible id="deal.highlights" defaultOpen={false}
      preview={`${items.length} soundbite${items.length === 1 ? '' : 's'} from meetings on this deal`}>
      <div>
        {items.map((h, i) => {
          const href = h.preview || h.meeting_url;
          return (
            <div key={h.id || `${h.note_id}-${i}`} style={{
              display: 'flex', gap: 10, padding: '9px 14px',
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
              alignItems: 'flex-start',
            }}>
              <div style={{ width: 18, height: 18, borderRadius: 999, background: HUE.amber.bg, color: HUE.amber.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                <Icon.Play size={10} />
              </div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  {href ? (
                    <a href={href} target="_blank" rel="noreferrer" title="Open the clip in Fireflies"
                       style={{ color: 'var(--text)', fontWeight: 500, textDecoration: 'none' }}>
                      {h.name || 'Soundbite'} ↗
                    </a>
                  ) : (
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{h.name || 'Soundbite'}</span>
                  )}
                  {h.start_time && (
                    <span style={{ fontSize: 10.5, color: 'var(--text-4)', flexShrink: 0 }}>
                      {h.start_time}{h.end_time ? `–${h.end_time}` : ''}
                    </span>
                  )}
                </div>
                {h.summary && (
                  <div style={{ marginTop: 2, color: 'var(--text-3)', lineHeight: 1.5 }}>
                    <ClampedText text={h.summary} lines={2} />
                  </div>
                )}
                <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-4)' }}>
                  {h.meeting_title || 'Meeting'}
                  {h.meeting_date && ` · ${new Date(h.meeting_date).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// ============ Call analytics — talk time + sentiment per meeting ============
// One row per recorded meeting (Fireflies MeetingAnalytics, mirrored onto
// notes.metadata.analytics): a stacked talk-time bar per speaker and the
// sentiment split. Items arrive oldest-first from the API (trend order);
// rows render newest-first. Hidden when no meeting has analytics.
const _SPEAKER_HUES = ['blue', 'violet', 'green', 'amber', 'rose', 'cyan', 'slate', 'indigo'];
const _speakerHue = (i) => HUE[_SPEAKER_HUES[i % _SPEAKER_HUES.length]] || HUE.slate;

const _fmtMins = (secs) => {
  if (secs == null) return null;
  const m = Math.round(secs / 60);
  return m >= 1 ? `${m}m` : `${Math.round(secs)}s`;
};

const CallAnalyticsSection = ({ items }) => {
  if (!items || items.length === 0) return null;
  const withSent = items.filter((m) => m.sentiments && m.sentiments.positive_pct != null);
  const avgPos = withSent.length
    ? Math.round(withSent.reduce((a, m) => a + (m.sentiments.positive_pct || 0), 0) / withSent.length)
    : null;
  const rows = [...items].reverse();  // newest first for reading
  return (
    <Section title={`Call analytics · ${items.length}`} collapsible id="deal.call_analytics" defaultOpen={false}
      preview={avgPos != null ? `${items.length} call${items.length === 1 ? '' : 's'} · ${avgPos}% positive on average` : `${items.length} call${items.length === 1 ? '' : 's'}`}>
      {avgPos != null && (
        <div style={{ padding: '8px 14px 0', fontSize: 11.5, color: 'var(--text-3)' }}>
          Avg positive sentiment <b style={{ color: 'var(--text-2)' }}>{avgPos}%</b> across {withSent.length} recorded call{withSent.length === 1 ? '' : 's'}
        </div>
      )}
      <div>
        {rows.map((m, i) => {
          const speakers = (m.speakers || []).filter((s) => (s.duration_pct || 0) > 0);
          const sent = m.sentiments || {};
          return (
            <div key={m.note_id || i} style={{
              padding: '9px 14px',
              borderTop: i === 0 && avgPos == null ? 'none' : '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12 }}>
                <span style={{ color: 'var(--text)', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.meeting_title || 'Meeting'}
                </span>
                {sent.positive_pct != null && (
                  <span style={{ fontSize: 10.5, flexShrink: 0 }} title="Sentiment split (positive / neutral / negative)">
                    <span style={{ color: HUE.green.fg }}>{Math.round(sent.positive_pct)}%</span>
                    <span style={{ color: 'var(--text-4)' }}> / {Math.round(sent.neutral_pct || 0)}% / </span>
                    <span style={{ color: HUE.rose.fg }}>{Math.round(sent.negative_pct || 0)}%</span>
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>
                  {_fmtMins(m.duration != null ? m.duration * 60 : null) || ''}
                  {m.meeting_date && ` · ${new Date(m.meeting_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`}
                </span>
              </div>
              {speakers.length > 0 && (
                <>
                  <div style={{ display: 'flex', height: 6, borderRadius: 4, overflow: 'hidden', marginTop: 6, background: 'var(--surface-2, var(--border))' }}>
                    {speakers.map((s, si) => (
                      <div key={si} title={`${s.name} — ${Math.round(s.duration_pct)}% of talk time${s.words_per_minute ? ` · ${Math.round(s.words_per_minute)} wpm` : ''}`}
                        style={{ width: `${Math.max(1, s.duration_pct)}%`, background: _speakerHue(si).fg, opacity: 0.75 }} />
                    ))}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--text-4)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {speakers.slice(0, 4).map((s, si) => (
                      <span key={si} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 7, height: 7, borderRadius: 999, background: _speakerHue(si).fg, opacity: 0.75, display: 'inline-block' }} />
                        {s.name} {Math.round(s.duration_pct)}%
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// ============ Exit Readiness Score card ============
// Renders the latest ERS for this engagement/company, with the 5 sub-scores.
// Hidden entirely until the Phase 2 scorer (Module 02) writes the first score —
// an "awaiting first scoring" banner on every engagement was just noise.
const ErsCard = ({ scores }) => {
  if (scores === null || scores.length === 0) return null;

  // Latest first (placeholder endpoint orders by created_at DESC)
  const latest = scores[0];
  const total = Number(latest.score_total) || 0;
  const routing = latest.routing;
  const routingHue = routing === 'blast_ready' ? 'green'
                   : routing === 'developing'  ? 'amber'
                   : routing === 'not_ready'   ? 'rose'
                   :                              'slate';
  const subs = [
    ['Timeline',  latest.score_timeline],
    ['Financial', latest.score_financial],
    ['Emotional', latest.score_emotional],
    ['Valuation', latest.score_valuation],
    ['Health',    latest.score_biz_health],
  ];
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <Icon.Sparkle size={13} color="var(--accent)" />
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>Exit Readiness Score</span>
        <span className="num" style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
          {total.toFixed(1)}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>/ 10</span>
        {routing && <Pill hue={routingHue} dot sz="sm">{routing.replace(/_/g, ' ')}</Pill>}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>
          {scores.length > 1 ? `${scores.length} scores · ` : ''}
          {new Date(latest.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
        {subs.map(([label, val]) => (
          <div key={label}>
            <div style={{ fontSize: 10, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
            <div className="num" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{val != null ? Number(val).toFixed(1) : '—'}</div>
          </div>
        ))}
      </div>
      {latest.primary_blocker && (
        <div style={{ marginTop: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, fontSize: 11.5, color: 'var(--text-2)' }}>
          <b style={{ color: 'var(--text)' }}>Primary blocker:</b> {latest.primary_blocker}
        </div>
      )}
      {latest.recommended_action && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--text-3)' }}>
          <b style={{ color: 'var(--text-2)' }}>Next step:</b> {latest.recommended_action}
        </div>
      )}
    </div>
  );
};

// ============ Right rail: workspaces ============
// Only when this deal has a workspace (the header's "New workspace" starts
// the first one); a row opens its workspace.
const WorkspacesPanel = ({ deal, workspaces, onNew }) => {
  if (!workspaces || workspaces.length === 0) return null;
  const open = (id) => window.AtlasNav?.goToWorkspace?.(id);
  return (
    <div>
      <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon.Sparkle size={13} color="var(--accent)" />
        <span style={{ fontSize: 13, fontWeight: 600 }}>Agent workspaces</span>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>
          {workspaces.length}
        </span>
        <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} onClick={onNew}>New</Btn>
      </div>

      {workspaces.map((w) => {
        const status = w.latest_run_status || (w.active_run_count > 0 ? 'running' : 'idle');
        const sHue = status === 'running' ? 'blue'
                   : status === 'completed' ? 'green'
                   : status === 'failed' ? 'rose'
                   : 'slate';
        return (
          <div key={w.id} role="button" tabIndex={0} title={`Open ${w.name}`}
            onClick={() => open(w.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(w.id); } }}
            onMouseEnter={window.hoverOnly((e) => { e.currentTarget.style.background = 'var(--surface-2)'; })}
            onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
            style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
              <Pill hue={sHue} dot sz="sm">{status}</Pill>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
              Lead · {w.lead_agent_key.replace(/-/g, ' ')}
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 12, fontSize: 10.5, color: 'var(--text-4)', fontFamily: 'var(--font-mono)' }}>
              <span>{w.run_count} runs</span>
              <span>{w.artifact_count} artifacts</span>
              <span>{w.handoff_count} handoffs</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============ Right rail: Tasks panel ============
// Phone: finger-sized check targets, 15px rows, and the add form in a
// full-screen sheet (Save above the keyboard). Desktop is unchanged.
// `dimEmpty` (the v2 company card): no tasks reads as one dimmed — row.
// `openTitle` (the v2 company card): the header reads Open tasks · N + Task, as on deals and people.
const TasksPanel = ({ deal, activeUser, tasks, onChange, dimEmpty = false, openTitle = false }) => {
  const phone = !!window.useDevice().phone;
  const [composing, setComposing] = React.useState(false);
  const [draft, setDraft] = React.useState({ title: '', priority: 'normal', due_date: '' });
  const [saving, setSaving] = React.useState(false);

  // Shared complete/undo interaction (primitives.jsx): optimistic check,
  // linger with "Done — next <date>" for recurring tasks, undo retracts the
  // spawned occurrence server-side.
  const taskToggle = window.useTaskToggle(onChange);
  const guest = !!window.AtlasIsGuest;   // read-only: no add, no check-off

  const save = async () => {
    if (!draft.title.trim() || !draft.due_date) {
      pushToast({ type: 'warning', title: 'Missing', body: 'Need a title and a due date.' });
      return;
    }
    setSaving(true);
    try {
      await window.AtlasApi.createTask({
        title: draft.title.trim(),
        priority: draft.priority,
        due_date: draft.due_date,
        engagement_id: deal.id,
        company_id: deal.target || null,
        assigned_to: activeUser?.id,
        created_by: activeUser?.id || null,
      });
      setDraft({ title: '', priority: 'normal', due_date: '' });
      setComposing(false);
      pushToast({ type: 'success', title: 'Task added' });
      onChange();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  const open = (tasks || []).filter((t) => t.status !== 'completed');
  const done = (tasks || []).filter((t) => t.status === 'completed');

  return (
    <div>
      {openTitle ? (
        <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Open tasks · {open.length}</span>
          {!composing && !guest && (
            <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} onClick={() => setComposing(true)} disabled={!activeUser} style={{ marginLeft: 'auto' }}>Task</Btn>
          )}
        </div>
      ) : (
      <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon.Check size={13} color="var(--accent)" />
        <span style={{ fontSize: phone ? 15 : 13, fontWeight: 600 }}>Tasks</span>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{open.length} open</span>
        {!composing && !guest && (
          <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} onClick={() => setComposing(true)} disabled={!activeUser}>Add</Btn>
        )}
      </div>
      )}

      {phone && (
        <Drawer open={composing} onClose={() => { if (!saving) { setComposing(false); setDraft({ title: '', priority: 'normal', due_date: '' }); } }}
          title="New task" subtitle={deal.name}
          footer={(
            <div style={{ display: 'flex', gap: 10, flex: 1 }}>
              <Btn variant="outline" disabled={saving} style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => { setComposing(false); setDraft({ title: '', priority: 'normal', due_date: '' }); }}>Cancel</Btn>
              <Btn variant="accent" onClick={save} disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Saving…' : 'Save task'}</Btn>
            </div>
          )}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <TextInput value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="What needs to happen…" />
            <Combobox value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} placeholder="Priority"
              options={[
                { value: 'urgent', label: 'Urgent' },
                { value: 'high',   label: 'High' },
                { value: 'normal', label: 'Normal' },
                { value: 'low',    label: 'Low' },
              ]} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-3)' }}>
              <span style={{ width: 70, flexShrink: 0 }}>Due</span>
              <TextInput type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} style={{ width: '100%' }} />
            </label>
            <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Assigned to you.</div>
          </div>
        </Drawer>
      )}
      {composing && !phone && (
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <TextInput
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="What needs to happen…"
            style={{ marginBottom: 6 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
            <Combobox value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
              options={[
                { value: 'urgent', label: 'Urgent' },
                { value: 'high',   label: 'High' },
                { value: 'normal', label: 'Normal' },
                { value: 'low',    label: 'Low' },
              ]} />
            <TextInput type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <Btn size="xs" variant="ghost" onClick={() => { setComposing(false); setDraft({ title: '', priority: 'normal', due_date: '' }); }}>Cancel</Btn>
            <Btn size="xs" variant="accent" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
          </div>
        </div>
      )}

      {tasks === null && <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {tasks && open.length === 0 && done.length === 0 && (dimEmpty ? (
        <div data-task-empty="" style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-4)' }}>—</div>
      ) : (
        <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>
          No tasks for this engagement.
        </div>
      ))}
      {open.map((t, i) => {
        const ps = taskToggle.stateFor(t.id);
        const checked = !!ps;
        const lingering = ps && ps.phase === 'done';
        const overdue = !checked && new Date(t.due_date) < new Date();
        const pHue = t.priority === 'urgent' ? 'rose' : t.priority === 'high' ? 'amber' : t.priority === 'low' ? 'slate' : 'blue';
        return (
          <div key={t.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: phone ? '4px 14px 10px 4px' : '8px 14px', borderBottom: i === open.length - 1 ? '1px solid var(--border)' : '1px solid var(--border)',
            background: lingering ? 'var(--green-bg)' : 'transparent',
            transition: 'background .3s ease',
          }}>
            {phone ? (
              // A 44px tap target around a 22px box.
              <button onClick={() => { if (!guest) taskToggle.toggle(t); }} disabled={guest}
                aria-label={checked ? 'Completed' : 'Mark complete'}
                style={{ width: 44, height: 44, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', flexShrink: 0,
                         display: 'inline-flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent' }}>
                <span style={{
                  width: 22, height: 22, borderRadius: 6,
                  border: checked ? 'none' : '1.5px solid var(--border-strong)',
                  background: checked ? 'oklch(0.55 0.12 155)' : 'var(--surface)',
                  color: '#fff', fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  animation: ps ? 'atlas-check-pop .25s ease' : 'none',
                }}>{checked ? '✓' : ''}</span>
              </button>
            ) : (
            <button onClick={() => { if (!guest) taskToggle.toggle(t); }} disabled={guest}
              title={guest ? undefined : t.recurrence ? 'Complete — the next occurrence will be scheduled' : 'Mark complete'}
              style={{
                width: 14, height: 14, borderRadius: 4, marginTop: 2,
                border: checked ? 'none' : '1.5px solid var(--border-strong)',
                background: checked ? 'oklch(0.55 0.12 155)' : 'var(--surface)',
                cursor: 'pointer', padding: 0, flexShrink: 0,
                color: '#fff', fontSize: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                animation: ps ? 'atlas-check-pop .25s ease' : 'none',
              }}>{checked ? '✓' : ''}</button>
            )}
            <div style={{ flex: 1, minWidth: 0, ...(phone ? { paddingTop: 10 } : null) }}>
              <div style={{ fontSize: phone ? 15 : 12.5, fontWeight: 500, color: 'var(--text)', textDecoration: checked ? 'line-through' : 'none' }}>
                {t.title}
                {(t.recurrence_text || t.recurrence) && (
                  <span title="Recurring — completing it schedules the next occurrence" style={{
                    marginLeft: 6, fontSize: 10, fontWeight: 500, color: 'var(--text-3)',
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    borderRadius: 999, padding: '0 5px', whiteSpace: 'nowrap',
                  }}>↻ {t.recurrence_text || 'repeats'}</span>
                )}
              </div>
              {lingering ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: phone ? 13 : 11 }}>
                  <span style={{ fontWeight: 600, color: 'var(--green-fg)' }}>
                    {ps.spawnedDue ? `Done — next ${window.fmtTaskDue(ps.spawnedDue)}` : 'Done'}
                  </span>
                  <button onClick={() => taskToggle.undo(t)} style={{
                    border: '1px solid var(--border-strong)', borderRadius: 5,
                    background: 'var(--surface)', cursor: 'pointer', padding: '1px 7px',
                    fontSize: 10.5, fontWeight: 600, color: 'var(--text-2)',
                    ...(phone ? { height: 34, padding: '0 14px', fontSize: 13, borderRadius: 8 } : null),
                  }}>Undo</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: phone ? 13 : 11 }}>
                  <Pill hue={pHue} sz="sm">{t.priority}</Pill>
                  <span style={{ color: overdue ? 'var(--rose-fg)' : 'var(--text-3)' }}>
                    {overdue ? `overdue · ${t.due_date}` : t.due_date}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {done.length > 0 && (
        <details style={{ borderBottom: '1px solid var(--border)' }}>
          <summary style={{ padding: phone ? '12px 14px' : '8px 14px', cursor: 'pointer', fontSize: phone ? 14 : 11, color: 'var(--text-3)' }}>
            {done.length} completed
          </summary>
          {done.map((t) => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', opacity: 0.5 }}>
              <button onClick={() => { if (!guest) taskToggle.toggle(t); }} disabled={guest} style={{
                width: 14, height: 14, borderRadius: 4, marginTop: 2,
                border: 'none', background: 'oklch(0.55 0.12 155)',
                cursor: 'pointer', padding: 0, flexShrink: 0,
                color: '#fff', fontSize: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>✓</button>
              <span style={{ fontSize: 12, color: 'var(--text-2)', textDecoration: 'line-through' }}>{t.title}</span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
};
window.TasksPanel = TasksPanel;

// ============ Workspace creator drawer ============
const WorkspaceCreatorDrawer = ({ deal, activeUser, onClose, onCreated }) => {
  const [name,          setName]         = React.useState(`${deal.name} workspace`);
  const [leadAgentKey,  setLeadAgentKey] = React.useState('deal-orchestrator');
  const [focusType,     setFocusType]    = React.useState(deal.target ? 'deal' : 'general');
  const [sharedContext, setShared]       = React.useState('');
  const [catalog,       setCatalog]      = React.useState(null);
  const [saving,        setSaving]       = React.useState(false);

  React.useEffect(() => {
    window.AtlasApi.agentCatalog()
      .then((cat) => setCatalog(Array.isArray(cat) ? cat : []))
      .catch(() => setCatalog([]));
  }, []);

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        focus_type: focusType,
        company_id: deal.target || null,
        engagement_id: deal.id,
        lead_agent_key: leadAgentKey,
        shared_context: sharedContext.trim() || null,
        created_by: activeUser?.full_name || null,
      };
      await window.AtlasApi.fetch('/api/agent-workspaces', {
        method: 'POST', body: JSON.stringify(payload),
      });
      pushToast({ type: 'success', title: 'Workspace created' });
      onCreated();
    } catch (e) {
      pushToast({ type: 'error', title: 'Create failed', body: e.message });
    } finally { setSaving(false); }
  };

  const agents = (catalog || []).filter((a) => a.enabled && !a.hidden);

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title="New agent workspace"
      subtitle={deal.name}
      width={540}
      footer={(
        <>
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={create} disabled={saving || !name.trim() || !leadAgentKey}>
            {saving ? 'Creating…' : 'Create workspace'}
          </Btn>
        </>
      )}
    >
      <Field label="Workspace name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Focus" hint="What this workspace coordinates.">
        <Combobox value={focusType} onChange={(e) => setFocusType(e.target.value)}
          options={[
            { value: 'deal',         label: 'Deal (this engagement)' },
            { value: 'company',      label: 'Company (the target)' },
            { value: 'opportunity',  label: 'Opportunity' },
            { value: 'data-quality', label: 'Data quality' },
            { value: 'general',      label: 'General' },
          ]} />
      </Field>
      <Field label="Lead agent" hint="The orchestrator that runs the workspace. You can hand off to specialists later.">
        {catalog === null && <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Loading…</div>}
        {catalog && agents.length === 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--rose-fg)' }}>
            No agents enabled. Go to Settings → Agents to turn one on.
          </div>
        )}
        {catalog && agents.length > 0 && (
          <Combobox value={leadAgentKey} onChange={(e) => setLeadAgentKey(e.target.value)}
            options={agents.map((a) => ({ value: a.key, label: `${a.label} — ${a.category}` }))} />
        )}
      </Field>
      <Field label="Shared context" hint="What every specialist agent should know up front about this workspace.">
        <textarea
          value={sharedContext}
          onChange={(e) => setShared(e.target.value)}
          rows={5}
          placeholder={`e.g. ${deal.name} is currently at ${deal.stageName || deal.stage}. Industry: ${deal.sector || '—'}. Focus on …`}
          style={{
            width: '100%', padding: '8px 10px', fontSize: 12.5,
            border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'inherit',
            resize: 'vertical', minHeight: 90,
          }}
        />
      </Field>
    </Drawer>
  );
};

// SyncBadge — shows "Synced from DealCloud · X ago" for DC-linked records.
// When an Atlas-side edit happens after the last sync, the badge turns amber.
// Its "Sync now" re-pull button was retired 2026-10-05 (DealCloud is being
// retired; the reconcile still runs on its own) — the per-field research
// wand (views/research.jsx) is the button that fills a field now.
// recordType / recordId / onSynced are still accepted so callers needn't change.
const SyncBadge = ({ lastSyncedAt, updatedAt, dealcloudId }) => {
  if (!dealcloudId || !lastSyncedAt) return null;

  const synced  = new Date(lastSyncedAt);
  const updated = updatedAt ? new Date(updatedAt) : null;
  // 60s grace — small clock-skew shouldn't be flagged as "local edit pending".
  const localEdited = updated && (updated - synced) > 60_000;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, height: 22,
      borderRadius: 999, background: localEdited ? 'var(--amber-bg)' : 'var(--surface-2)',
      color:      localEdited ? 'var(--amber-fg)' : 'var(--text-3)',
      border: '1px solid var(--border)', fontSize: 11, fontWeight: 500, overflow: 'hidden',
    }}>
      <span
        title={
          localEdited
            ? `Last sync: ${synced.toLocaleString()}\nAtlas edit at: ${updated.toLocaleString()}`
            : `Last synced from DealCloud at ${synced.toLocaleString()}.`
        }
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 8px' }}
      >
        <Icon.Refresh size={11} />
        Synced {relativeTime(lastSyncedAt)}
        {localEdited && <span style={{ color: 'var(--amber-fg)', fontWeight: 600 }}>· edited in Atlas</span>}
      </span>
    </span>
  );
};

// ── Attachment chips (mirrored from DealCloud, served from local_bucket) ──
// Renders inline below a note's body. Files that exist on disk are clickable
// (open in new tab); referenced_only chips are grayed with an explanation
// tooltip so historical metadata stays visible.

const ATTACHMENT_EXT_HUE = {
  pdf: 'rose', xls: 'green', xlsx: 'green', csv: 'green',
  doc: 'blue', docx: 'blue', txt: 'slate', md: 'slate',
  ppt: 'amber', pptx: 'amber',
  jpg: 'violet', jpeg: 'violet', png: 'violet', gif: 'violet', webp: 'violet', heic: 'violet',
  mp4: 'violet', mov: 'violet', avi: 'violet', webm: 'violet',
  zip: 'slate', tar: 'slate', gz: 'slate', '7z': 'slate', rar: 'slate',
};

const _fmtSize = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// Browser-renderable image extensions. HEIC isn't supported natively so it
// falls back to the generic chip.
const _IMG_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']);
const _isImage = (att) => _IMG_EXTS.has((att.file_extension || '').toLowerCase());

// Touch: the 14–16px × is too small to hit with a finger, so the button
// becomes a 36px transparent target with the same small circle drawn in
// its middle. Desktop keeps the bare circle.
const _attTouch = () => !!(window.AtlasDevice && window.AtlasDevice.touch);
const TouchX = ({ onClick, title, circle, style }) => (
  <button type="button" onClick={onClick} title={title} aria-label={title}
    style={{ width: 36, height: 36, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', flex: 'none',
             display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
             WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', ...style }}>
    <span aria-hidden style={{ width: 20, height: 20, borderRadius: 999, fontSize: 12, lineHeight: 1,
                               display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...circle }}>×</span>
  </button>
);

const AttachmentChip = ({ att, onDetach }) => {
  const touch = _attTouch();
  const available = att.storage_status === 'available' && att.storage_uri;
  const hue = ATTACHMENT_EXT_HUE[(att.file_extension || '').toLowerCase()] || 'slate';
  const h = HUE[hue] || HUE.slate;
  const sizeStr = _fmtSize(att.size_bytes);
  const isImg = available && _isImage(att);
  const Tag  = available ? 'a' : 'span';
  // Downloads sit behind the login wall — a bare href can't carry the auth
  // header, so clicks resolve a fresh signed URL through the API client.
  const tagProps = available
    ? { href: '#', onClick: (e) => { e.preventDefault(); window.AtlasApi.openAttachment(att.id, att.original_filename); } }
    : {};

  // Image variant: thumbnail card with overlaid filename + size on hover.
  // Touch has no hover, so the filename is printed under the thumbnail.
  if (isImg) {
    const detach = (e) => { e.preventDefault(); e.stopPropagation(); onDetach(att); };
    return (
      <span style={{ position: 'relative', display: 'inline-block', ...(touch ? { width: 64, verticalAlign: 'top' } : null) }}>
        <Tag
          {...tagProps}
          title={`${att.original_filename}${sizeStr ? ` · ${sizeStr}` : ''} — open full size`}
          style={{
            display: 'inline-block', width: 64, height: 64, borderRadius: 6,
            border: '1px solid var(--border)', overflow: 'hidden',
            backgroundColor: 'var(--surface-2)', cursor: 'pointer',
          }}
        >
          <window.AuthThumb
            attachmentId={att.id}
            alt={att.original_filename}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </Tag>
        {touch && (
          <span style={{ display: 'block', marginTop: 2, fontSize: 10.5, color: 'var(--text-3)', lineHeight: 1.3,
                         overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {att.original_filename}
          </span>
        )}
        {onDetach && touch && (
          <TouchX onClick={detach} title="Detach"
            style={{ position: 'absolute', top: -14, right: -14 }}
            circle={{ background: '#000', color: '#fff', border: '1.5px solid #fff' }} />
        )}
        {onDetach && !touch && (
          <button onClick={detach}
            title="Detach"
            style={{
              position: 'absolute', top: -4, right: -4, width: 14, height: 14, borderRadius: 999,
              background: '#000', color: '#fff', border: '1.5px solid #fff', cursor: 'pointer',
              fontSize: 9, lineHeight: 1, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
        )}
      </span>
    );
  }

  // Non-image chip
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 0, position: 'relative' }}>
      <Tag
        {...tagProps}
        title={available
          ? `${att.original_filename}${sizeStr ? ` · ${sizeStr}` : ''} — ${window.AtlasApi.attachmentOpensInline(att.original_filename) ? 'opens in new tab' : 'download'}`
          : `${att.original_filename} — referenced in DealCloud but the file is no longer in the backup`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px',
          borderRadius: 6, border: '1px solid var(--border)',
          background: available ? h.bg  : 'var(--surface-2)',
          color:      available ? h.fg  : 'var(--text-4)',
          fontSize: 11.5, fontWeight: 500, textDecoration: 'none',
          cursor: available ? 'pointer' : 'default',
          maxWidth: 240, overflow: 'hidden',
        }}
      >
        <Icon.Doc size={10} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {att.original_filename}
        </span>
        {sizeStr && <span style={{ fontSize: 10, opacity: 0.7 }}>· {sizeStr}</span>}
        {!available && <span style={{ fontSize: 10, opacity: 0.7 }}>· missing</span>}
      </Tag>
      {onDetach && touch && (
        <TouchX onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDetach(att); }} title="Detach"
          style={{ margin: '-8px -8px -8px -4px' }}
          circle={{ background: 'var(--surface)', color: 'var(--text-3)', border: '1px solid var(--border)' }} />
      )}
      {onDetach && !touch && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDetach(att); }}
          title="Detach"
          style={{
            marginLeft: 4, width: 16, height: 16, borderRadius: 999,
            background: 'var(--surface)', color: 'var(--text-3)', border: '1px solid var(--border)',
            cursor: 'pointer', fontSize: 10, lineHeight: 1, padding: 0,
          }}>×</button>
      )}
    </span>
  );
};

const AttachmentChipList = ({ attachments, onDetach }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6, alignItems: 'flex-start' }}>
      {attachments.map((a) => <AttachmentChip key={a.id} att={a} onDetach={onDetach} />)}
    </div>
  );
};

// useFileDrop — the dragover/dragleave/drop wiring every attach surface
// shares, so a dropped file goes through the SAME handler (and therefore the
// same validation + upload call) as the file picker instead of a second code
// path. Returns [dragOver, handlers]; spread the handlers onto the element
// that should accept the drop.
//   • dragenter AND dragover must both preventDefault, or the browser keeps
//     its "navigate to the dropped file" default and `drop` never fires.
//   • enter/leave fire once per nested child, so leave is depth-counted —
//     checking e.target === e.currentTarget drops the highlight as soon as
//     the cursor crosses a chip.
//   • Only file drags arm the zone: the columns popover and the tag pickers
//     drag text/elements around, and those must not light it up.
const _dragHasFiles = (e) => {
  const t = e.dataTransfer && e.dataTransfer.types;
  return !!t && Array.prototype.indexOf.call(t, 'Files') !== -1;
};
const useFileDrop = (onFiles, disabled) => {
  const [dragOver, setDragOver] = React.useState(false);
  const depth = React.useRef(0);
  if (disabled) return [false, {}];
  return [dragOver, {
    onDragEnter: (e) => {
      if (!_dragHasFiles(e)) return;
      e.preventDefault(); depth.current += 1; setDragOver(true);
    },
    onDragOver: (e) => {
      if (!_dragHasFiles(e)) return;
      e.preventDefault(); e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e) => {
      if (!_dragHasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragOver(false);
    },
    onDrop: (e) => {
      if (!_dragHasFiles(e)) return;
      e.preventDefault(); depth.current = 0; setDragOver(false);
      const files = e.dataTransfer && e.dataTransfer.files;
      if (files && files.length) onFiles(files);
    },
  }];
};

// Auth is enforced by the upload API itself — this must not gate on an
// activeUser prop, because its only caller (NoteModal in primitives.jsx)
// has no user in scope and a `disabled` gate here left the button
// permanently dead (Samuel's "Attach does not work" report).
const AttachButton = ({ noteId, onUploaded }) => {
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  // Both the picker and a drop land here — one upload path, one set of toasts.
  const upload = async (files) => {
    if (!files || files.length === 0 || uploading) return;
    setUploading(true);
    try {
      const created = await window.AtlasApi.uploadAttachments(noteId, files);
      pushToast({ type: 'success', title: `Uploaded ${created.length} file${created.length === 1 ? '' : 's'}` });
      onUploaded && onUploaded(created);
    } catch (err) {
      pushToast({ type: 'error', title: 'Upload failed', body: err.message });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const [dragOver, dropHandlers] = useFileDrop(upload);
  return (
    <div
      {...dropHandlers}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 4px', borderRadius: 5,
        border: `1px dashed ${dragOver ? 'var(--accent)' : 'transparent'}`,
        background: dragOver ? 'var(--accent-soft)' : 'transparent',
      }}
    >
      <input
        ref={fileInputRef}
        type="file" multiple
        onChange={(e) => upload(e.target.files)}
        style={{ display: 'none' }}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        title={_attTouch() ? 'Attach a file to this note' : 'Attach a file to this note — or drag one onto this row'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 6px', border: 'none', background: 'transparent',
          color: 'var(--text-3)', fontSize: 11, cursor: uploading ? 'default' : 'pointer', borderRadius: 4,
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <Icon.Plus size={10} /> {uploading ? 'Uploading…' : 'Attach'}
      </button>
      {/* Touch screens can't drag a file in — no hint to advertise it. */}
      {!_attTouch() && (
        <span style={{ fontSize: 11, color: dragOver ? 'var(--accent-text)' : 'var(--text-4)', fontWeight: dragOver ? 600 : 400 }}>
          {dragOver ? 'Drop to attach' : 'or drag and drop'}
        </span>
      )}
    </div>
  );
};

// ComposerAttachments — pending-file row for the note composers. The note
// doesn't exist yet when files are picked, so the parent holds them in state
// and uploads right after the create call returns the new note id.
const ComposerAttachments = ({ files, onChange, disabled }) => {
  const fileInputRef = React.useRef(null);
  // Picker and drop share this: same append, same pending list, same upload
  // on save. Disabled (not signed in) means no drop handlers at all, so the
  // row stops advertising a drop it would only reject.
  const addFiles = (list) => {
    if (list && list.length) onChange([...files, ...Array.from(list)]);
  };
  const [dragOver, dropHandlers] = useFileDrop(addFiles, disabled);
  return (
    <div
      {...dropHandlers}
      style={{
        display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center',
        // Outline, not border: it paints outside the box, so arming the drop
        // zone never nudges the composer's layout.
        outline: dragOver ? '2px dashed var(--accent)' : 'none', outlineOffset: 4, borderRadius: 4,
      }}
    >
      {files.map((f, i) => (
        <span key={`${f.name}:${i}`}
          title={`${f.name} · ${_fmtSize(f.size)} — uploads when the note is saved`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px',
            borderRadius: 6, border: '1px dashed var(--border)', background: 'var(--surface)',
            color: 'var(--text-2)', fontSize: 11.5, fontWeight: 500,
            maxWidth: 240, overflow: 'hidden',
          }}>
          <Icon.Doc size={10} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
          <span style={{ fontSize: 10, opacity: 0.7 }}>· {_fmtSize(f.size)}</span>
          {_attTouch() ? (
            <TouchX onClick={() => onChange(files.filter((_, j) => j !== i))} title="Remove"
              style={{ margin: '-10px -10px -10px -6px' }}
              circle={{ background: 'var(--surface)', color: 'var(--text-3)', border: '1px solid var(--border)' }} />
          ) : (
            <button onClick={() => onChange(files.filter((_, j) => j !== i))}
              title="Remove"
              style={{
                width: 16, height: 16, borderRadius: 999, flex: 'none',
                background: 'var(--surface)', color: 'var(--text-3)', border: '1px solid var(--border)',
                cursor: 'pointer', fontSize: 10, lineHeight: 1, padding: 0,
              }}>×</button>
          )}
        </span>
      ))}
      <input
        ref={fileInputRef}
        type="file" multiple
        onChange={(e) => {
          if (e.target.files && e.target.files.length) onChange([...files, ...Array.from(e.target.files)]);
          e.target.value = '';
        }}
        style={{ display: 'none' }}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        title={disabled ? 'Sign in first' : 'Attach files — drag them here or click to choose; they upload when the note is saved'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 6px', border: 'none', background: 'transparent',
          color: 'var(--text-3)', fontSize: 11, cursor: 'pointer', borderRadius: 4,
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <Icon.Plus size={10} /> Attach
      </button>
      {!disabled && !_attTouch() && (
        <span style={{ fontSize: 11, color: dragOver ? 'var(--accent-text)' : 'var(--text-4)', fontWeight: dragOver ? 600 : 400 }}>
          {dragOver ? 'Drop to attach' : 'or drag and drop'}
        </span>
      )}
    </div>
  );
};

// Upload composer-picked files onto a just-created note. A failed upload must
// not read as a failed save — the note already exists — so it warns and lets
// the caller finish; the modal's Attach button is the retry path.
const uploadPendingAttachments = async (note, files) => {
  if (!note?.id || !files || files.length === 0) return;
  try {
    await window.AtlasApi.uploadAttachments(note.id, files);
  } catch (e) {
    pushToast({
      type: 'warning', title: 'Note saved, but attachments failed',
      body: `${e.message} — open the note and re-attach.`,
    });
  }
};

// ── Files panel → composer handoff ───────────────────────────────────────
// The right-rail Files panel is a read-only list, and an attachment can't
// exist without a note, so files dropped on it are handed to the note
// composer on the same page as pending attachments — the very state the
// composer's own picker and drop zone feed. The panel sits in the right rail
// and the composer in the centre pane with no shared state between them, so
// the handoff rides a window event, the same convention the panel already
// uses for 'atlas-attachments-changed'.
const COMPOSER_ATTACH_EVENT = 'atlas-composer-attach';

// A panel is scoped to one record and a composer files notes against one
// record: only the composer for the same record takes the drop.
const _handoffMatches = (scope, target) => {
  if (!scope || !target) return false;
  if (scope.engagement_id) return scope.engagement_id === target.engagement_id;
  if (scope.company_id)    return scope.company_id    === target.company_id;
  if (scope.contact_id)    return scope.contact_id    === target.contact_id;
  return false;
};

// Composer side. Appends handed-off files to the pending list and reveals
// the composer, so a drop that happened in another column is visibly
// somewhere. Returns a ref to put on the composer container — that's what
// gets scrolled into view. Pass target = null to opt out (signed out).
const useComposerFileHandoff = (target, onFiles) => {
  const ref = React.useRef(null);
  // Keep the latest props without resubscribing the listener every render.
  const latest = React.useRef(null);
  latest.current = { target, onFiles };
  React.useEffect(() => {
    const take = (e) => {
      const d = e.detail;
      if (!d || d.accepted || !d.files || !d.files.length) return;
      if (!_handoffMatches(d.scope, latest.current.target)) return;
      d.accepted = true;
      latest.current.onFiles(d.files);
      // The composer is usually collapsed when the drop lands, so the
      // container doesn't exist yet — wait for the open render.
      const reveal = (tries) => {
        const el = ref.current;
        if (!el) { if (tries > 0) requestAnimationFrame(() => reveal(tries - 1)); return; }
        const ta = el.querySelector('textarea');
        if (ta) ta.focus({ preventScroll: true });
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
      requestAnimationFrame(() => reveal(5));
    };
    window.addEventListener(COMPOSER_ATTACH_EVENT, take);
    return () => window.removeEventListener(COMPOSER_ATTACH_EVENT, take);
  }, []);
  return ref;
};

// Panel side. Dispatch is synchronous, so `accepted` tells us whether a
// composer actually took the files — a drop that landed nowhere says so
// instead of silently swallowing the file.
const handOffFilesToComposer = (files, scope) => {
  const list = Array.from(files || []);
  if (!list.length) return;
  const detail = { files: list, scope, accepted: false };
  window.dispatchEvent(new CustomEvent(COMPOSER_ATTACH_EVENT, { detail }));
  pushToast(detail.accepted
    ? {
      type: 'success',
      title: `${list.length} file${list.length === 1 ? '' : 's'} added to the note`,
      body: 'They upload when you save the note.',
    }
    : {
      type: 'warning', title: 'Nowhere to attach these',
      body: 'Files hang off a note — open "Add note" on this page and drop them on the composer.',
    });
};

window.DealDetail         = DealDetail;
window.EditableCell       = EditableCell;
window.EDIT_CELL_WRAP     = EDIT_CELL_WRAP;
window.EstCloseValue      = EstCloseValue;
window.estCloseStatus     = estCloseStatus;
window.dealIsClosed       = dealIsClosed;
window.HoldReasonPrompt   = HoldReasonPrompt;
window.HOLD_CADENCE_OPTIONS = HOLD_CADENCE_OPTIONS;
window.HOLD_CADENCE_LABEL = HOLD_CADENCE_LABEL;
window.HOLD_CADENCE_TIP = HOLD_CADENCE_TIP;
window.ExitDealDrawer     = ExitDealDrawer;
window.CopyIconBtn        = CopyIconBtn;
window.STAGE_DOT_HUE      = STAGE_DOT_HUE;
window.stagesForDealType  = stagesForDealType;
window.SALE_APPROACHES    = SALE_APPROACHES;
window.SyncBadge          = SyncBadge;
window.AttachmentChip     = AttachmentChip;
window.AttachmentChipList = AttachmentChipList;
window.useFileDrop        = useFileDrop;
window.useComposerFileHandoff = useComposerFileHandoff;
window.AttachButton       = AttachButton;
window.ComposerAttachments = ComposerAttachments;
window.uploadPendingAttachments = uploadPendingAttachments;
// Phone-number helpers (display / tel: digits / parsed list) and the phone
// sheet editors, for the other phone pages (company / person records).
window.phoneDisplay       = phoneDisplay;
window.phoneDial          = phoneDial;
window.phoneNumbers       = phoneNumbers;
window.PhoneFieldSheet    = PhoneFieldSheet;
window.PhoneUsersSheet    = PhoneUsersSheet;
// Shared with the record blocks that moved out of this file (buyer_board.jsx,
// files_panel.jsx), which read them through window.*; this is their one owner.
Object.assign(window, {
  PURSUIT_STAGE_OPTIONS, pursuitKeyFor, cleanDcStage, dcStageHue,
  ATTACHMENT_EXT_HUE, _fmtSize, handOffFilesToComposer,
});
// Wave D (step 10): read by views/deal_page_v2.jsx (the v2 deal page), the
// Meetings & calls drawer body (calling.jsx) and the All details drawer.
Object.assign(window, {
  EditableTitle, StageRail, NextActionCard, PropertiesPanel, DealMergeRow, WorkspaceCreatorDrawer,
  HighlightsSection, CallAnalyticsSection, ENG_TYPE_PILL, HOLD_REASON_LABEL,
});
